// The shared END-USER transaction screen orchestrator: saved-records list
// (pagination / enforced filters / filter sets / search), record editor
// (header form + buffered child tabs, atomic batch save incl. the
// approval-staged envelope), delete, custom/system/bulk actions, item_change,
// record history, approvals badge, view switching and deep linking.
//
// Extracted from the platform designer's LivePreviewTab so the designer Live
// Preview tab, the platform Run view and (in a later phase) the standalone
// runtime client render ONE implementation. All I/O rides an injected
// `TxnScreenRuntime` (see ./contract.ts); designer-only chrome (deploy menu,
// fix-code, reset, customize, preview log) stays in the host and arrives via
// `headerActions` / `extraModals` / the imperative `apiRef` handle.
//
// Layout: list fills the available height. Clicking Add/Edit opens an
// editor in a large modal so the form has room to breathe.

import { Component, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import type { ReactNode, Ref, RefObject } from 'react'
import { createPortal } from 'react-dom'
import {
  AlertTriangle, Check, Copy, Download, EyeOff, FilePlus, Inbox, Lock as LockIcon, Mail, Minus, MoreVertical, PanelTop,
  RefreshCw, Play, RotateCcw, Save, Square, X,
} from 'lucide-react'
import type { ColumnDef, TransactionModel, ItemChangeFormChange, ItemChangeResult, SqlModel } from '../types'
import ContentPanel from '../widgets/ContentPanel'
import { contentPanelVisible } from '../contentPanel'
import {
  normalizeItemChange, partitionActionsByScope, withTransactionActions, bulkEligibleActions, bulkActionCandidates, isSystemDeleteAction, actionPlacement,
  visionActionOf, visionNotWiredMessage, visionRunnable, visionRecordStates, showConfirmAllows,
  visionShowConfirmByTitle, visionInRowMenu, visionScreenOffersDelete,
  resolveReportActionParams, buildAssistantActionRequest, buildPaymentActionRequest, buildImportActionRequest,
  buildWizardActionRequest,
  buildOpenActionRequest,
  buildSendActionRequest,
  formLinesViewOnly,
  rowSelectOf,
  actionCaptureOf, withCapturedValue,
} from '../types'
import type { ActionDef, OpenScreenPayload } from '../types'
import { collectSeededProtect, type ItemChangeExecutor } from '../list/useItemChange'
import { useHeaderItemChange } from './useHeaderItemChange'
import { useOpenSequence, type OpenListFilter } from './useOpenSequence'
import ReportActionModal from '../../report/ReportActionModal'
import type { ReportRuntimeAdapter } from '../../report/types'
import type { TxnNotifier } from '../context'
import { TxnRuntimeProvider } from '../context'
import { PeopleProvider, mergePeople, type PeopleDirectory } from '../people'
import { attemptWithReason, attemptWithWarningConfirm, extractBlockingWarnings } from '../warnings'
import { useWarningConfirm } from '../WarningsConfirmModal'
import { useActionCapture } from '../ActionCaptureModal'
import {
  actionErrorMessages, firstValidationError, useActionError, useValidationErrorPopup,
} from '../ActionErrorModal'
import { useReasonPrompt } from '../ReasonForChangeModal'
import {
  useActionConfirm, attemptActionWithConfirm, isActionConfirmCancelled,
} from '../ActionConfirmModal'
import { useKeyboardShortcuts, useShortcutLayer } from '../keyboard'
import { actionShortcutCombo, shortcutHint, systemCombo, withShortcutHint } from '../actionShortcuts'
import { useBulkAction } from '../useBulkAction'
import HtmlResultModal from '../HtmlResultModal'
import SelectGridModal from '../SelectGridModal'
import type { SplitScrollHandle } from '../SplitScrollTable'
import type {
  EmbedDetailMap,
  EnforcedConditionEcho, EnforcedFilterSetEcho, FilterNotice, RunActionResponse,
  ValidationErrorItem,
} from '../contracts'
import {
  allSqlModels, auditTokenOf, buildParentPk, calendarDefaultForInput,
  calendarTokenOf, childFormsOf, columnFormKey, headerFormNo, inputType,
  isAutoGenerate, isFunctionCallDefault, isListHidden, listOrderedColumns, mainSqlModel, parseApiError,
  literalDefaults, projectVarOf, visibleColumns,
} from '../utils'
import { orderedDisplayCols } from '../list/columnLayout'
import { ExportWindow, TemplateManager, exportActionOf, type ExportScope } from '../export'
import { ScreenActionButtons, ScreenActions, asMoreMenuItems, type ScreenActionItem } from '../list/ScreenActions'
import FormRenderer, { fieldAnchorId, focusFieldAnchor, FormModeBadge } from '../FormRenderer'
import ListView, { screenCardStyle } from '../list/ListView'
import { screenMargin, useHostNamesObject } from '../../objectChrome'
import { Z_FULLSCREEN_VIEW } from '../../layers'
import FormTabs from '../list/FormTabs'
import RecordHistoryModal from '../list/RecordHistoryModal'
import ProcessTrailModal from '../flow/ProcessTrailModal'
import ProcessOverview from '../flow/ProcessOverview'
import { instrumentScreenRuntime } from '../../activity'
import { processFlows } from '../flow/contract'
import type { NodeActions } from '../flow/contract'
import RecordAliasModal from '../list/RecordAliasModal'

/** What to call ONE record, wherever a dialog has to name it.
 *
 * Prefers the column the vector configuration nominates as the display
 * column — the same value a search will show — so the user is looking at the
 * exact label their alias will sit beside. Falls back to the row's first
 * non-key text value, then to the record id, which is still better than an
 * unlabelled dialog when the whole point is telling two similar records apart.
 */
function recordDisplayLabel(
  rows: Array<Record<string, unknown>>,
  txnId: string,
  model?: { vector_search?: { display_column?: string; columns?: string[] } } | null,
): string {
  const row = rows.find((r) => String(r._txn_id) === String(txnId))
  if (!row) return txnId
  const wanted = model?.vector_search?.display_column
    || model?.vector_search?.columns?.[0]
    || ''
  if (wanted) {
    const key = Object.keys(row).find((k) => k.toLowerCase() === wanted.toLowerCase())
    const value = key ? row[key] : undefined
    if (value != null && String(value).trim()) return String(value).trim()
  }
  for (const [k, v] of Object.entries(row)) {
    if (k.startsWith('_')) continue
    if (typeof v === 'string' && v.trim()) return v.trim()
  }
  return txnId
}
import {
  TXN_SCREEN_VIEWS, TXN_SCREEN_BUILTIN_VIEWS,
  type EmbedListView,
} from '../list/InterfaceSwitcher'
import { LayoutSwitch, EditorLayoutSwitch, editorLayoutMenuItems, layoutMenuItems } from '../list/LayoutSwitch'
import ListTabs from '../list/ListTabRow'
import {
  ALL_TAB_ID, DEFAULT_EDITOR_LAYOUT, DEFAULT_LAYOUT_SWITCH_PLACE, EDITOR_LAYOUTS, LEGACY_EDITOR_VIEWS,
  editorLayoutLabel, isEditorLayout, type EditorLayout, type LayoutSwitchPlace,
} from '../list/listScreenConfig'
import { initialTab, listTabsOf, tabsEcho, type TabEcho } from '../list/listTabs'
import { getScreenPref, updateScreenPref, useScreenPref } from './screenPrefs'
import ChildTabularGrid, { type ChildRow } from '../list/ChildTabularGrid'
import type { ListPagination } from '../list/PaginationBar'
import CompactListView from '../compact/CompactListView'
import CompactFormShell from '../compact/CompactFormShell'
import { isMobileViewport } from '../compact/useIsMobile'
import {
  useGridLayout,
  type GridLayoutNotify, type GridLayoutPersistence,
} from '../list/useGridLayout'
import ChildFormTab, { withLocalId, type ChildStore } from './ChildFormTab'
import LineEntryBar, { type HeaderEdit } from '../lineEntry/LineEntryBar'
import SaveReview, { type ReviewItem, type ReviewSummary } from '../lineEntry/SaveReview'
import { isKey as isKeyColumn } from '../utils'
import type { WarningItem } from '../contracts'
import { lineEntryOf } from '../lineEntry/model'
import {
  acknowledgedWarnings, afterRender, annotateRowError, applyFormRowsChange, bufferHasUnsaved,
  flushRowCompletions, formChangeTouchesRows, listAllChildRows, markChildrenSaved,
  applySelectableRowsChange, selectableRowsForSave,
} from './detailsBuffer'
import DefaultErrorDetailsModal from './ErrorDetailsModal'
import type { ErrorReport } from './ErrorDetailsModal'
import type { BatchScope, EnforcedOverrides, ObjectRightsSummary, TxnScreenRuntime, TxnSubmitMode } from './contract'
import { canUseButton, executeValidate } from './contract'
import { TxnScreenRuntimeProvider } from './screenContext'
import { WizardFillContext, type WizardFillTarget } from './wizardFill'
import { Z_RECORD_EDITOR } from '../../layers'
import { useShellEditor } from '../../shell/nav/editorContext'
import { changedSince, expectText, makeSnapshot, type EditorSnapshot } from '../../shell/nav/editorSessions'
import { tw } from '../../testHooks'
import { useCustomizeSession, type CustomizeSession } from '../../customize/session'
import { TxnCustomizeContext, type TxnCustomizeApi } from '../../customize/txnContext'
import { CustomizeBar, CustomizeBarAction, CustomizeMenu, MoreMenuHeading, type MoreMenuItem } from '../../customize/ui'
import TxnFieldPanel from '../../customize/TxnFieldPanel'
import BuildFieldPanel from '../../customize/BuildFieldPanel'
import AddFieldDialog from '../../customize/AddFieldDialog'
import BuildBarTools from '../../customize/BuildBarTools'
import ButtonPanel from '../../customize/ButtonPanel'
import { MakeChangesButton } from '../../customize/ui'
import { formBase as czFormBase, columnKey as czColumnKey } from '../../customize/txnOps'
import type { CustomizeTransport } from '../../customize/model'
import {
  UdaScreenProvider, createUdaScreenStore, observeUdaMarkers, type UdaScreenApi,
} from '../../uda/context'
import { useUdaList, useUdaRecord } from '../../uda/useUdaResolve'
import {
  attributeGroups, driverValues, groupCaptions, masterSourceNote, promotedColumns, withUdaColumns,
} from '../../uda/merge'
import { evaluateFlags } from '../../uda/flagEval'
import { FlagBadges, FlagIcons } from '../../uda/flags'
import { UdaNotice } from '../../uda/notice'
import { seedUdaProtect } from '../../uda/protect'
import { UDA_SETUP_SCREEN } from '../../uda/setupSlot'

// ── Host-facing surfaces ──────────────────────────────────────────────────

/** Everything a host-supplied alternate interface (spreadsheet entry /
 *  form-input capture — Phase 3b keeps those in the host) needs to render in
 *  the same filter/action context as the list. */
export interface TxnScreenInterfaceContext {
  /** Which alternate view is active ('tabular' | 'form-input'). */
  view: EmbedListView
  /** The "View" switcher node — thread into the interface's toolbar. */
  switcher: ReactNode
  /** The Approvals inbox button (null when the object isn't
   *  approval-controlled) — thread as beforeAddActions. */
  approvalsButton: ReactNode
  /** Split-scroll handle shared with the list (freeze-first-column). */
  splitHandle: RefObject<SplitScrollHandle | null>
  /** Active enforced-filter context, kept in sync with the list. */
  enforcedOverrides: EnforcedOverrides
  activeFilterSetId: string | null
  bypassed: boolean
  applyFilters: (overrides: EnforcedOverrides) => Promise<void>
  selectFilterSet: (sel: { setId?: string; bypass?: boolean }) => Promise<void>
  /** Row / bulk custom actions, shared with the list. */
  rowActions: ActionDef[]
  runRowAction: (name: string, txnId: string, header?: Record<string, unknown>) => void | Promise<void>
  bulkActions: ActionDef[]
  runBulkAction: (name: string, txnIds: string[]) => Promise<unknown>
  bulkBusy: boolean
  /** Access rights derived from the same `can(...)` gate the list uses, so the
   *  capture interfaces (spreadsheet / form-input) hide add/edit/delete
   *  affordances a read-only viewer must not get (the server still gates). */
  canAdd: boolean
  canEdit: boolean
  canDelete: boolean
  /** The record currently open in the editor, if any. A host interface that
   *  represents ONE record (the Document view) opens on it; without it the
   *  interface can only ever show a blank one. */
  recordTxnId: string | null
  /** Dismiss the record editor. The Document view drives its own save, so it
   *  needs to close itself once the record is written. */
  closeEditor: () => void
  /** The record editor's layout (standard / simple / document / compact) and
   *  the switch that changes it — the Bulk entry editor wears the same choice. */
  editorLayout: EditorLayout
  editorLayoutSwitch: (opts?: { exclude?: EditorLayout[] }) => ReactNode
  /** Bulk entry's editor layout (Document is never offered there; with no
   *  choice made it opens Simple — every form on one page). */
  bulkEditorLayout: 'standard' | 'simple' | 'compact'
  /** The list TAB in force ('all' or a tab id; null when the list has none) —
   *  the capture views list the same records the tab shows. */
  activeTab: string | null
  /** The document's handle on its unsaved edits: the editor-layout switch
   *  asks Save / Discard before leaving it (the document saves on its own). */
  documentControl: { current: DocumentControl | null }
  /** The screen's actions placed ON THE BAR (Export, form buttons — see
   *  ScreenActions) as toolbar buttons — thread beside the approvals button,
   *  before Add. */
  actionButtons: ReactNode
  /** The screen's ⋮ — the host's own menu (`renderMore`) with the screen's
   *  ⋮-placed actions folded in, else the screen's own ⋮ — thread as the
   *  toolbar's `headerActions` (after Save All), never a second ⋮. */
  moreMenu: ReactNode
}

/** What the Document editor exposes to the screen hosting it. */
export interface DocumentControl {
  dirty: boolean
  save: () => Promise<boolean>
  discard: () => void
}

/** Imperative surface for the HOST's own chrome (designer deploy / reset):
 *  lets it drive the screen without owning its state. */
export interface TxnScreenHandle {
  /** Re-fetch the first list page. */
  refresh: () => Promise<void>
  /** Re-fetch the approvals badge count (best-effort). */
  refreshApprovals: () => void
  /** Silently close the editor and drop all form/buffer state (no dirty
   *  prompt) — for after an external clear like the designer's reset. */
  closeAndClearEditor: () => void
  /** Surface a failed request in the screen's error modal (+ toast). */
  reportError: (context: string, err: unknown, opts?: { toast?: boolean }) => void
  /** Overlay the list's busy state while a host operation runs. */
  setBusy: (busy: boolean) => void
  /** Enter Customize mode (the host's own ⋮ "Customize" item), or with
   *  scope 'app' Make changes (the ⋮ "Make changes" item). */
  enterCustomize: (scope?: 'org' | 'app') => void
  /** Run a handler='open' action's payload on this screen: open the record
   *  (add / edit / edit-or-add), set its columns firing each item_change, then
   *  the follow-up steps. For a host that mounts the screen itself (a drawer). */
  runOpen: (payload: OpenScreenPayload) => Promise<void>
}

export interface TxnScreenProps {
  project: string
  objName: string
  model: TransactionModel
  /** The injected I/O contract — see ./contract.ts. Must be referentially
   *  stable across renders (memoize in the host). */
  runtime: TxnScreenRuntime
  /** Where "Apply layout" persists width/order: 'design' (default) writes the
   *  shared transaction JSON — the designer live preview; 'customize' stores a
   *  per-tenant overlay — the run app, leaving the core design untouched. */
  gridLayoutMode?: 'design' | 'customize'
  /** Whether this viewer may SAVE a dragged column width / order (default
   *  true). In 'customize' mode only the organization's functional
   *  administrator may — everyone else can still rearrange for the session. */
  canSaveLayout?: boolean
  /** Extra busy overlay from the host (e.g. the designer's deploy in
   *  flight) — OR-ed into the list's busy state. */
  busy?: boolean
  /** Host chrome rendered at the right of the list toolbar (the designer's
   *  deploy menu). Also thread it into interface slots yourself. */
  headerActions?: ReactNode
  /** The host's ⋮ menu, built with the list's own items (row density) in
   *  it — for a host whose `headerActions` IS a ⋮ menu (the designer's
   *  deploy menu). Absent ⇒ the screen's own ⋮ carries them. */
  renderMore?: (items: MoreMenuItem[]) => ReactNode
  /** Make changes stored a change to the model on disk (or undid one). A
   *  host holding its own copy of the model re-reads it. */
  onBuildChange?: () => void
  /** Host-only modals rendered inside the screen container (fix-code,
   *  preview audit/log, customize panel …). */
  extraModals?: ReactNode
  /** Vision-style dialogs: the warnings confirm reads OK / Cancel (as a
   *  Vision screen's) instead of "Ignore warnings and proceed". Hosts turn it
   *  on for imported Vision (Java business logic) applications. Reaches the
   *  detail line editors too. A clicked list row also opens in view mode, as
   *  on Vision's browse screen. */
  visionDialogs?: boolean
  /** Alternate interfaces for the view switcher. When absent the switcher is
   *  hidden and the screen always shows the list. */
  interfaces?: {
    tabular?: (ctx: TxnScreenInterfaceContext) => ReactNode
    formInput?: (ctx: TxnScreenInterfaceContext) => ReactNode
  }
  /**
   * The transaction filled in as a laid-out DOCUMENT (obj_type `E`).
   *
   * Its own slot rather than a third entry in `interfaces` because it is
   * offered CONDITIONALLY: the switcher shows "Document" only where a Document
   * Form is actually anchored on this object, exactly as 'process' is only
   * shown where the object sits on a flow. Folding it in with the capture
   * interfaces would offer it on every transaction and render an error on most
   * of them.
   */
  documentView?: (ctx: TxnScreenInterfaceContext) => ReactNode
  /** Take the viewer to a record on ANOTHER object's screen — used by the
   *  process trail's "Open full screen". Optional: without it the trail still
   *  shows every linked record in its own right-hand pane, it just has no way
   *  out to that object's own screen (which only a host that owns navigation
   *  can provide). */
  onOpenLinkedRecord?: (objName: string, txnId: string) => void
  /**
   * Hand a record to somebody: opens the host's assign dialog for THIS record,
   * so the person who gets it opens the record rather than hunting for it.
   * The screen only knows which record was picked and what it is called — the
   * dialog belongs to the host, because only the host has the work panel the
   * task will land in. Absent → no "Assign to…" entry, which is the correct
   * state for a surface with no worklist (the designer's live preview).
   */
  onAssignRecord?: (ctx: {
    objName: string; recordKey: string; label: string; objTitle: string
  }) => void
  /** The cross-object "My Approvals" inbox — stays host-side for now.
   *  Rendered only while open. */
  renderApprovalsInbox?: (props: {
    onClose: () => void
    onCountChange: (count: number | null) => void
  }) => ReactNode
  /** Full-detail error modal override (the platform injects its own chrome).
   *  Defaults to the package modal. */
  renderErrorDetails?: (props: { report: ErrorReport | null; onClose: () => void }) => ReactNode
  /** Run-view deep linking: open a record on mount (mode 'add' | 'edit' + rec).
   *  Only set when the host (RunPage) drives the URL. */
  initialScreen?: {
    mode?: string
    rec?: string
    /**
     * Which VIEW to land on — 'document' is the one that matters.
     *
     * A link from the repository to "edit this document on its record" has to
     * arrive at the document face of the record, not at the grid form: those
     * are two editors over one record, and the one the reader asked for is the
     * one that looks like the document they were just reading. Applied once,
     * and deliberately NOT remembered as the reader's own preference — they
     * followed a link, they did not choose a way of working.
     */
    view?: string
    /**
     * A worklist task's vetted seed, already fetched by the host. The screen
     * never fetches it itself: the payload is authorised per caller and can
     * be far larger than a URL, so the link carries only the task key and the
     * host resolves it.
     */
    seed?: Record<string, unknown>
    /**
     * A handler='open' action button on ANOTHER screen opened this one: which
     * record to open and the values / follow-up steps to apply (see
     * useOpenSequence). Read from the open hand-off by the host; `openKey`
     * (the hand-off id) makes it run once per click, however often the screen
     * re-renders. Runs whether or not the host drives the URL.
     */
    open?: OpenScreenPayload
    openKey?: string
  }
  /** Reflect the open record back to the URL so it can be shared. */
  onScreenChange?: (s: { mode?: string; rec?: string } | null) => void
  /** The record editor opened or closed (add / edit, and which record). A
   *  drawer that holds this screen closes with its editor — see ObjectDrawer. */
  onEditorChange?: (s: { open: boolean; txnId: string | null }) => void
  /** Bump to re-run runtime.ensureReady + reload (designer redeploy). */
  versionKey?: unknown
  /** Initial interface view. When omitted, defaults to 'compact' on a narrow
   *  (phone-width) viewport, else 'list'. The user can still switch. */
  defaultView?: EmbedListView
  /** Share the split-scroll handle with host chrome ("Freeze first column"
   *  in the designer's menu). Omit to keep it internal. */
  splitHandle?: RefObject<SplitScrollHandle | null>
  /** Imperative handle for host-driven refresh / clear / error reporting. */
  apiRef?: Ref<TxnScreenHandle>
  /** The screen is a form that RUNS something (an IOFlow flow's input form)
   *  instead of a record editor — see `TxnSubmitMode`. Absent ⇒ unchanged. */
  submit?: TxnSubmitMode
}

interface RowShape extends Record<string, unknown> {
  _txn_id: string
}

// Height of each child grid in the "simple" editor layout. ChildTabularGrid is
// a height:100% flex column with a fixed toolbar over a flex:1 scrolling body,
// so it needs a DEFINITE height — inside an auto-height scroll parent its
// toolbar unpins and every grid grows to its full row count. Same value the
// smart-page single-classic template uses.
const SIMPLE_GRID_HEIGHT = 320

/**
 * Map a task seed's keys onto the form keys this screen reads.
 *
 * A work rule is authored against the MODEL, so its seed is keyed by logical
 * column name (`PoNo`); the form state is keyed by the column's normalised
 * name (`pono`), and a seed built from a database row may arrive keyed by
 * db_name (`PO_NO`). All three denote one field, so accept any of them rather
 * than making the rule author guess which spelling the screen wants.
 *
 * `__rows__` (detail lines) is skipped here: it is not a header field, and the
 * child-form buffer is populated separately.
 */
function mapSeedToFormKeys(
  seed: Record<string, unknown>,
  sm: SqlModel | null | undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if (!seed) return out
  // Only a PLAIN object is a seed. A handler bound straight to a DOM
  // `onClick` receives the MouseEvent, and `startAdd`'s seed parameter is
  // optional, so TypeScript cannot reject that wiring. Without this the
  // event's own keys are copied in below (unmapped keys are kept on
  // purpose), putting `target` — a DOM node whose React fiber closes a
  // cycle — into the form values, where it surfaces much later as a save
  // body that will not serialize.
  const proto = Object.getPrototypeOf(seed)
  if (proto !== Object.prototype && proto !== null) return out
  const norm = (v: unknown) => String(v ?? '').trim().replace(/\s+/g, '_').toLowerCase()
  const bySpelling = new Map<string, string>()
  for (const col of (sm ? visibleColumns(sm) : [])) {
    const formKey = columnFormKey(col)
    bySpelling.set(norm(col.name), formKey)
    bySpelling.set(norm((col as { db_name?: string }).db_name), formKey)
    bySpelling.set(norm(formKey), formKey)
  }
  for (const [key, value] of Object.entries(seed)) {
    if (key === '__rows__') continue
    const mapped = bySpelling.get(norm(key))
    // An unmapped key is kept under its own name rather than dropped: a
    // multi-form seed may legitimately name a field this form does not show,
    // and losing it silently would be worse than carrying it.
    out[mapped || key] = value
  }
  return out
}


/**
 * Which LIST layout and EDITOR layout this person last used for THIS object,
 * and its last tab, are remembered per person on the server (`__screens__`,
 * see ./screenPrefs) — they follow the person to another device; this browser
 * keeps a mirror for the first paint and offline use.
 *
 * `rememberView` is kept for hosts that pre-select a view (the designer's
 * preview applies the view the Design tab was saved on). An old LIST view id
 * that was really an editor layout (`simple` / `document`) sets the editor.
 */
export function rememberView(project: string, objName: string, view: EmbedListView): void {
  const editor = LEGACY_EDITOR_VIEWS[view]
  updateScreenPref(project, objName, editor
    ? { view: 'list', editor: editor as EditorLayout }
    : { view })
}

/** A remembered list view as a list layout this screen draws ('' for none). */
function rememberedListView(project: string, objName: string): EmbedListView | '' {
  const v = getScreenPref(project, objName).view
  return v && v !== 'simple' && v !== 'document' ? (v as EmbedListView) : ''
}

/**
 * The edit window's size is the same kind of habit as the view: one choice for
 * the person, kept in the browser, and reopened the way they left it.
 */
const EDITOR_MAXIMIZED_KEY = 'twasta.txn.editor.maximized'

function rememberedMaximized(): boolean {
  try {
    return window.localStorage.getItem(EDITOR_MAXIMIZED_KEY) === '1'
  } catch {
    return false  // private mode, blocked storage — the default size is fine
  }
}

function rememberMaximized(on: boolean): void {
  try {
    window.localStorage.setItem(EDITOR_MAXIMIZED_KEY, on ? '1' : '0')
  } catch {
    /* not remembering is a smaller failure than a thrown toggle */
  }
}

export default function TxnScreen({
  project, objName, model, runtime: hostRuntime, gridLayoutMode = 'design', canSaveLayout = true,
  busy: hostBusy = false, headerActions, renderMore: hostRenderMore, onBuildChange, extraModals, visionDialogs = false, interfaces, documentView,
  onOpenLinkedRecord, onAssignRecord, renderApprovalsInbox, renderErrorDetails,
  initialScreen: hostInitialScreen, onScreenChange: hostOnScreenChange, onEditorChange, versionKey,
  splitHandle, apiRef, defaultView, submit,
}: TxnScreenProps) {
  // Open editors (the Run shell — see shell/nav/editorContext). As the screen
  // on display ('main') a user's Add / Edit is offered to the shell, which
  // opens it in a dedicated, minimisable session; as such a session's own
  // kept-alive instance ('session') this screen opens THAT record and shows
  // only its editor. Neither: everything below behaves exactly as before.
  const shellEd = useShellEditor(objName)
  // A submit form is one form on its own pane: never a shell editor session.
  const shellMain = !submit && shellEd?.role === 'main' ? shellEd : null
  const shellSession = !submit && shellEd?.role === 'session' ? shellEd : null
  // A session instance opens from its own intent and never drives the URL —
  // the screen on display reflects the shown editor there instead.
  const initialScreen = shellSession ? undefined : hostInitialScreen
  const onScreenChange = shellSession ? undefined : hostOnScreenChange
  // Filled in by the session block below; read by the save paths above it.
  const sessionHooks = useRef<{
    /** True while a restored editor re-checks its record: a save waits. */
    holdSave?: () => boolean
    expect?: () => Promise<Record<string, unknown> | null>
    afterSave?: (res: { txn_id?: string; header?: Record<string, unknown> }, wasAdd: boolean) => void
    beforeClose?: () => void
  }>({})
  // Every surface's opens / saves / deletes / actions / searches are recorded
  // here, once, by wrapping the host's transport (a no-op until a host has
  // installed the activity tracker).
  // Additional attributes: a store for this screen when the host has them,
  // and the runtime observing the engine's `_uda` markers on list / load
  // responses (which forms have attributes). No `runtime.uda` ⇒ no store and
  // the very same runtime as before.
  const headerFormNoRef = useRef('1')
  const udaKey = hostRuntime.uda ? objName : ''
  const udaStore = useMemo<UdaScreenApi | null>(() => (hostRuntime.uda && udaKey
    ? createUdaScreenStore({
      objName: udaKey,
      transport: hostRuntime.uda,
      resolve: (req) => hostRuntime.execute(udaKey, 'uda_resolve', { ...req }),
    })
    : null), [hostRuntime, udaKey])
  const runtime = useMemo(
    () => observeUdaMarkers(instrumentScreenRuntime(hostRuntime), udaStore, udaKey,
      () => headerFormNoRef.current),
    [hostRuntime, udaStore, udaKey])
  const notify = runtime.txn.notify
  const notifyInfo = notify.info ?? notify.success
  const notifyWarn = notify.warn ?? notifyInfo

  // The Run shell's header already names this transaction; drawing it again at
  // the head of the list is the same name twice. False on every surface that
  // carries no such chrome (the designer's Live Preview), and false for a list
  // embedded under a shell that names a DIFFERENT object.
  const hostNamesThis = useHostNamesObject(objName, 'T')

  // Working copy of the model so an "Apply column layout" can bake width/order
  // back into the design and reflect it here without waiting for the parent to
  // reload (the prop re-syncs it when the object reloads upstream).
  const [workingModel, setWorkingModel] = useState(model)
  useEffect(() => { setWorkingModel(model) }, [model])
  const sm = useMemo(() => mainSqlModel(workingModel), [workingModel])
  // The header form as RENDERED: its own buttons plus the transaction-level
  // `actions[]` (run by the engine as header actions). Used only where
  // buttons are drawn — never saved back into the model.
  const headerSm = useMemo(
    () => withTransactionActions(sm, workingModel.actions), [sm, workingModel])
  const forms = useMemo(() => allSqlModels(workingModel), [workingModel])
  headerFormNoRef.current = forms[0]?.formNo || '1'
  // Tabs shown on the record editor: the header plus only its DIRECT child
  // forms. Deeper forms (a child's own children) are reached by drilling into
  // a child row — they must not appear as top-level sibling tabs.
  const topForms = useMemo(() => {
    const header = forms[0]
    if (!header) return []
    return [header, ...childFormsOf(workingModel, headerFormNo(workingModel))]
  }, [forms, workingModel])
  // Read by the save path and the rows-apply callback, which are memoised on
  // other deps: a `row_select` form changes what they send / merge.
  const topFormsRef = useRef(topForms)
  topFormsRef.current = topForms
  // Direct child forms drawn as an editable table INSIDE a header group
  // (`display_in_group`) rather than as a tab. Same detailsBuffer, same save.
  const inlineFormNos = useMemo(() => new Set(
    topForms.slice(1)
      .filter(f => (f.sm.display_in_group || '').trim())
      .map(f => f.formNo),
  ), [topForms])
  const tabForms = useMemo(
    () => topForms.filter(f => !inlineFormNos.has(f.formNo)),
    [topForms, inlineFormNos],
  )
  // Live column layout (drag to resize / reorder) for the list + child windows,
  // with author "Apply to design" persistence — backed by the host-injected
  // notify + persistence (the package carries no toast / HTTP client).
  const layoutNotify = useMemo<GridLayoutNotify>(() => ({
    success: (msg) => notify.success(msg),
    error: (msg) => notify.error(msg),
    // notify identity follows runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [runtime])
  // ── Customize mode ───────────────────────────────────────────────────
  // The organization's functional administrator re-presents this screen in
  // place: the list's columns / freeze, every form's groups and fields, the
  // child grids. Each change is an overlay diff saved at once (session).
  const [customizing, setCustomizing] = useState(false)
  const [canCustomize, setCanCustomize] = useState(false)
  // One mode, two scopes: 'org' = Customize (this organization's overlay);
  // 'app' = Make changes (the transaction model itself, for everyone — the
  // platform's builders only, through runtime.build).
  const [czScope, setCzScope] = useState<'org' | 'app'>('org')
  const [canBuild, setCanBuild] = useState(false)
  // Make changes opens read-only while the AI is building this screen.
  const [czReadOnly, setCzReadOnly] = useState<string | null>(null)
  const [czField, setCzField] = useState<{
    formNo: string; colKey: string; anchor: HTMLElement | null; tab?: 'basics' | 'rules' | 'change' | 'checks'
  } | null>(null)
  // Make changes: the Add-a-field dialog, and the ⚙ of a button (name null =
  // a new one). `scopeKey` is a form's form_no or 'txn'.
  const [czAddField, setCzAddField] = useState<{ formNo: string; group: string | null } | null>(null)
  const [czButton, setCzButton] = useState<{ scopeKey: string; name: string | null; anchor: HTMLElement | null } | null>(null)
  // The mode is ONE surface — its bar, the list or record being worked on,
  // the field panel — and it opens full screen: a layer of its own over the
  // page (not the Run shell's full screen, whose Exit bar would add a line).
  // The list's and the editor's Full screen / Restore button toggle it;
  // restoring keeps the whole mode in the panel.
  const [czFull, setCzFull] = useState(false)
  const czFullOn = customizing && czFull
  const czFullScreen = useMemo(() => ({ on: czFullOn, set: setCzFull }), [czFullOn])
  useEffect(() => {
    let cancelled = false
    if (!runtime.customize) { setCanCustomize(false); return undefined }
    runtime.customize.canCustomize()
      .then((v) => { if (!cancelled) setCanCustomize(!!v) })
      .catch(() => { if (!cancelled) setCanCustomize(false) })
    return () => { cancelled = true }
  }, [runtime])
  useEffect(() => {
    let cancelled = false
    if (!runtime.build) { setCanBuild(false); return undefined }
    runtime.build.canBuild()
      .then((v) => { if (!cancelled) setCanBuild(!!v) })
      .catch(() => { if (!cancelled) setCanBuild(false) })
    return () => { cancelled = true }
  }, [runtime])
  const czTransport = useMemo<CustomizeTransport>(() => {
    const base = czScope === 'app' ? runtime.build?.transport : runtime.customize?.transport
    if (!base) {
      return {
        get: () => Promise.reject(new Error('Customize is not available here')),
        patch: () => Promise.reject(new Error('Customize is not available here')),
        reset: () => Promise.reject(new Error('Customize is not available here')),
      }
    }
    if (!czReadOnly) return base
    return { ...base, patch: () => Promise.reject(new Error(czReadOnly)) }
  }, [runtime, czScope, czReadOnly])
  const czSession: CustomizeSession = useCustomizeSession({
    objType: 'T', objName, transport: czTransport, active: customizing,
    onEffective: (r) => {
      const eff = (r.effective as { transaction?: TransactionModel } | undefined)?.transaction
      if (eff) setWorkingModel(eff)
    },
  })
  const czSessionRef = useRef(czSession)
  czSessionRef.current = czSession
  // Every stored Make-changes step (and undo / redo) renews changeLog.
  const onBuildChangeRef = useRef(onBuildChange)
  onBuildChangeRef.current = onBuildChange
  const lastChangeLog = useRef(czSession.changeLog)
  useEffect(() => {
    if (lastChangeLog.current === czSession.changeLog) return
    const had = lastChangeLog.current.length
    lastChangeLog.current = czSession.changeLog
    if (czScope === 'app' && (had || czSession.changeLog.length)) onBuildChangeRef.current?.()
  }, [czSession.changeLog, czScope])
  const customizingRef = useRef(customizing)
  customizingRef.current = customizing

  const layoutPersistence = useMemo<GridLayoutPersistence>(() => ({
    saveDesign: (p, o, m) => runtime.layout?.saveDesign
      ? runtime.layout.saveDesign(p, o, m)
      : Promise.reject(new Error('Layout persistence is not available on this surface')),
    saveOverlay: (p, o, set) => {
      // In Customize mode a dragged width / order goes through the session,
      // so it can be undone like every other change.
      if (customizingRef.current) {
        return czSessionRef.current.apply({ set, reset: [] }, 'Column layout').then((ok) => {
          if (!ok) throw new Error(czSessionRef.current.errors.join('; ') || 'Not saved')
        })
      }
      return runtime.layout?.saveOverlay
        ? runtime.layout.saveOverlay(p, o, set)
        : Promise.reject(new Error('Layout persistence is not available on this surface'))
    },
  }), [runtime])
  const gridLayout = useGridLayout({
    model: workingModel, setModel: setWorkingModel, project, objName,
    canEdit: customizing || canSaveLayout, mode: customizing ? 'customize' : gridLayoutMode,
    notify: customizing ? { success: () => {}, error: layoutNotify.error } : layoutNotify,
    persistence: layoutPersistence,
  })
  // A dragged column width / order saves on its own while customizing.
  useEffect(() => {
    if (!customizing) return undefined
    const dirty = Object.entries(gridLayout.layouts).find(([, l]) =>
      !!l && ((l.order && l.order.length > 0) || Object.keys(l.widths || {}).length > 0))
    if (!dirty || gridLayout.applyingFormNo) return undefined
    const t = setTimeout(() => gridLayout.apply(dirty[0]), 250)
    return () => clearTimeout(t)
  }, [customizing, gridLayout])
  const [activeFormNo, setActiveFormNo] = useState<string>(
    forms[0]?.formNo || '1',
  )
  // Runtime interface switcher for this standalone transaction (Live Preview
  // tab + /run reuse this component). Only offered when the host supplies the
  // alternate interfaces — it's the transaction's own surface, not a
  // designer-gated smart-page embed.
  // The person's remembered choices for this screen (list layout, editor
  // layout, last tab) — server memory with a browser mirror. A submit form
  // remembers nothing: it is one form, never a list.
  const screenPref = useScreenPref(project, objName, submit ? null : runtime.screenPrefs)
  const [view, setView] = useState<EmbedListView>(() => {
    if (submit) return 'list'
    // The view they left this object on wins over the host's default — it is
    // their own last explicit choice. 'process' is the exception: it is only
    // offered once the flow check comes back, so it is applied there.
    const last = rememberedListView(project, objName)
    if (last && last !== 'process') return last
    const dv = defaultView && LEGACY_EDITOR_VIEWS[defaultView] ? 'list' : defaultView
    return dv ?? (isMobileViewport() ? 'compact' : 'list')
  })
  // The record EDITOR's layout, chosen in the open record and independent of
  // the list layout: Standard (a tab per form), Simple (every form on one
  // page), Document (the record as a laid-out document — only where one is
  // anchored) or Compact (one narrow column). A submit form is always Simple.
  const [editorLayout, setEditorLayout] = useState<EditorLayout>(() => {
    if (submit) return 'simple'
    const remembered = getScreenPref(project, objName).editor
    if (remembered) return remembered
    const legacy = defaultView ? LEGACY_EDITOR_VIEWS[defaultView] : undefined
    if (legacy && isEditorLayout(legacy)) return legacy
    const designed = (sm?.list_view as { editor_layout?: unknown } | undefined)?.editor_layout
    if (isEditorLayout(designed)) return designed
    return isMobileViewport() ? 'compact' : DEFAULT_EDITOR_LAYOUT
  })
  // The person switched something on this mount: a later server answer must
  // not move them (it only fills in what the mirror did not know).
  const touchedViewRef = useRef(false)
  const touchedEditorRef = useRef(false)
  // The document's handle on its own unsaved edits (see DocumentControl).
  const documentControl = useRef<DocumentControl | null>(null)
  // ── List tabs (list_view.tabs) ──────────────────────────────────────────
  // Drawn from the MODEL at once (the same normaliser the engine runs), then
  // from the server's echo, which adds the counts. The request only NAMES the
  // tab; the server reads its conditions from the saved model.
  const tabsCfg = useMemo(() => (submit ? null : listTabsOf(sm)), [sm, submit])
  const [activeTab, setActiveTab] = useState<string | null>(
    () => initialTab(tabsCfg, getScreenPref(project, objName).tab))
  const activeTabRef = useRef<string | null>(activeTab)
  const [tabEcho, setTabEcho] = useState<TabEcho[] | null>(
    () => (tabsCfg ? tabsEcho(tabsCfg) : null))
  // The model's tabs changed (the designer's preview): redraw the row and keep
  // the tab only while it still exists.
  const tabsSigRef = useRef(JSON.stringify(tabsCfg))
  useEffect(() => {
    const sig = JSON.stringify(tabsCfg)
    if (sig === tabsSigRef.current) return
    tabsSigRef.current = sig
    setTabEcho(tabsCfg ? tabsEcho(tabsCfg) : null)
    const keep = initialTab(tabsCfg, activeTabRef.current)
    activeTabRef.current = keep
    setActiveTab(keep)
  }, [tabsCfg])
  // The editor actually drawn: Document only where a document is anchored.
  const effectiveEditor: EditorLayout = editorLayout === 'document' && !documentView
    ? 'standard' : editorLayout
  // Imperative control of the active view's split-scroll table, so the host
  // menu's "Freeze first column" works in both list and spreadsheet.
  const internalSplitRef = useRef<SplitScrollHandle | null>(null)
  const splitHandleRef = splitHandle ?? internalSplitRef
  // formValues is updated on every keystroke, but only the parent PK
  // columns matter for child-tab routing. Stabilise the parent_pk
  // object so ChildFormTab's useEffect doesn't refetch on each render.
  // Defined here (relies on sm + formValues below) — actual values
  // computed via useMemo a bit further down.

  // Ready state: runtime.ensureReady (the designer's deploy-on-mount) must
  // resolve before the first fetch. Hosts without ensureReady are ready at
  // mount.
  const [ready, setReady] = useState(false)
  const readyRef = useRef(false)

  // List state
  const [rows, setRows] = useState<RowShape[]>([])
  // Who the rows' user-key columns point at, as the engine named them.
  // Names accumulate across pages and loads (see `mergePeople`).
  const [people, setPeople] = useState<PeopleDirectory | null>(null)
  const [total, setTotal] = useState(0)
  const [busy, setBusy] = useState(false)
  // Server-side pagination + designer-enforced filter conditions.
  const [loadingMore, setLoadingMore] = useState(false)
  const [enforcedEcho, setEnforcedEcho] = useState<EnforcedConditionEcho[] | undefined>()
  // Designer-defined filter sets the end user can switch between (+ bypass).
  const [filterSetsEcho, setFilterSetsEcho] = useState<EnforcedFilterSetEcho[] | undefined>()
  // Non-blocking messages from the active set's filter validations.
  const [filterNotices, setFilterNotices] = useState<FilterNotice[] | undefined>()
  const [activeFilterSetId, setActiveFilterSetId] = useState<string | null>(null)
  const [bypassed, setBypassed] = useState(false)
  const filterSetIdRef = useRef<string | undefined>(undefined)
  // An 'open' action button found several matching records: the list shows
  // just those until the person clears it.
  const [openFilter, setOpenFilter] = useState<OpenListFilter | null>(null)
  const openFilterRef = useRef<OpenListFilter | null>(null)
  const bypassRef = useRef(false)
  // Pagination mode: 'infinite' appends older pages ("Load earlier"); 'paged'
  // replaces the visible rows with one server page at a time (Prev/Next) so
  // the DOM stays bounded on large tables.
  const [pageMode, setPageMode] = useState<'infinite' | 'paged'>('infinite')
  const [pageSize, setPageSize] = useState(50)
  const [page, setPage] = useState(0) // 0-based; only meaningful in 'paged' mode
  const rowsRef = useRef<RowShape[]>([])
  const pageSizeRef = useRef(50)
  // Once the user picks a page size we stop letting the designer-configured
  // page_size echo overwrite it.
  const userPickedSizeRef = useRef(false)
  const overridesRef = useRef<EnforcedOverrides>({})
  useEffect(() => { rowsRef.current = rows }, [rows])

  // Form state (rendered inside an editor modal)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editTxnId, setEditTxnId] = useState<string | null>(null)
  // Per-column protect overrides ({form_key: 1|0}) returned by item_change —
  // 1 locks the column, 0 unlocks even if statically protected. Reset whenever
  // a different record opens; seeded on open so conditionally-disabled columns
  // start in the right state.
  const [dynamicProtect, setDynamicProtect] = useState<Record<string, 0 | 1>>({})
  // View-only: the loaded record is locked by a designer edit-lock rule (or is
  // awaiting approval), surfaced by the engine on the load response. The form
  // opens read-only with a View indicator and the Save controls are hidden.
  const [viewOnly, setViewOnly] = useState(false)
  const [lockReason, setLockReason] = useState('')
  // The engine's verdict that the open record's Vision workflow is in progress.
  const [wfInProgress, setWfInProgress] = useState(false)
  // The record popup names the SCREEN it belongs to — "New Registration
  // Master" rather than a bare "New record" — so a modal opened from a
  // dashboard, a second screen or a drill-down says which object it is editing.
  // Same source and fallback the form-input capture view already uses
  // (EmbedCapturePreview), so the two views title a record identically.
  const screenName = sm?.form_title || objName
  const editorTitle = viewOnly
    ? `View ${screenName}`
    : editTxnId ? `Edit ${screenName}` : `New ${screenName}`
  const [historyTxn, setHistoryTxn] = useState<string | null>(null)
  // Whether this object is placed on a Process Flow, and so whether a record
  // can offer a trail at all. Resolved once per object when the screen mounts:
  // an application that draws no flows makes ONE cheap call and looks exactly
  // as it did before this existed.
  const [onFlow, setOnFlow] = useState(false)
  const [trailTxn, setTrailTxn] = useState<string | null>(null)
  // Bumped whenever this screen re-reads its records. The process view
  // watches it so a save, an action or a delete made from ANYWHERE on the
  // screen refreshes the cards it is showing, the way the list refreshes
  // itself.
  const [dataVersion, setDataVersion] = useState(0)
  const [aliasTxn, setAliasTxn] = useState<string | null>(null)
  // The "Also known as" action is offered only when three things hold: the
  // object is a vector-searchable master, its model allows aliases, and the
  // host supplied the transport. Any one missing and teaching an alias would
  // be a no-op the user can't see, so the action simply isn't there.
  const aliasesEnabled = !!(
    model?.vector_search?.enabled
    && model.vector_search.allow_alias !== false
    && runtime.recordAliases && runtime.saveRecordAliases
  )
  const openAliases = aliasesEnabled ? setAliasTxn : undefined
  // "Assign to…": hand a record to a colleague. The screen resolves WHICH
  // record and what it is called (the same label the alias dialog shows, so
  // one record reads one way everywhere) and the host owns the dialog — only
  // it has the work panel the task lands in.
  const openAssign = onAssignRecord
    ? (txnId: string) => onAssignRecord({
      objName,
      recordKey: String(txnId),
      label: recordDisplayLabel(displayRowsRef.current, String(txnId), model),
      objTitle: String(sm?.form_title || sm?.main_table || objName),
    })
    : undefined
  // "My Approvals" inbox: records pending the current user's approval across
  // objects. The badge shows how many are pending; the count is best-effort
  // (approvals may not be configured for any object).
  const [approvalsOpen, setApprovalsOpen] = useState(false)
  const [approvalsCount, setApprovalsCount] = useState<number | null>(null)
  // Whether the currently open object is approval-controlled — echoed by the
  // engine on the list response. Drives whether the "Approvals" inbox button
  // is shown on this screen at all.
  const [approvalEnabled, setApprovalEnabled] = useState(false)
  // ── Per-object end-user rights (standalone runtime) ───────────────────
  // Fetched on mount when the host supplies runtime.rights; hosts without it
  // (the platform designer preview / Run view) keep FULL rights — behavior
  // unchanged. While the fetch is pending actions stay hidden (no revoke
  // flicker); a fetch FAILURE falls back to full rights — the server-side
  // authorization on every call stays the authoritative gate, this is UX on
  // top of it, exactly like the legacy runtime embed.
  // The Export system action: the open Export window (its scope) and the
  // organization's template manager. Drawn by the screen itself; a host only
  // supplies `runtime.txn.exportApi`.
  const [exportScope, setExportScope] = useState<ExportScope | null>(null)
  const [exportManage, setExportManage] = useState<{ templateId?: string } | null>(null)
  const [exportReloadKey, setExportReloadKey] = useState(0)
  const [rights, setRights] = useState<ObjectRightsSummary | null>(null)
  const [rightsPending, setRightsPending] = useState(!!runtime.rights)
  const can = useCallback((a: string) => {
    if (!runtime.rights) return true
    if (rightsPending) return false
    if (!rights) return true // fetch failed — server still gates
    return rights.actions.includes('*') || rights.actions.includes(a)
  }, [runtime, rights, rightsPending])
  // The viewer may open records for editing (vs view-only) — mirrors the
  // legacy runtime embed's `can('edit') || can('save')`.
  const canEditRecords = can('edit') || can('save')
  // Per-button rights: a custom button some role holds BY NAME is hidden from
  // a viewer who does not hold it (the server refuses it as well).
  const canUseAction = useCallback(
    (name: string) => !runtime.rights || rightsPending || canUseButton(rights, name),
    [runtime, rights, rightsPending])
  const [formValues, setFormValues] = useState<Record<string, unknown>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formBanner, setFormBanner] = useState<string | null>(null)
  const [formDirty, setFormDirty] = useState(false)
  // The header field the USER last changed — Line Entry's `on_header_change`
  // offers / runs a line command when it names that column. Loads and
  // item_change cascades never set it.
  const [headerEdit, setHeaderEdit] = useState<HeaderEdit | null>(null)

  // In-memory detail tree for the record being added/edited: the header's
  // children keyed by child form_no, each row carrying its own __children.
  // Child grids read/write this buffer instead of the server; the whole tree
  // commits atomically on Save. In edit mode each child table is lazy-loaded
  // into the buffer when its tab first opens.
  const [detailsBuffer, setDetailsBuffer] = useState<EmbedDetailMap>({})
  // The latest save handlers, for a save that re-runs itself after flushing
  // row completions (its own closure holds the pre-flush state).
  const saveFormRef = useRef<() => Promise<unknown>>(async () => undefined)
  const saveAndCloseRef = useRef<() => Promise<unknown>>(async () => undefined)
  const detailsBufferRef = useRef<EmbedDetailMap>({})
  // Bumped after each save so a `row_select` form lists its unticked
  // candidates again (the save echoes only the rows it kept).
  const [rowSelectRefill, setRowSelectRefill] = useState(0)
  useEffect(() => { detailsBufferRef.current = detailsBuffer }, [detailsBuffer])
  const editTxnIdRef = useRef<string | null>(editTxnId)
  useEffect(() => { editTxnIdRef.current = editTxnId }, [editTxnId])
  // Child form_nos currently being lazy-loaded (dedupe concurrent fetches).
  const loadingFormsRef = useRef<Set<string>>(new Set())

  // ── Additional attributes (user-defined attributes) ───────────────────
  // Which attributes apply to the open record is decided by the server
  // (`uda_resolve`, plan D4): asked when a record opens, again when one of
  // its DECIDING fields changes and after a save. The answer's columns are
  // merged into the header form for rendering, item change and Customize.
  // Values of columns that stop applying stay in formValues (switching back
  // restores them); the server drops them at save (D6).
  const udaHeaderNo = forms[0]?.formNo || '1'
  const [udaNonce, setUdaNonce] = useState(0)
  const udaHeader = useUdaRecord({
    api: udaStore, formNo: udaHeaderNo, values: formValues, txnId: editTxnId,
    active: !!udaStore && editorOpen, nonce: udaNonce,
  })
  // The list's promoted attributes + flags: asked once per layer version,
  // and only when the list said the header form has attributes (`_uda`).
  const [udaListVersion, setUdaListVersion] = useState<number | null>(null)
  const udaList = useUdaList({
    api: udaStore, formNo: udaHeaderNo, defVersion: udaStore ? udaListVersion : null, active: !!udaStore,
  })
  const udaHeaderSm = useMemo(() => withUdaColumns(sm, udaHeader.columns), [sm, udaHeader.columns])
  const udaHeaderSmView = useMemo(
    () => withUdaColumns(headerSm, udaHeader.columns), [headerSm, udaHeader.columns])
  const udaListSm = useMemo(
    () => withUdaColumns(sm, promotedColumns(udaList.columns)), [sm, udaList.columns])
  // "Asked because Customer type is EXPORT (from the customer master)" in
  // each attribute group's header, and the "Additional details" sub-heading
  // above the attribute groups (prototype screen 1).
  const udaOwnGroups = useMemo(
    () => new Set((sm?.columns || []).map(w => String(w?.column?.group_name || 'Others'))), [sm])
  const udaCaptions = useMemo(() => {
    if (!udaHeader.sets.length) return undefined
    const columnOf = (via: string) => {
      const name = String(via || '').split('.').slice(1).join('.').trim().replace(/\s+/g, '_').toLowerCase()
      return (sm?.columns || []).map(w => w.column).find(c => !!c && columnFormKey(c) === name)
    }
    const caps = groupCaptions(udaHeader.sets, udaOwnGroups,
      (why) => masterSourceNote(why, udaHeader.columns, columnOf))
    return Object.keys(caps).length ? caps : undefined
  }, [udaHeader.sets, udaHeader.columns, sm, udaOwnGroups])
  const udaGroupsHeading = useMemo(() => {
    const groups = attributeGroups(udaHeader.columns, udaOwnGroups)
    return groups.length ? { groups, label: 'Additional details' } : undefined
  }, [udaHeader.columns, udaOwnGroups])
  // Flags raised on the open record (title strip).
  const udaHeaderFlags = useMemo(() => (udaHeader.flags.length
    ? evaluateFlags(udaHeader.flags,
      { ...formValues, ...driverValues(udaHeader.columns, udaHeader.drivers) }, { place: 'header_badge' })
    : []), [udaHeader.flags, udaHeader.columns, udaHeader.drivers, formValues])
  const udaHeaderBadges = udaHeaderFlags.length ? <FlagBadges flags={udaHeaderFlags} /> : null
  const udaHeaderNotice = udaHeader.error
    ? <UdaNotice message={udaHeader.error} onRetry={udaHeader.retry} />
    : null
  // A list row's flags (show_on list_column). The ENGINE evaluates them per
  // row (`_uda_flags`: the keys that fire) — the values they read are not on
  // the list; the list-scope answer gives the names, icons and colours.
  const udaListFlagDefs = useMemo(
    () => (udaList.flags || []).filter(f => !f.hidden && (f.show_on || []).includes('list_column')),
    [udaList.flags])
  const udaRowFlags = useMemo(() => (udaListFlagDefs.length
    ? (row: Record<string, unknown>) => {
      const fired = Array.isArray(row._uda_flags) ? (row._uda_flags as unknown[]).map(String) : []
      const raised = udaListFlagDefs.filter(f => fired.includes(String(f.key)))
      return raised.length ? <FlagIcons flags={raised} size={24} /> : null
    }
    : undefined), [udaListFlagDefs])
  // Customize → Additional attributes (the setup screen arrives in Phase 5:
  // until UDA_SETUP_SCREEN is registered the entry stays hidden and no
  // can-setup request is made).
  const UdaSetup = UDA_SETUP_SCREEN
  const [canUdaSetup, setCanUdaSetup] = useState(false)
  const [czUda, setCzUda] = useState<{ formNo?: string; columnKey?: string } | null>(null)
  useEffect(() => {
    let cancelled = false
    if (!UdaSetup || !customizing || !runtime.uda) { setCanUdaSetup(false); setCzUda(null); return undefined }
    runtime.uda.canSetup(objName)
      .then((v) => { if (!cancelled) setCanUdaSetup(!!v) })
      .catch(() => { if (!cancelled) setCanUdaSetup(false) })
    return () => { cancelled = true }
  }, [UdaSetup, customizing, runtime, objName])

  // Parent PK for child tabs, stable across re-renders unless the PK
  // values actually change (avoids ChildFormTab's useEffect re-firing
  // on every parent-form keystroke).
  const parentPk = useMemo(
    () => (sm ? buildParentPk(sm, formValues) : {}),
    [sm, formValues],
  )
  const parentPkKey = useMemo(
    () => JSON.stringify(parentPk),
    [parentPk],
  )

  // Full-detail error modal: opened whenever a request fails with an
  // unexpected/server error so the user can read and copy the complete
  // message + traceback.
  const [errorReport, setErrorReport] = useState<ErrorReport | null>(null)

  // List-level custom action result presenters (html_modal / select_grid run
  // from the toolbar or a row's 3-dot menu — i.e. with no record open yet).
  const [actionHtml, setActionHtml] = useState<{ title: string; html: string } | null>(null)
  const [actionGrid, setActionGrid] = useState<{
    title: string
    res: RunActionResponse
    target: { mode: 'add' } | { mode: 'edit'; txnId: string }
  } | null>(null)
  // handler='report' — the bound Jasper report opened with row-resolved params.
  const [actionReport, setActionReport] = useState<{
    title: string
    objName: string
    adapter: ReportRuntimeAdapter
    params: Record<string, unknown>
  } | null>(null)
  // Custom actions split by resolved scope: form-scoped render before "Add",
  // row-scoped render in each row's 3-dot menu. System add / edit actions
  // come back as systemOverrides — they merge into the built-in Add button /
  // Edit pencil (label / hover text + pre-action validations) instead of
  // rendering as duplicate buttons.
  const { formActions, rowActions, systemOverrides } = useMemo(
    () => partitionActionsByScope(headerSm?.actions), [headerSm])

  // Capture a failed request: parse the envelope, open the details modal
  // (with the server traceback when present), and — unless suppressed —
  // raise a short toast for immediate feedback. `context` is a
  // human-readable description of what was being attempted.
  const reportError = useCallback(
    (context: string, err: unknown, opts?: { toast?: boolean }) => {
      const parsed = parseApiError(err)
      setErrorReport({ context, parsed })
      if (opts?.toast !== false) {
        notify.error(`${context}: ${parsed.message}`)
      }
    },
    // notify identity follows runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime],
  )

  // Fetch one server page. ``append`` adds an older page below the current
  // rows ("load earlier"); otherwise it replaces them (refresh / re-range).
  // ``withCounts`` asks for every tab's record count — on a first load, a
  // scope change and after a save; never on paging (the counts cannot move).
  const fetchPage = useCallback(async (offset: number, append: boolean, withCounts = false) => {
    if (!readyRef.current) return
    if (append) setLoadingMore(true)
    else setBusy(true)
    try {
      const tab = activeTabRef.current
      const res = await runtime.list(objName, {
        ...(tab ? { tab } : {}),
        ...(tab && withCounts && tabsCfg?.counts ? { withTabCounts: true } : {}),
        limit: pageSizeRef.current,
        offset,
        // Only once the user has chosen a size does it beat the object's
        // configured page size — before that, `pageSizeRef` is merely this
        // screen's own default and the designer's setting should win.
        explicitLimit: userPickedSizeRef.current,
        enforcedOverrides: overridesRef.current,
        filterSetId: filterSetIdRef.current,
        bypass: bypassRef.current,
        // The records an 'open' action button matched, when it matched more
        // than one (see useOpenSequence) — cleared from the chip above the list.
        ...(openFilterRef.current ? { filters: openFilterRef.current.filters } : {}),
      })
      // The designer-configured page size seeds the control, but never
      // overrides a size the user explicitly chose.
      if (res.page_size && !userPickedSizeRef.current) {
        pageSizeRef.current = res.page_size
        setPageSize(res.page_size)
      }
      setPeople((prev) => mergePeople(prev, res))
      // The header form's attribute layer version (null = none) — drives the
      // list's promoted attribute columns and flags.
      setUdaListVersion(res._uda ? (Number(res._uda.def_version) || 0) : null)
      setEnforcedEcho(res.enforced_filters)
      setApprovalEnabled(!!res.approval_enabled)
      setFilterSetsEcho(res.enforced_filter_sets)
      setFilterNotices(res.filter_notices)
      setActiveFilterSetId(res.active_filter_set_id ?? null)
      setBypassed(!!res.bypassed)
      // Keep the active-set ref in sync with what the server resolved, so a
      // first load (no explicit selection) settles on the default set.
      if (!bypassRef.current) filterSetIdRef.current = res.active_filter_set_id ?? undefined
      if (res.list_tabs?.length) {
        // A response without counts (paging, a tab switch) keeps the last ones.
        const echo = res.list_tabs
        setTabEcho((prev) => echo.map((t) => (t.count != null ? t : {
          ...t, ...(prev?.find((p) => p.id === t.id)?.count != null
            ? { count: prev!.find((p) => p.id === t.id)!.count } : {}),
        })))
      }
      setTotal(res.total)
      setRows((prev) => append
        ? [...prev, ...(res.rows as RowShape[])]
        : (res.rows as RowShape[]))
    } catch (err) {
      // A remembered (or linked) tab the model no longer has: open on the
      // model's default instead of an error the person cannot act on.
      const parsed = parseApiError(err)
      if (activeTabRef.current && tabsCfg
          && parsed.validationErrors?.some((e) => e?.code === 'TAB_NOT_FOUND')
          && activeTabRef.current !== tabsCfg.default) {
        activeTabRef.current = tabsCfg.default
        setActiveTab(tabsCfg.default)
        if (append) setLoadingMore(false)
        else setBusy(false)
        await fetchPage(0, false, true)
        return
      }
      reportError('Could not load records', err)
    } finally {
      if (append) setLoadingMore(false)
      else setBusy(false)
    }
  }, [runtime, objName, reportError, tabsCfg])

  const refreshList = useCallback(async () => {
    setPage(0)
    setDataVersion((v) => v + 1)
    await fetchPage(0, false, true)
  }, [fetchPage])

  // A tab picked: the first page of its records; the counts stand (the scope
  // did not change). Remembered for this person unless `remember` is false
  // (a server answer applying what they chose elsewhere).
  const selectTab = useCallback(async (id: string, opts?: { remember?: boolean }) => {
    if (id === activeTabRef.current) return
    activeTabRef.current = id
    setActiveTab(id)
    if (opts?.remember !== false) updateScreenPref(project, objName, { tab: id })
    setPage(0)
    await fetchPage(0, false)
  }, [fetchPage, project, objName])

  // Best-effort count of records pending the current user's approval. Failures
  // are swallowed (approvals may simply not be configured) so the toolbar badge
  // just stays hidden rather than surfacing an error.
  const refreshApprovalsCount = useCallback(async () => {
    try {
      const res = await runtime.approvals.list({ limit: 1 })
      // `count` is only the returned page length (1 with limit:1); `total` is
      // the true pre-slice count. Prefer total, fall back for older servers.
      setApprovalsCount(res?.total ?? res?.count ?? 0)
    } catch {
      setApprovalsCount(null)
    }
  }, [runtime])

  const loadEarlier = useCallback(async () => {
    await fetchPage(rowsRef.current.length, true)
  }, [fetchPage])

  // Paged mode: jump to a specific 0-based page, replacing the visible rows.
  const gotoPage = useCallback(async (next: number) => {
    const target = Math.max(0, next)
    setPage(target)
    await fetchPage(target * pageSizeRef.current, false)
  }, [fetchPage])

  // User changed rows-per-page: lock the choice, reset to the first page.
  const changePageSize = useCallback(async (size: number) => {
    userPickedSizeRef.current = true
    pageSizeRef.current = Math.max(1, size)
    setPageSize(pageSizeRef.current)
    setPage(0)
    await fetchPage(0, false)
  }, [fetchPage])

  // Switch between infinite-scroll and paged navigation; both reset to the
  // first page so the offset bookkeeping starts clean.
  const changeMode = useCallback(async (next: 'infinite' | 'paged') => {
    setPageMode(next)
    setPage(0)
    await fetchPage(0, false)
  }, [fetchPage])

  // ONE pagination object, handed to whichever view is on screen.
  //
  // Every built-in view reads the same `rows` off the same query, so they page
  // identically or they disagree about what "this page" means. The compact card
  // stack used to be given only the append half of this and drew a lone "Load
  // more" button, which left it stuck on whatever first page the object's
  // configured size happened to be.
  const listPagination: ListPagination = useMemo(() => ({
    loadedCount: rows.length,
    total,
    hasMore: rows.length < total,
    loadingMore,
    onLoadEarlier: loadEarlier,
    mode: pageMode,
    onSetMode: changeMode,
    pageSize,
    onSetPageSize: changePageSize,
    page,
    onGotoPage: gotoPage,
  }), [rows.length, total, loadingMore, loadEarlier, pageMode, changeMode,
    pageSize, changePageSize, page, gotoPage])

  // End user changes the enforced conditions' values (locked ones are ignored
  // server-side); reset to the newest page.
  const applyFilters = useCallback(async (
    overrides: EnforcedOverrides,
  ) => {
    overridesRef.current = overrides
    setPage(0)
    await fetchPage(0, false, true)
  }, [fetchPage])

  // User switched the active filter set (or chose "No filter"). Reset the
  // per-condition overrides — the new set's defaults come from its echo.
  const selectFilterSet = useCallback(async (
    sel: { setId?: string; bypass?: boolean },
  ) => {
    bypassRef.current = !!sel.bypass
    filterSetIdRef.current = sel.bypass ? undefined : sel.setId
    overridesRef.current = {}
    setPage(0)
    await fetchPage(0, false, true)
  }, [fetchPage])

  // Prepare the backing store once on mount (the designer deploys the preview
  // schema via runtime.ensureReady), then load the first page + the approvals
  // badge. The host re-triggers the whole sequence (redeploy) by bumping
  // `versionKey`. An ensureReady failure keeps the screen un-ready — the host
  // surfaced the error (see contract).
  useEffect(() => {
    let cancelled = false
    void (async () => {
      if (runtime.ensureReady) {
        try {
          await runtime.ensureReady(objName)
        } catch {
          return
        }
      }
      if (cancelled) return
      // Resolve the viewer's per-object rights BEFORE the first fetch so the
      // toolbar renders with the right affordances. can_read === false skips
      // the data fetches entirely (the no-access notice renders instead).
      if (runtime.rights) {
        let canRead = true
        try {
          const r = await runtime.rights(objName)
          if (cancelled) return
          setRights(r)
          canRead = r.can_read
        } catch (err) {
          if (cancelled) return
          setRights(null) // full-rights fallback — the server still gates
          // eslint-disable-next-line no-console
          console.warn('rights fetch failed', err)
        }
        setRightsPending(false)
        // A submit form never reads records; its gate is `add` (below).
        if (!canRead && !submit) {
          readyRef.current = true
          setReady(true)
          return
        }
      }
      readyRef.current = true
      setReady(true)
      // A submit form has no records, no process trail and no approvals.
      if (submit) return
      // Does this object sit on a process flow? Answered beside the rights
      // resolve and never awaited by the list — a slow answer must not hold up
      // the records, it only decides whether one menu item is offered.
      void processFlows(runtime.execute, objName)
        .then((res) => {
          if (cancelled) return
          const on = (res.in_flows || []).length > 0
          setOnFlow(on)
          // Left on the process view last time: restore it now that the answer
          // is in. Restoring it before would have shown "no process" for as
          // long as the check took, on the one screen that does have one.
          if (on && rememberedListView(project, objName) === 'process') setView('process')
        })
        .catch(() => { if (!cancelled) setOnFlow(false) })
      await refreshList()
      void refreshApprovalsCount()
    })()
    return () => { cancelled = true }
    // Deliberately keyed on versionKey only — mirrors the original
    // deploy-once-on-mount behavior (+ host-driven redeploys).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionKey])

  // ── Form actions ──────────────────────────────────────────────────────

  const guardDirtyClose = useCallback((): boolean => {
    // A user may add/edit/delete child rows without touching the header, so
    // the buffer's unsaved state must gate the discard prompt too.
    if (!formDirty && !bufferHasUnsaved(detailsBuffer)) return true
    return window.confirm(
      'You have unsaved changes in the form. Discard them?',
    )
  }, [formDirty, detailsBuffer])

  // Evaluate the form's conditional-protect state once for the given values
  // (rule-mode item_changes carrying protect entries only fire on a live
  // change, so opening a form must compute the initial lock state). Guarded
  // by a sequence token so a slow response can't lock fields of a different
  // record the user has since opened.
  const protectSeedSeq = useRef(0)
  const seedProtect = useCallback((header: Record<string, unknown>) => {
    const seq = ++protectSeedSeq.current
    void collectSeededProtect(sm, header, async (col, hdr) => {
      const res = await runtime.execute(objName, 'item_change', {
        field_name: columnFormKey(col), header: hdr, header_values: {},
      })
      return res as ItemChangeResult
    }).then((protect) => {
      if (seq !== protectSeedSeq.current) return
      if (Object.keys(protect).length === 0) return
      setDynamicProtect(prev => ({ ...prev, ...protect }))
    })
  }, [sm, runtime, objName])

  // The same lock seed for the record's ADDITIONAL ATTRIBUTES, once the
  // resolve answer has brought their columns: their own protect rules fired
  // once over the stored values. Same token: an answer that lands after the
  // record was closed or swapped is dropped (its own epoch token: the base
  // seed's token moves when ITS seed starts, which may be after this one).
  const udaSeededRef = useRef('')
  // Bumped whenever a different record opens (or the editor closes), so a
  // slow seed never lands on another record.
  const udaEpochRef = useRef(0)
  useEffect(() => { udaEpochRef.current += 1 }, [editorOpen, editTxnId])
  useEffect(() => {
    if (!editorOpen || !udaHeader.resolved || udaHeader.columns.length === 0) return
    const seq = udaEpochRef.current
    const sig = `${seq}|${udaHeader.defVersion}|${udaHeader.columns.map(c => c.name).join(',')}`
    if (sig === udaSeededRef.current) return
    udaSeededRef.current = sig
    void seedUdaProtect(udaHeader.columns, formValues, async (col, hdr) => {
      const res = await runtime.execute(objName, 'item_change', {
        field_name: columnFormKey(col), header: hdr, header_values: {},
      })
      return res as ItemChangeResult
    }).then((protect) => {
      if (seq !== udaEpochRef.current) return
      if (Object.keys(protect).length === 0) return
      setDynamicProtect(prev => ({ ...prev, ...protect }))
    }).catch(() => { /* best-effort: the form stays usable */ })
    // formValues is read as of the answer; re-seeding on every keystroke is
    // what the live item change already covers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, udaHeader.resolved, udaHeader.columns, udaHeader.defVersion, runtime, objName])

  /**
   * Open a blank record. ``seed`` pre-fills it from a worklist task: values a
   * rule mapped off the source record, or that the Assistant derived from a
   * sentence. The seed is applied LAST and wins over every default, because it
   * was chosen deliberately for this piece of work — a default exists only to
   * fill what nobody decided.
   */
  const startAdd = useCallback(async (seed?: Record<string, unknown>): Promise<Record<string, unknown> | undefined> => {
    setEditTxnId(null)
    // Mirror startEdit: child sections mount before this component's own
    // ref-sync effect runs, so the ref must drop the PREVIOUS record's id
    // synchronously or a lazy-load fired on mount would fetch that record's
    // rows into a brand-new one.
    editTxnIdRef.current = null
    setViewOnly(false)
    setLockReason('')
    setWfInProgress(false)
    setDetailsBuffer({})
    setFieldErrors({})
    setFormBanner(null)
    setDynamicProtect({})
    protectSeedSeq.current++
    // This open's token: a slow resolve-defaults answer must not land on a
    // record the user has since closed or swapped for another.
    const openSeq = protectSeedSeq.current
    setActiveFormNo(forms[0]?.formNo || '1')
    // 1. Seed literal defaults locally.
    // 2. Fetch project-variable resolutions from the backend and merge.
    //    Audit tokens are intentionally left blank — they're stamped
    //    server-side at INSERT and depend on save-time context.
    const initial: Record<string, unknown> = sm ? literalDefaults(sm) : {}
    // A seed's own keys may be authored as logical column names; map them to
    // the form keys this screen reads, so a rule written against the model
    // lands on the right fields.
    const seedValues = seed ? mapSeedToFormKeys(seed, sm) : {}
    const withSeed = { ...initial, ...seedValues }
    setFormValues(withSeed)
    setFormDirty(Object.keys(seedValues).length > 0)
    setEditorOpen(true)
    let seeded = withSeed
    try {
      const res = await runtime.resolveDefaults(objName)
      if (openSeq !== protectSeedSeq.current) return undefined
      if (res?.values && Object.keys(res.values).length > 0) {
        const server = res.values
        // The server's resolution WINS over the client's literal seeding: it
        // is the same default resolved properly, and on a Vision form it
        // carries the bean's itm_default values, which Vision applies AFTER
        // the initial values (RATE_ROUND 'R' over an initial 'N'). What it
        // must not beat is what somebody chose — the seed, or a field the
        // user already changed while this request was in flight (its value no
        // longer equals what was seeded).
        const mergeServer = (base: Record<string, unknown>) => {
          const out = { ...base }
          for (const [k, v] of Object.entries(server)) {
            if (k in seedValues) continue
            if (k in base && base[k] !== withSeed[k]) continue
            out[k] = v
          }
          return out
        }
        seeded = mergeServer(withSeed)
        setFormValues((prev) => mergeServer(prev))
      }
      // Lock state the defaults imply (a Vision bean's itm_default protect
      // flags) — the same dynamic-protect channel an item change uses.
      if (res?.protect && Object.keys(res.protect).length > 0) {
        const protect = res.protect
        setDynamicProtect((prev) => ({ ...prev, ...protect }))
      }
    } catch (err) {
      // Non-fatal: form still opens with literal defaults; user can fill
      // project-variable columns manually. Surfacing the error would
      // be noise for someone who hasn't defined any variables yet.
      // eslint-disable-next-line no-console
      console.warn('resolve-defaults failed', err)
    }
    // Initial lock state for conditionally-protected columns (e.g. a
    // defaulted radio that disables one of two lookups).
    seedProtect(seeded)
    // What the form now holds — for a caller that goes on to set columns
    // (an 'open' action button's sequence).
    return seeded
  }, [sm, runtime, objName, forms, seedProtect])

  const startEdit = useCallback(async (
    txnId: string, opts?: { forceView?: boolean },
  ): Promise<{ header: Record<string, unknown>; viewOnly: boolean; lockReason: string } | undefined> => {
    setBusy(true)
    setDetailsBuffer({})
    setFieldErrors({})
    setFormBanner(null)
    setDynamicProtect({})
    protectSeedSeq.current++
    setActiveFormNo(forms[0]?.formNo || '1')
    try {
      const res = await runtime.load(objName, txnId)
      // A Vision header's itm_defaultedit (J2EE): descriptive values shown on
      // open, folded in BEFORE the form is seeded so the record does not open
      // dirty, plus the lock state the stored values imply.
      const itemDefaults = res.item_defaults
      const header = { ...res.header, ...(itemDefaults?.updates || {}) }
      delete header._txn_id
      setPeople((prev) => mergePeople(prev, res))
      setFormValues(header)
      setEditTxnId(txnId)
      // Set the ref synchronously (not via its sync effect). In the compact
      // editor every child form mounts on this same open, and a child's
      // lazy-load effect runs BEFORE the parent's ref-sync effect — so without
      // this, ensureChildRowsLoaded would see a stale null editTxnId and skip
      // the fetch. (The desktop editor dodged this by mounting child tabs
      // lazily on tab-click.)
      editTxnIdRef.current = txnId
      if (itemDefaults?.protect && Object.keys(itemDefaults.protect).length > 0) {
        const protect = itemDefaults.protect
        setDynamicProtect((prev) => ({ ...prev, ...protect }))
      }
      // Initial lock state for conditionally-protected columns, computed from
      // the record's stored values.
      seedProtect(header)
      // Open read-only when the engine reports the record locked, when the
      // caller forced view (a 'view' system action was invoked from the list),
      // or when the viewer lacks edit/save rights (standalone runtime).
      const ro = !!opts?.forceView || res.editable === false || !canEditRecords
      const roReason = ro
        ? (res.lock_reason
          || (!canEditRecords ? 'You do not have permission to edit records' : ''))
        : ''
      setViewOnly(ro)
      setLockReason(roReason)
      setWfInProgress(res.wf_in_progress === true)
      setFormDirty(false)
      setEditorOpen(true)
      return { header, viewOnly: ro, lockReason: roReason }
    } catch (err) {
      reportError('Could not load record', err)
      return undefined
    } finally {
      setBusy(false)
    }
  }, [runtime, objName, forms, reportError, canEditRecords, seedProtect])

  const closeEditor = useCallback(() => {
    if (!guardDirtyClose()) return
    setEditorOpen(false)
    setEditTxnId(null)
    setViewOnly(false)
    setLockReason('')
    setWfInProgress(false)
    setFormValues({})
    setDetailsBuffer({})
    setFieldErrors({})
    setFormBanner(null)
    setFormDirty(false)
    setDynamicProtect({})
    protectSeedSeq.current++
    setActiveFormNo(forms[0]?.formNo || '1')
  }, [guardDirtyClose, forms])

  // A user's Add / Edit. Inside the Run shell (and in a view whose editor is
  // the record editor) the shell opens it as a minimisable session instead;
  // view-only opens, Customize, the document / spreadsheet / form-input views
  // and action-driven opens stay local, exactly as before.
  const delegatesEditors = !!shellMain && !customizing
    && (view === 'list' || view === 'compact') && editorLayout !== 'document'
  const openAdd = useCallback(async (seed?: Record<string, unknown>): Promise<Record<string, unknown> | undefined> => {
    // Only a PLAIN object is a seed (a DOM onClick hands over its event).
    const plain = seed && (Object.getPrototypeOf(seed) === Object.prototype || Object.getPrototypeOf(seed) === null)
      ? seed : undefined
    if (delegatesEditors && shellMain?.requestEditor({ objName, mode: 'add', seed: plain })) return undefined
    return startAdd(plain)
  }, [delegatesEditors, shellMain, objName, startAdd])
  const openEdit = useCallback(async (txnId: string, opts?: { forceView?: boolean }) => {
    if (delegatesEditors && !opts?.forceView
      && shellMain?.requestEditor({ objName, mode: 'edit', txnId: String(txnId) })) return undefined
    return startEdit(txnId, opts)
  }, [delegatesEditors, shellMain, objName, startEdit])

  // A submit form IS its one open form: open it as soon as the screen is
  // ready, and again after Clear (closeEditor), so there is always a form.
  useEffect(() => {
    if (!submit || !ready || rightsPending || editorOpen || !can('add')) return
    void startAdd()
  }, [submit, ready, rightsPending, editorOpen, can, startAdd])

  // ── Run-view deep linking ────────────────────────────────────────────
  // Keep the open record in sync with the URL (initialScreen / onScreenChange)
  // so a shared link restores it and opening a record updates the address bar.
  // A single "applied" key guards the two effects from echoing each other.
  // Inside the Run shell the editor on display may be a SESSION's: the URL
  // then follows the shell's active editor for this screen.
  const appliedScreenRef = useRef<string>('')
  const shellActive = shellMain?.activeEditor || null
  const screenKey = editorOpen ? (editTxnId ? `edit:${editTxnId}` : 'add')
    : shellActive ? (shellActive.mode === 'edit' && shellActive.txnId ? `edit:${shellActive.txnId}` : 'add')
      : ''

  // Restore the URL's intent once the screen is ready (startAdd / startEdit
  // need the backing store). Only active when URL-driven (onScreenChange set).
  useEffect(() => {
    if (!onScreenChange || !ready) return
    const mode = (initialScreen?.mode || '').toLowerCase()
    const rec = initialScreen?.rec || ''
    const want = mode === 'edit' && rec ? `edit:${rec}` : mode === 'add' ? 'add' : ''
    if (want === appliedScreenRef.current) return
    appliedScreenRef.current = want
    if (want === 'add') void openAdd(initialScreen?.seed)
    else if (want.startsWith('edit:')) void openEdit(rec)
    else if (editorOpen) closeEditor()
  }, [
    initialScreen?.mode, initialScreen?.rec, initialScreen?.seed, ready,
    onScreenChange, openAdd, openEdit, closeEditor, editorOpen,
  ])

  // Tell the host the editor opened / closed (a drawer closes with it).
  const onEditorChangeRef = useRef(onEditorChange)
  onEditorChangeRef.current = onEditorChange
  useEffect(() => {
    onEditorChangeRef.current?.({ open: editorOpen, txnId: editTxnId })
  }, [editorOpen, editTxnId])

  // Reflect editor open/close back to the URL.
  useEffect(() => {
    if (!onScreenChange) return
    if (screenKey === appliedScreenRef.current) return
    appliedScreenRef.current = screenKey
    if (!screenKey) onScreenChange(null)
    else if (screenKey.startsWith('edit:')) onScreenChange({ mode: 'edit', rec: screenKey.slice(5) })
    else onScreenChange({ mode: 'add' })
  }, [screenKey, onScreenChange])

  // Append the child-form rows an item_change-style result carries
  // (`forms: {"2": {rows: [...]}}`) to the details buffer — the SAME for a
  // header column's item_change (pick a PO, its lines arrive) and an
  // 'item_change' action button. The header's own entry is skipped; the caller
  // applies its `set` / `protect`.
  const appendResultRows = useCallback((
    resForms: Record<string, ItemChangeFormChange> | undefined, headerNo: string,
  ) => {
    if (!resForms) return
    setDetailsBuffer(b => {
      let changed = false
      const next = { ...b }
      for (const [fno, change] of Object.entries(resForms)) {
        if (fno === headerNo) continue
        if (!formChangeTouchesRows(change)) continue
        // rows are appended, or — with `replace` — swapped for the form's
        // existing ones (saved rows kept as _deleted for the batch save). A
        // `row_select` form takes them as unticked candidates instead.
        const rs = rowSelectOf(topFormsRef.current.find(f => f.formNo === fno)?.sm)
        next[fno] = rs
          // Ticked by default on a new record, and on a saved one whose
          // criteria changed (the list is rebuilt) — not on the open refill.
          ? applySelectableRowsChange(next[fno], change, rs, withLocalId,
            rs.defaultTicked && (editTxnIdRef.current === null || !!change.replace))
          : applyFormRowsChange(next[fno], change, withLocalId)
        changed = true
      }
      return changed ? next : b
    })
  }, [])

  // A line's row event (row_complete…) returned values for the HEADER — e.g.
  // its recomputed totals. ChildFormTab passes them up; apply the header's own
  // `set` to the form.
  const applyHeaderPatch = useCallback((patch: Record<string, ItemChangeFormChange>) => {
    const headerNo = forms[0]?.formNo || '1'
    const set = patch[headerNo]?.set
    if (!set || Object.keys(set).length === 0) return
    setFormValues(prev => ({ ...prev, ...set }))
    setFormDirty(true)
  }, [forms])

  // Debounced item_change handler. Re-evaluates dependent fields via the
  // deployed business-logic function — the SHARED header hook, also used by
  // the approvals inbox's Edit mode so both behave identically.
  const executeFunnel = useCallback(
    (o: string, a: string, p?: Record<string, unknown>) => runtime.execute(o, a, p),
    [runtime],
  )
  const itemChangeMode = useCallback(
    (): 'add' | 'edit' => (editTxnIdRef.current ? 'edit' : 'add'), [])
  const triggerItemChange = useHeaderItemChange({
    execute: executeFunnel,
    notify,
    objName,
    // With the record's additional attributes merged in, so an attribute's
    // own item change fires like any column's.
    sm: udaHeaderSm,
    setFormValues,
    setDynamicProtect,
    editMode: itemChangeMode,
    reportError,
    headerFormNo: forms[0]?.formNo || '1',
    // Lines the handler produced for the child forms (a PO picked on a
    // receipt header bringing its pending lines).
    onFormRows: appendResultRows,
  })

  // "Ignore warnings and proceed" confirm — a save / action blocked ONLY by
  // ignorable business-logic warnings opens this modal; proceeding retries the
  // same call with the acknowledged codes in ignore_warnings.
  const { confirmWarnings: confirmPlainWarnings, warningsModal } = useWarningConfirm(visionDialogs ? 'OK' : undefined)
  // Every action button's failure: an Error popup with only OK.
  const { showActionError, actionErrorModal } = useActionError()
  // Imported Vision apps: a failed validation pops up ONE error at a time (the
  // first) with its code + description and only OK; OK puts the cursor on the
  // failing column. Fix it and save again for the next one. Other apps keep
  // the inline banner listing every error.
  const { showValidationError, validationErrorModal } = useValidationErrorPopup()
  const showVisionValidationError = useCallback((items: ValidationErrorItem[]) => {
    const first = firstValidationError(items)
    if (!first) return
    const field = first.field && first.field !== '_record' ? first.field : null
    setFieldErrors(field ? { [field]: first.message } : {})
    setFormBanner(null)
    showValidationError({
      message: first.message,
      code: first.code,
      fieldLabel: field ? columnLabelFromModel(sm, field) : null,
      onOk: field ? () => focusFieldAnchor(field) : undefined,
    })
  }, [sm, showValidationError])

  // ── Save review (Line Entry, canvas board 8) ─────────────────────────
  // A save refused — or warning — on specific LINES opens one review of all
  // of them: must-fix with "Go to cell", check-and-confirm with a tick each.
  const [review, setReview] = useState<{
    mustFix: ReviewItem[]; confirm: ReviewItem[]; resolve?: (ok: boolean) => void
  } | null>(null)
  const reviewItem = useCallback((e: { field?: string | null; message: string; code?: string | null;
    scope?: { path?: Array<{ form_no: string; index: number }> } | null }, i: number): ReviewItem => {
    const path = e.scope?.path || []
    const last = path[path.length - 1]
    const top = path[0]
    const row = top ? (detailsBufferRef.current[top.form_no] || [])[top.index] as Record<string, unknown> | undefined : undefined
    const childSm = top ? forms.find(f => f.formNo === top.form_no)?.sm : undefined
    // The line's identity: its first filled, visible, non-key column.
    let title = ''
    for (const w of childSm?.columns || []) {
      const c = w?.column
      if (!c || isKeyColumn(c) || c.hidden === true || c.hidden === '1' || c.hidden === 'true') continue
      const v = row?.[columnFormKey(c)]
      if (v != null && v !== '') { title = String(v); break }
    }
    return {
      key: `${e.code || ''}:${path.map(p => `${p.form_no}.${p.index}`).join('/')}:${e.field || ''}:${i}`,
      where: last ? `Line ${path.map(p => p.index + 1).join(' › ')}` : 'Header',
      title, message: e.message, path, field: e.field ?? null, code: e.code ?? null,
    }
  }, [forms])
  const reviewSummary = useCallback((): ReviewSummary[] => topForms.slice(1).map(f => {
    const rows = (detailsBufferRef.current[f.formNo] || []).filter(r => !r._deleted)
    const le = lineEntryOf(f.sm)
    const names = new Set<string>()
    for (const src of le?.sources || []) {
      if (src.picker?.totals?.length) src.picker.totals.forEach(t => names.add(String(t).toLowerCase()))
      else for (const [c, l] of Object.entries(src.limits || {})) if (l?.status) names.add(c.toLowerCase())
    }
    const cols = (f.sm.columns || []).map(w => w?.column).filter((c): c is NonNullable<typeof c> =>
      !!c && names.has(String(c.name).toLowerCase()))
    return {
      label: f.sm.form_title || `Form ${f.formNo}`,
      lines: rows.length,
      sums: cols.map(c => ({
        label: String(c.heading || c.name),
        value: rows.reduce((a, r) => a + (Number(r[columnFormKey(c)]) || 0), 0),
      })),
    }
  }).filter(s => s.lines > 0), [topForms])
  // Warnings: line-scoped ones go to the review, the rest to the usual confirm.
  const confirmWarnings = useCallback((warns: WarningItem[]): Promise<boolean> => {
    if (!warns.some(w => (w.scope?.path || []).length > 0)) return confirmPlainWarnings(warns)
    return new Promise<boolean>(resolve => {
      setReview({ mustFix: [], confirm: warns.map((w, i) => reviewItem(w, i)), resolve })
    })
  }, [confirmPlainWarnings, reviewItem])
  const closeReview = useCallback((ok: boolean) => {
    setReview(r => { r?.resolve?.(ok); return null })
  }, [])
  const goToReviewItem = useCallback((it: ReviewItem) => {
    closeReview(false)
    const top = it.path?.[0]
    if (!top) { setActiveFormNo(forms[0]?.formNo || '1'); return }
    setActiveFormNo(top.form_no)
    const row = (detailsBufferRef.current[top.form_no] || [])[top.index] as Record<string, unknown> | undefined
    if (!row) return
    const key = row._txn_id != null ? `t:${row._txn_id}` : `l:${row._localId}`
    window.setTimeout(() => {
      const cell = document.querySelector<HTMLElement>(
        `[data-grid-row="${key}"][data-grid-col="${it.field || ''}"]`)
        || document.querySelector<HTMLElement>(`[data-grid-row="${key}"]`)
      cell?.scrollIntoView({ block: 'center' })
      cell?.querySelector<HTMLElement>('input, select, textarea, button')?.focus()
    }, 80)
  }, [closeReview, forms])
  // Errors on several lines: mark every failing line and open the review.
  const reviewLineErrors = useCallback((err: unknown): boolean => {
    const parsed = parseApiError(err)
    const scoped = parsed.validationErrors.filter(e => (e.scope?.path || []).length > 0
      && (e.severity || 'E') === 'E')
    if (!scoped.length) return false
    setDetailsBuffer(b => {
      let next = b
      for (const e of scoped) {
        next = annotateRowError(next, e.scope!.path!, e.field ? { [e.field]: e.message } : {}, e.message)
      }
      return next
    })
    setActiveFormNo(scoped[0].scope!.path![0].form_no)
    setReview({ mustFix: scoped.map((e, i) => reviewItem(e, i)), confirm: [] })
    return true
  }, [reviewItem])
  // 21 CFR 11.10(e). Driven by the SERVER's refusal, never by a rule in here:
  // whether a change needs a reason is declared on the object, and a client
  // that decided for itself would be the copy that stops asking.
  const { promptReason, reasonModal } = useReasonPrompt()
  const { confirmAction, actionConfirmModal } = useActionConfirm()
  const { captureFor, actionCaptureModal } = useActionCapture()

  // Route an atomic-batch failure to the right place: a header-form error shows
  // inline on the header tab (existing path); a child/grandchild row error is
  // flagged on that row (annotateRowError walks the buffer's __children) and we
  // jump to the failing child tab with a breadcrumb banner.
  const routeBatchError = useCallback((err: unknown) => {
    const parsed = parseApiError(err)
    const scope = (parsed.details as { scope?: BatchScope } | null)?.scope ?? null
    const path = scope?.path
    if (!path || path.length === 0) {
      if (visionDialogs && parsed.validationErrors.length > 0) {
        showVisionValidationError(parsed.validationErrors)
      } else {
        applyValidationErrors(parsed.validationErrors, parsed.message, setFieldErrors, setFormBanner, notify)
      }
      if (parsed.validationErrors.length === 0) {
        reportError('Save failed', err, { toast: false })
      }
      setActiveFormNo(forms[0]?.formNo || '1')
      return
    }
    const rowFieldErrors: Record<string, string> = {}
    let recordError: string | null = null
    for (const ve of parsed.validationErrors) {
      if (ve.field && ve.field !== '_record') rowFieldErrors[ve.field] = ve.message
      else if (!recordError) recordError = ve.message
    }
    recordError = recordError || parsed.message || 'Save failed'
    setDetailsBuffer(b => annotateRowError(b, path, rowFieldErrors, recordError as string))
    // An inline table lives on the header tab — the row is flagged in place.
    setActiveFormNo(inlineFormNos.has(path[0].form_no)
      ? (forms[0]?.formNo || '1') : path[0].form_no)
    const rowLabel = `Row ${path.map(p => `#${p.index + 1}`).join(' › ')}`
    const first = visionDialogs ? firstValidationError(parsed.validationErrors) : null
    if (first) {
      // The failing line is flagged in its grid; open it to correct the column.
      showValidationError({ message: `${rowLabel}: ${first.message}`, code: first.code })
      return
    }
    setFormBanner(`${rowLabel}: ${recordError}`)
    notify.error(recordError)
    // notify identity follows runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms, inlineFormNos, reportError, runtime, visionDialogs, showVisionValidationError, showValidationError])

  // Atomically commit the whole transaction (header + the buffered child tree)
  // in one request. Stays in the editor afterwards (now in edit mode) so the
  // user can keep working.
  const saveForm = useCallback(async () => {
    // An inline grid's last edited row is finished (row_complete) before the
    // save; when one ran, save again on the next render so the rebuilt rows
    // and header values are what gets written.
    try {
      if (await flushRowCompletions()) {
        await afterRender()
        return saveFormRef.current()
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setFormBanner(message)
      notify.error(message)
      return
    }
    if (sessionHooks.current.holdSave?.()) return
    setBusy(true)
    setFieldErrors({})
    setFormBanner(null)
    // An open editor's compare-and-set: the stored values its edit started
    // from (the engine refuses with RECORD_CHANGED when they moved on).
    const sessionExpect = editTxnId ? ((await sessionHooks.current.expect?.()) || null) : null
    try {
      const res = await attemptWithReason(
        (reason, reasonCode) => attemptWithWarningConfirm(
          (ignore) => runtime.saveBatch(objName, {
            header: formValues,
            details: detailsForSave(detailsBuffer, topFormsRef.current),
            txn_id: editTxnId || undefined,
            apply_item_change: true,
            ...(sessionExpect ? { expect: sessionExpect } : {}),
            ...(reason ? { reason, reason_code: reasonCode } : {}),
            ...(ignore.length ? { ignore_warnings: ignore } : {}),
          }),
          confirmWarnings,
          undefined,
          // Warnings already OK'd on a detail line are not asked again.
          acknowledgedWarnings(detailsBuffer),
        ),
        promptReason,
      )
      // A submit form: the host's saveBatch ran the thing; nothing was saved,
      // so there is no record to promote — keep the values for another go.
      if (submit) {
        res.prompts?.forEach(p => notifyInfo(p.message))
        setFormDirty(false)
        submit.onDone(res)
        return
      }
      // Approval-controlled object: the save was STAGED for approval instead of
      // writing a live row. There's no txn_id/header to promote to edit mode —
      // toast the server message and return to the list.
      const staged = approvalEnvelopeOf(res)
      if (staged) {
        notify.success(staged.message || 'Submitted for approval. The changes will be saved once approved.')
        sessionHooks.current.beforeClose?.()
        setFormDirty(false)
        setEditorOpen(false)
        setEditTxnId(null)
        setFormValues({})
        setDetailsBuffer({})
        await refreshList()
        void refreshApprovalsCount()
        return
      }
      notify.success(editTxnId ? 'Record updated' : 'Record added')
      // Informational severity-'P' prompts — the save succeeded.
      res.prompts?.forEach(p => notifyInfo(p.message))
      // Promote to edit mode and merge the server-stamped header (auto_generate
      // PKs, audit timestamps, project-variable defaults) so the parent_pk that
      // child grids lazy-load by is complete.
      setEditTxnId(res.txn_id)
      if (res.header) {
        const saved = { ...res.header }
        delete (saved as Record<string, unknown>)._txn_id
        setFormValues(saved)
      }
      // Re-ask which additional attributes apply, from the saved record.
      setUdaNonce((n) => n + 1)
      // Reflect the committed tree (rows now carry their server _txn_id) without
      // a refetch, so the editor stays usable and unopened tabs stay lazy.
      setDetailsBuffer(markChildrenSaved(res.details || {}))
      setRowSelectRefill(n => n + 1)
      setFormDirty(false)
      sessionHooks.current.afterSave?.(res, !editTxnId)
      await refreshList()
    } catch (err) {
      if (!reviewLineErrors(err)) routeBatchError(err)
    } finally {
      setBusy(false)
    }
    // notify helpers follow runtime (already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, objName, formValues, detailsBuffer, editTxnId, refreshList, refreshApprovalsCount, routeBatchError, confirmWarnings, promptReason, reviewLineErrors, submit])

  const saveAndClose = useCallback(async () => {
    // An inline grid's last edited row is finished (row_complete) before the
    // save; when one ran, save again on the next render so the rebuilt rows
    // and header values are what gets written.
    try {
      if (await flushRowCompletions()) {
        await afterRender()
        return saveAndCloseRef.current()
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setFormBanner(message)
      notify.error(message)
      return
    }
    if (sessionHooks.current.holdSave?.()) return
    setBusy(true)
    setFieldErrors({})
    setFormBanner(null)
    // An open editor's compare-and-set: the stored values its edit started
    // from (the engine refuses with RECORD_CHANGED when they moved on).
    const sessionExpect = editTxnId ? ((await sessionHooks.current.expect?.()) || null) : null
    try {
      const res = await attemptWithReason(
        (reason, reasonCode) => attemptWithWarningConfirm(
          (ignore) => runtime.saveBatch(objName, {
            header: formValues,
            details: detailsForSave(detailsBuffer, topFormsRef.current),
            txn_id: editTxnId || undefined,
            apply_item_change: true,
            ...(sessionExpect ? { expect: sessionExpect } : {}),
            ...(reason ? { reason, reason_code: reasonCode } : {}),
            ...(ignore.length ? { ignore_warnings: ignore } : {}),
          }),
          confirmWarnings,
          undefined,
          // Warnings already OK'd on a detail line are not asked again.
          acknowledgedWarnings(detailsBuffer),
        ),
        promptReason,
      )
      // Staged for approval (see saveForm) — close the form and surface the
      // server message instead of treating it as a saved row.
      const staged = approvalEnvelopeOf(res)
      notify.success(staged
        ? (staged.message || 'Submitted for approval. The changes will be saved once approved.')
        : (editTxnId ? 'Record updated' : 'Record added'))
      if (!staged) res.prompts?.forEach(p => notifyInfo(p.message))
      sessionHooks.current.beforeClose?.()
      setFormDirty(false)
      setEditorOpen(false)
      setEditTxnId(null)
      setFormValues({})
      setDetailsBuffer({})
      await refreshList()
      if (staged) void refreshApprovalsCount()
    } catch (err) {
      if (!reviewLineErrors(err)) routeBatchError(err)
    } finally {
      setBusy(false)
    }
    // notify helpers follow runtime (already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, objName, formValues, detailsBuffer, editTxnId, refreshList, refreshApprovalsCount, routeBatchError, confirmWarnings, promptReason, reviewLineErrors])
  saveFormRef.current = saveForm
  saveAndCloseRef.current = saveAndClose

  // ── Open editors (Run shell) ──────────────────────────────────────────
  // The screen on display refreshes its list when one of its sessions saved.
  const mainRefreshToken = shellMain?.refreshToken || 0
  useEffect(() => {
    if (mainRefreshToken) void refreshList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainRefreshToken])

  // A kept-alive SESSION instance: opens its record from the session's
  // intent, reports dirty / title back, holds the record's edit lock while it
  // is on screen (released on minimise, re-taken on restore after re-reading
  // the record), and keeps a reload-recovery snapshot. Docs:
  // docs/plans/runtime-shell-layouts.md §4–5.
  const sessionCtx = shellSession
  const sessionId = sessionCtx?.session.id || ''
  const sessionVisible = !!sessionCtx?.visible
  const sessionCtxRef = useRef(sessionCtx)
  sessionCtxRef.current = sessionCtx
  const formValuesRef = useRef(formValues)
  formValuesRef.current = formValues
  const [sessionBlock, setSessionBlock] = useState<SessionBlock | null>(null)
  const sessionBlockRef = useRef(sessionBlock)
  sessionBlockRef.current = sessionBlock
  // "Checking the record…" is said only when the check is slow: a quick one
  // (the usual restore) leaves the window exactly as it was — no notice
  // popping in and out, Save not flicking off and on.
  const [checkingShown, setCheckingShown] = useState(false)
  useEffect(() => {
    if (sessionBlock?.kind !== 'checking') { setCheckingShown(false); return }
    const t = setTimeout(() => setCheckingShown(true), CHECKING_NOTICE_DELAY_MS)
    return () => clearTimeout(t)
  }, [sessionBlock])
  const [priorChanges, setPriorChanges] = useState<ChangedField[] | null>(null)
  const [sessionNote, setSessionNote] = useState<string | null>(null)
  /** The stored values the edit started from — the version source shared by
   *  the restore-time check and the save-time `expect`. */
  const baselineRef = useRef<Record<string, unknown> | null>(null)
  /** The re-read after a save that settles the baseline; the next save waits
   *  for it. */
  const baselineSettleRef = useRef<Promise<void> | null>(null)
  /** The record's OWN read-only state (an edit-lock rule, approval, rights). */
  const recordRoRef = useRef<{ viewOnly: boolean; lockReason: string }>({ viewOnly: false, lockReason: '' })

  // The columns the version source covers: stored main-table columns only —
  // the set the engine's compare-and-set accepts (`_main_table_columns`), minus
  // `secure` ones (they load masked).
  const baselineKeys = useMemo(() => {
    // The main table as the engine resolves it (`ddl.table_db_name`, else the
    // joins block's main entry); a model with neither and no joins stores
    // every column in the one table its columns name.
    const smx = sm as (SqlModel & { table_name?: string; joins?: { join_predicates?: { joins?: unknown } } }) | null
    const joins = smx?.joins?.join_predicates?.joins
    const joinList = (Array.isArray(joins) ? joins : joins ? [joins] : []) as Array<{ main_table?: unknown; table?: string }>
    const tables = new Set((smx?.columns || []).map((w) => String((w?.column as { table_name?: string } | undefined)?.table_name || '').trim().toLowerCase()).filter(Boolean))
    const main = String(smx?.main_table || smx?.table_name || '').trim().replace(/\s+/g, '_').toLowerCase()
      || String(joinList.find((j) => j?.main_table)?.table || '').trim().toLowerCase()
      || (!joinList.length && tables.size === 1 ? [...tables][0] : '')
    const out: string[] = []
    for (const w of sm?.columns || []) {
      const c = w?.column as (ColumnDef & {
        sql_expression?: string; uda?: unknown; secure?: unknown; table_name?: string
      }) | undefined
      if (!c) continue
      if (String(c.sql_expression || '').trim() || c.uda || c.secure) continue
      const src = String(c.table_name || '').trim().toLowerCase()
      if (src && src !== main) continue
      out.push(columnFormKey(c))
    }
    return out
  }, [sm])
  const baselineOf = useCallback((header: Record<string, unknown>) => {
    const lower = new Map(Object.entries(header || {}).map(([k, v]) => [k.toLowerCase(), v]))
    const out: Record<string, unknown> = {}
    for (const k of baselineKeys) if (lower.has(k.toLowerCase())) out[k] = lower.get(k.toLowerCase()) ?? null
    return out
  }, [baselineKeys])
  const labelOfKey = useCallback((key: string) => {
    const col = (sm?.columns || []).map((w) => w?.column).find((c) => !!c && columnFormKey(c) === key)
    return String(col?.heading || col?.name || key)
  }, [sm])
  /** What the user changed: baseline value → their value, field by field. */
  const changedFields = useCallback((base: Record<string, unknown> | null, values: Record<string, unknown>,
    details?: typeof detailsBuffer): ChangedField[] => {
    if (!base) return []
    const lower = new Map(Object.entries(values || {}).map(([k, v]) => [k.toLowerCase(), v]))
    const show = (v: unknown) => {
      const t = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
      return t.length > 120 ? `${t.slice(0, 117)}…` : t
    }
    const out: ChangedField[] = Object.keys(base)
      .filter((k) => expectText(base[k]) !== expectText(lower.get(k.toLowerCase())))
      .map((k) => ({ key: k, label: labelOfKey(k), from: show(base[k]), to: show(lower.get(k.toLowerCase())) }))
    if (details && bufferHasUnsaved(details)) {
      out.push({ key: '__lines', label: 'Lines', from: '', to: 'lines added, changed or removed' })
    }
    return out
  }, [labelOfKey])
  // Never snapshotted: secure / password columns.
  const secretKeys = useMemo(() => {
    const out: string[] = []
    for (const f of forms) {
      for (const w of f.sm.columns || []) {
        const c = w?.column as (ColumnDef & { secure?: unknown; input_type?: string }) | undefined
        if (!c) continue
        const t = `${c.col_type || ''} ${c.input_type || ''}`.toLowerCase()
        if (c.secure || t.includes('password')) out.push(columnFormKey(c))
      }
    }
    return out
  }, [forms])

  // ── the record's edit lock ──
  const lockTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const lockHeldRef = useRef<string | null>(null)
  const stopLockTimer = () => {
    if (lockTimerRef.current) { clearInterval(lockTimerRef.current); lockTimerRef.current = null }
  }
  const lockedOut = useCallback((res: { holder_name?: string; same_user?: boolean }) => {
    const message = res.same_user
      ? 'You are already editing this record in another window.'
      : `${res.holder_name || 'Someone else'} is editing this record.`
    setSessionBlock({ kind: 'locked', message })
    setViewOnly(true)
    setLockReason(message)
  }, [])
  const releaseLock = useCallback(() => {
    stopLockTimer()
    const txn = lockHeldRef.current
    if (!txn || !sessionId) return
    lockHeldRef.current = null
    void runtime.execute(objName, 'edit_lock', { op: 'release', txn_id: txn, session_id: sessionId })
      .catch((err: unknown) => console.warn('[editor] could not release the edit lock', err))
  }, [runtime, objName, sessionId])
  const releaseLockRef = useRef(releaseLock)
  releaseLockRef.current = releaseLock
  /** Take (or re-take) the lock. True = editable; false = held elsewhere. */
  const takeLock = useCallback(async (txnId: string): Promise<boolean> => {
    const ctx = sessionCtxRef.current
    if (!ctx || !sessionId) return true
    const ttl = ctx.lockTtlSeconds
    try {
      const res = await runtime.execute(objName, 'edit_lock', {
        op: 'acquire', txn_id: txnId, session_id: sessionId, ttl,
      }) as { held?: boolean; holder_name?: string; same_user?: boolean; ttl?: number } | null
      if (res && res.held === false) { lockedOut(res); return false }
      lockHeldRef.current = txnId
      stopLockTimer()
      const every = Math.max(10, Math.round((res?.ttl || ttl) / 3)) * 1000
      lockTimerRef.current = setInterval(() => {
        void runtime.execute(objName, 'edit_lock', { op: 'renew', txn_id: txnId, session_id: sessionId, ttl })
          .then((r) => {
            const rr = r as { held?: boolean; holder_name?: string; same_user?: boolean } | null
            // Lost the lease (expired while the network was away, and somebody
            // else took it): stop editing — Save would only collide.
            if (rr && rr.held === false) { stopLockTimer(); lockHeldRef.current = null; lockedOut(rr) }
          })
          .catch((err: unknown) => console.warn('[editor] could not renew the edit lock', err))
      }, every)
      return true
    } catch (err) {
      // Advisory: a server without edit locks, or a failed call, never stops
      // the edit — the save-time compare-and-set still guards the record.
      console.warn('[editor] edit lock unavailable — continuing without it', err)
      return true
    }
  }, [runtime, objName, sessionId, lockedOut])
  const takeLockRef = useRef(takeLock)
  takeLockRef.current = takeLock
  /** Editable again: the record's own read-only state, no session block. */
  const unblock = useCallback(() => {
    setSessionBlock(null)
    setViewOnly(recordRoRef.current.viewOnly)
    setLockReason(recordRoRef.current.lockReason)
  }, [])

  const hydrateSnapshot = useCallback((snap: EditorSnapshot, stale: boolean) => {
    setFormValues((prev) => ({ ...prev, ...snap.header }))
    setDetailsBuffer(snap.details || {})
    if (snap.activeFormNo) setActiveFormNo(snap.activeFormNo)
    setFormDirty(true)
    const notes: string[] = []
    if (stale) {
      notes.push('This draft was kept from an earlier visit and is older than usual — check the values before saving.')
    }
    if (snap.omitted?.length) {
      notes.push(`Files are not kept across a reload — re-attach: ${snap.omitted.map((k) => labelOfKey(k.split('.').pop() || k)).join(', ')}.`)
    }
    setSessionNote(notes.length ? notes.join(' ') : null)
  }, [labelOfKey])

  // Open the session's record once the screen can fetch.
  const sessionOpenedRef = useRef(false)
  const lastRestoreRef = useRef(sessionCtx?.session.restoreNonce ?? 0)
  useEffect(() => {
    const ctx = sessionCtxRef.current
    if (!ctx || !ready || rightsPending || sessionOpenedRef.current) return
    sessionOpenedRef.current = true
    lastRestoreRef.current = ctx.session.restoreNonce
    const sess = ctx.session
    const snap = sess.snapshot || null
    void (async () => {
      try {
        if (sess.mode === 'add' || !sess.txnId) {
          await startAdd(sess.intent?.seed)
          if (snap) hydrateSnapshot(snap, !!sess.stale)
          return
        }
        setSessionBlock({ kind: 'checking' })
        const res = await startEdit(sess.txnId)
        // The record could not be opened (deleted, or no longer this user's to
        // see — startEdit has said why): the session ends rather than linger
        // in Open editors with nothing behind it.
        if (!res) { setSessionBlock(null); sessionCtxRef.current?.closed('discarded'); return }
        recordRoRef.current = { viewOnly: res.viewOnly, lockReason: res.lockReason }
        const stored = baselineOf(res.header)
        if (snap) {
          // A draft kept across a reload: the record must not have moved on.
          if (snap.baseline && changedSince(snap.baseline, res.header).length) {
            baselineRef.current = stored
            setSessionBlock({ kind: 'changed', fields: changedFields(snap.baseline, snap.header), fromSnapshot: true })
            setViewOnly(true)
            return
          }
          baselineRef.current = snap.baseline || stored
          hydrateSnapshot(snap, !!sess.stale)
        } else {
          baselineRef.current = stored
        }
        if (res.viewOnly) { setSessionBlock(null); return }
        setViewOnly(true) // read-only until the lock answers
        if (await takeLockRef.current(sess.txnId)) unblock()
      } catch (err) {
        reportError('Could not open the record', err)
        setSessionBlock(null)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!sessionCtx, ready, rightsPending])

  /** Restore: re-read the record, then re-take its lock, before it can be
   *  saved. The form stays as it was while this runs (a save waits — see
   *  `holdSave`); it turns read-only only when the record moved on or
   *  someone else holds it. */
  const recheckRecord = useCallback(async () => {
    const txn = editTxnIdRef.current
    if (!sessionCtxRef.current || !txn || recordRoRef.current.viewOnly) return
    setSessionBlock({ kind: 'checking' })
    let stored: Record<string, unknown>
    try {
      const res = await runtime.load(objName, txn)
      stored = { ...(res.header || {}) }
    } catch (err) {
      reportError('Could not re-read the record', err)
      unblock()
      return
    }
    if (changedSince(baselineRef.current, stored).length) {
      setSessionBlock({
        kind: 'changed', fromSnapshot: false,
        fields: changedFields(baselineRef.current, formValuesRef.current, detailsBufferRef.current),
      })
      setViewOnly(true)
      return
    }
    if (await takeLockRef.current(txn)) unblock()
  }, [runtime, objName, reportError, unblock, changedFields])
  const restoreNonce = sessionCtx?.session.restoreNonce ?? 0
  useEffect(() => {
    if (!sessionCtxRef.current || !sessionOpenedRef.current) return
    if (restoreNonce === lastRestoreRef.current) return
    lastRestoreRef.current = restoreNonce
    if (editTxnIdRef.current) void recheckRecord()
  }, [restoreNonce, recheckRecord])

  /** "Reload record": drop the stale values, open the stored record afresh,
   *  and keep the list of what the user had changed on screen to re-enter. */
  const reloadRecord = useCallback(async () => {
    const txn = editTxnIdRef.current
    if (!txn) return
    const fields = sessionBlock?.kind === 'changed' ? sessionBlock.fields : []
    setSessionBlock({ kind: 'checking' })
    const res = await startEdit(txn)
    if (!res) { setSessionBlock(null); return }
    recordRoRef.current = { viewOnly: res.viewOnly, lockReason: res.lockReason }
    baselineRef.current = baselineOf(res.header)
    setPriorChanges(fields.length ? fields : null)
    setSessionNote(null)
    if (res.viewOnly) { setSessionBlock(null); return }
    setViewOnly(true)
    if (await takeLockRef.current(txn)) unblock()
  }, [sessionBlock, startEdit, baselineOf, unblock])

  // Minimise releases the lock at once (typed values stay in this form).
  useEffect(() => {
    if (sessionCtxRef.current && !sessionVisible) releaseLockRef.current()
  }, [sessionVisible])
  useEffect(() => () => releaseLockRef.current(), [])

  // Report dirty / title / mode back to the Open editors list.
  const sessionReport = sessionCtx?.report
  const sessionTitle = useMemo(() => {
    if (!sessionCtx) return ''
    const wanted = model?.vector_search?.display_column || model?.vector_search?.columns?.[0] || ''
    if (wanted) {
      const key = Object.keys(formValues).find((k) => k.toLowerCase() === wanted.toLowerCase())
      const v = key ? formValues[key] : undefined
      if (v != null && String(v).trim()) return String(v).trim()
    }
    for (const w of sm?.columns || []) {
      const c = w?.column
      if (!c || isKeyColumn(c)) continue
      const v = formValues[columnFormKey(c)]
      if (typeof v === 'string' && v.trim() && v.length <= 80) return v.trim()
    }
    return editTxnId ? String(editTxnId) : `New ${screenName}`
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!sessionCtx, formValues, sm, model, editTxnId, screenName])
  useEffect(() => {
    if (!sessionReport || !editorOpen) return
    sessionReport({
      dirty: formDirty || bufferHasUnsaved(detailsBuffer),
      title: sessionTitle,
      mode: editTxnId ? 'edit' : 'add',
      txnId: editTxnId || undefined,
    })
  }, [sessionReport, editorOpen, formDirty, detailsBuffer, sessionTitle, editTxnId])

  // Reload-recovery snapshot (debounced; flushed on minimise).
  const sessionSnapshot = sessionCtx?.snapshot
  const snapshotNow = useCallback(() => {
    if (!sessionSnapshot || !editorOpen) return
    try {
      sessionSnapshot(makeSnapshot({
        header: formValuesRef.current, details: detailsBufferRef.current,
        baseline: baselineRef.current, activeFormNo, secretKeys, now: Date.now(),
      }))
    } catch (err) {
      console.warn('[editor] could not keep a recovery copy of this record', err)
    }
  }, [sessionSnapshot, editorOpen, activeFormNo, secretKeys])
  useEffect(() => {
    if (!sessionSnapshot || !editorOpen) return undefined
    const t = setTimeout(snapshotNow, 400)
    return () => clearTimeout(t)
  }, [sessionSnapshot, editorOpen, formValues, detailsBuffer, snapshotNow])
  const minimiseSession = useMemo(() => (sessionCtx
    ? () => { snapshotNow(); sessionCtxRef.current?.minimise() }
    : undefined), [!!sessionCtx, snapshotNow]) // eslint-disable-line react-hooks/exhaustive-deps

  // The editor closed (saved and closed, or discarded): the session ends.
  const closeReasonRef = useRef<'saved' | 'discarded'>('discarded')
  const wasOpenRef = useRef(false)
  useEffect(() => {
    const ctx = sessionCtxRef.current
    if (!ctx) return
    if (editorOpen) { wasOpenRef.current = true; return }
    if (!wasOpenRef.current) return
    wasOpenRef.current = false
    releaseLockRef.current()
    ctx.closed(closeReasonRef.current)
  }, [editorOpen])
  // × from an Open editors list: this editor's own close (Save / Discard rule).
  const closeRequest = sessionCtx?.closeRequest || 0
  const lastCloseReqRef = useRef(closeRequest)
  useEffect(() => {
    if (!sessionCtxRef.current || closeRequest === lastCloseReqRef.current) return
    lastCloseReqRef.current = closeRequest
    if (editorOpen) closeEditor()
    else sessionCtxRef.current.closed('discarded')
  }, [closeRequest, editorOpen, closeEditor])

  sessionHooks.current = sessionCtx ? {
    holdSave: () => {
      if (sessionBlockRef.current?.kind !== 'checking') return false
      setCheckingShown(true) // say why nothing happened
      return true
    },
    expect: async () => {
      await baselineSettleRef.current
      return baselineRef.current && Object.keys(baselineRef.current).length ? baselineRef.current : null
    },
    afterSave: (res, wasAdd) => {
      if (res.header) baselineRef.current = baselineOf(res.header)
      // The save answers with the values it WROTE, not what the database
      // holds (an audit timestamp comes back with another precision / zone,
      // a DATE as a datetime, a post-commit hook may move a total): the
      // baseline is the stored record, re-read — as the restore check reads it.
      if (res.txn_id) {
        const txn = String(res.txn_id)
        const settle = runtime.load(objName, txn)
          .then((stored) => {
            if ((editTxnIdRef.current ?? txn) === txn && stored?.header) baselineRef.current = baselineOf(stored.header)
          })
          .catch((err: unknown) => console.warn('[editor] could not re-read the saved record', err))
          .finally(() => { if (baselineSettleRef.current === settle) baselineSettleRef.current = null })
        baselineSettleRef.current = settle
      }
      sessionCtxRef.current?.saved()
      setPriorChanges(null)
      setSessionNote(null)
      // An add that saved is now an edit of that record: take its lock.
      if (wasAdd && res.txn_id) {
        recordRoRef.current = { viewOnly: false, lockReason: '' }
        void takeLockRef.current(String(res.txn_id))
      }
    },
    beforeClose: () => {
      closeReasonRef.current = 'saved'
      sessionCtxRef.current?.saved()
    },
  } : {}

  // The notice the session editor shows above its form.
  const sessionNotice = sessionCtx ? (
    <SessionNotice
      block={sessionBlock?.kind === 'checking' && !checkingShown ? null : sessionBlock}
      prior={priorChanges}
      note={sessionNote}
      onRetry={() => { void recheckRecord() }}
      onReload={() => { void reloadRecord() }}
      onDismissPrior={() => setPriorChanges(null)}
      onDismissNote={() => setSessionNote(null)}
    />
  ) : null

  const deleteRow = useCallback(async (txnId: string) => {
    if (!window.confirm('Delete this record?')) return
    setBusy(true)
    try {
      await runtime.remove(objName, txnId)
      notify.success('Record deleted')
      if (editTxnId === txnId) {
        setEditTxnId(null)
        setFormValues({})
        setDetailsBuffer({})
        setFormDirty(false)
        setEditorOpen(false)
      }
      await refreshList()
    } catch (err) {
      reportError('Delete failed', err)
    } finally {
      setBusy(false)
    }
    // notify identity follows runtime (already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, objName, refreshList, editTxnId, reportError])

  // The inbox reports its pending count on every reload of its list: the
  // first one is the open, every later one follows a decision / staged edit
  // (or a return to its pending tab) — re-read the grid then, so an approved
  // record shows up while the inbox is still open.
  const inboxReportsRef = useRef(0)
  useEffect(() => { if (approvalsOpen) inboxReportsRef.current = 0 }, [approvalsOpen])
  const onInboxCountChange = useCallback((count: number | null) => {
    setApprovalsCount(count)
    inboxReportsRef.current += 1
    if (inboxReportsRef.current > 1) void refreshList()
  }, [refreshList])

  const onFieldChange = useCallback((name: string, v: unknown) => {
    const next = { ...formValues, [name]: v }
    setFormValues(next)
    setFormDirty(true)
    setHeaderEdit(prev => ({ key: name, value: v, n: (prev?.n || 0) + 1 }))
    // `name` is the column's form_key (see FormRenderer). Find the
    // matching column to fire item_change. Accept form_key, raw name,
    // and db_name as identifiers for resilience against legacy callers.
    const col = udaHeaderSm?.columns?.find(w => {
      const c = w.column
      if (!c) return false
      if (columnFormKey(c) === name) return true
      if (c.name === name) return true
      const dbn = (c.db_name || '').replace(/\s+/g, '_').toLowerCase()
      return !!dbn && dbn === name
    })?.column
    if (col && normalizeItemChange(col.item_change)) {
      triggerItemChange(columnFormKey(col), next)
    }
  }, [formValues, udaHeaderSm, triggerItemChange])

  // ── Custom action buttons ─────────────────────────────────────────────
  // Apply an action's item_change-style result (handler 'item_change') or the
  // rows the user picked from a 'select_grid' to the open form.
  const applyActionResult = useCallback((res: RunActionResponse) => {
    const headerNo = forms[0]?.formNo || '1'
    if (res.handler === 'select_grid') {
      const rows = res.rows || []
      if (rows.length === 0) return
      // Route the picked rows to the action's target line form (or the first
      // child form); with no child form, merge the first row into the header.
      const target = res.target_form_no ? String(res.target_form_no) : ''
      const childForm = (target && topForms.find(f => f.formNo === target && f.formNo !== headerNo))
        || topForms.find(f => f.formNo !== headerNo)
      if (childForm) {
        setDetailsBuffer(b => ({
          ...b,
          [childForm.formNo]: [
            ...(b[childForm.formNo] || []),
            ...rows.map(r => withLocalId(r)),
          ],
        }))
      } else {
        setFormValues(prev => ({ ...prev, ...rows[0] }))
        setFormDirty(true)
      }
      return
    }
    // item_change result: flat updates + header set, plus child-form rows.
    const headerSet = { ...(res.updates || {}), ...(res.forms?.[headerNo]?.set || {}) }
    if (Object.keys(headerSet).length) {
      setFormValues(prev => ({ ...prev, ...headerSet }))
      setFormDirty(true)
    }
    appendResultRows(res.forms, headerNo)
  }, [forms, topForms, appendResultRows])

  // ── handler='wizard' on a `fill` wizard — the wizard works on THIS buffer ──
  // (docs/plans/wizard-external-website-step.md §7). The seed is read when
  // the button is clicked; the result lands as unsaved edits — header values
  // the wizard already ran item_change for, rows appended to their forms.
  const fillState = useRef({ formValues, detailsBuffer })
  fillState.current = { formValues, detailsBuffer }
  const wizardFill = useMemo<WizardFillTarget>(() => ({
    seed: () => {
      const { formValues: hv, detailsBuffer: buf } = fillState.current
      const details: Record<string, Array<Record<string, unknown>>> = {}
      for (const f of topForms.slice(1)) {
        details[f.formNo] = ((buf[f.formNo] || []) as Array<Record<string, unknown>>)
          .filter(r => !r._deleted)
      }
      return { header: { ...hv }, details }
    },
    apply: (result) => {
      const headerNo = forms[0]?.formNo || '1'
      const header = result?.header || {}
      if (Object.keys(header).length) {
        setFormValues(prev => ({ ...prev, ...header }))
        setFormDirty(true)
      }
      let added = 0
      const lines = result?.lines || {}
      const known = new Set(topForms.slice(1).map(f => f.formNo))
      const skipped: string[] = []
      for (const [fno, rows] of Object.entries(lines)) {
        if (fno === headerNo || !rows?.length) continue
        if (!known.has(fno)) { skipped.push(fno); continue }
        added += rows.length
        setDetailsBuffer(b => ({ ...b, [fno]: [...(b[fno] || []), ...rows.map(r => withLocalId(r))] }))
      }
      if (added) setFormDirty(true)
      if (skipped.length) notifyWarn(`Lines for form ${skipped.join(', ')} were not added — that form is not on this screen.`)
      const n = Object.keys(header).length
      if (added || n) {
        notifyInfo([n ? `${n} value${n === 1 ? '' : 's'} filled` : '', added ? `${added} line${added === 1 ? '' : 's'} added` : '']
          .filter(Boolean).join(', ') + ' — review and save.')
      }
    },
  }), [forms, topForms, notifyInfo, notifyWarn])

  // ── handler='open' — this screen opened by ANOTHER screen's button ─────
  // Line forms with a Line Entry (and their commands), and the header columns
  // their `on_header_change` listens to — what an open sequence may drive.
  const openLineForms = useMemo(() => topForms.slice(1).flatMap((f) => {
    const le = lineEntryOf(f.sm)
    return le ? [{ formNo: f.formNo, commands: le.commands.map(c => c.name) }] : []
  }), [topForms])
  const openHeaderChangeColumns = useMemo(() => topForms.slice(1).flatMap((f) => {
    const col = lineEntryOf(f.sm)?.onHeaderChange?.column
    return col ? [String(col).trim().replace(/\s+/g, '_').toLowerCase()] : []
  }), [topForms])
  const showOpenMatches = useCallback((filter: OpenListFilter | null) => {
    const had = !!openFilterRef.current
    openFilterRef.current = filter
    setOpenFilter(filter)
    if (filter || had) void refreshList()
  }, [refreshList])
  const openSequence = useOpenSequence({
    objName,
    sm,
    headerNo: forms[0]?.formNo || '1',
    objTitle: screenName,
    runtime,
    notify,
    lineForms: openLineForms,
    headerChangeColumns: openHeaderChangeColumns,
    startAdd: () => startAdd(),
    startEdit: (txnId, opts) => startEdit(txnId, opts),
    setFormValues,
    setFormDirty,
    setDynamicProtect,
    setFormBanner,
    setHeaderEdit,
    itemChangeNow: triggerItemChange.now,
    applyActionResult,
    showMatches: showOpenMatches,
    currentTxnId: () => editTxnIdRef.current,
    canAdd: () => can('add') && sm?.list_view?.allow_add !== false,
    canEdit: () => canEditRecords,
    confirmWarnings,
    confirmAction,
  })
  // Run the payload once per hand-off, as soon as the screen can fetch.
  const appliedOpenRef = useRef<string>('')
  const viewRef = useRef(view)
  viewRef.current = view
  const editorLayoutRef = useRef(editorLayout)
  editorLayoutRef.current = editorLayout
  const runOpenRef = useRef(openSequence.runOpen)
  runOpenRef.current = openSequence.runOpen
  useEffect(() => {
    const payload = initialScreen?.open
    // Wait for the rights too: the sequence asks them which half it may do.
    if (!ready || rightsPending || !payload) return
    const key = initialScreen?.openKey || JSON.stringify(payload)
    if (appliedOpenRef.current === key) return
    appliedOpenRef.current = key
    // The values land in the record editor. The Spreadsheet and Bulk entry
    // views each keep a record of their own, and the Document editor saves
    // through its own path, so this visit uses the list and the standard
    // editor (the person's remembered choices are kept).
    if (viewRef.current === 'tabular' || viewRef.current === 'form-input') {
      setView('list')
    }
    if (editorLayoutRef.current === 'document') setEditorLayout('standard')
    void runOpenRef.current(payload)
  }, [ready, rightsPending, initialScreen?.open, initialScreen?.openKey])

  // Headings for a select_grid's columns: every form's column headings, the
  // action's target form winning a clash (its rows are what is shown).
  const gridLabels = useCallback((targetFormNo?: string) => {
    const out: Record<string, string> = {}
    const ordered = [
      ...forms.filter(f => f.formNo !== targetFormNo),
      ...forms.filter(f => f.formNo === targetFormNo),
    ]
    for (const f of ordered) {
      for (const w of f.sm.columns || []) {
        const c = w?.column
        if (!c) continue
        const heading = String(c.heading || c.name || '')
        out[columnFormKey(c)] = heading
        if (c.name) out[String(c.name)] = heading
      }
    }
    return out
  }, [forms])

  // Perform a built-in CRUD op for a handler='system' action button.
  const handleSystemAction = useCallback((op: 'add' | 'edit' | 'delete' | 'save' | 'view') => {
    if (op === 'save') void saveForm()
    else if (op === 'add') void startAdd()
    else if (op === 'delete' && editTxnId) void deleteRow(editTxnId)
    // 'edit' is a no-op while already in the editor.
  }, [saveForm, startAdd, deleteRow, editTxnId])

  // An imported Vision Add / Edit / Audit Trail clicked on the open record
  // (shown there per its DISPLAY_MODE): Add starts a new record, Edit reopens
  // this one in edit mode (a locked record still opens read-only), Audit Trail
  // shows its history. Unsaved changes are confirmed away first.
  const performVisionBuiltin = useCallback((op: 'add' | 'edit' | 'audit_trail') => {
    if (op === 'audit_trail') {
      if (editTxnId) setHistoryTxn(editTxnId)
      return
    }
    if (!guardDirtyClose()) return
    if (op === 'add') {
      if (can('add') && sm?.list_view?.allow_add !== false) void startAdd()
      else notify.error('You do not have permission to add records')
    } else if (editTxnId) {
      void startEdit(editTxnId)
    }
    // notify follows runtime (stable per screen).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTxnId, guardDirtyClose, startAdd, startEdit, sm, can])

  // Surface a run_action validation / business-logic failure in the Error
  // popup (OK only — the action stopped), like every other action button.
  const handleActionError = useCallback((err: unknown) => {
    const parsed = parseApiError(err)
    if (parsed.validationErrors.length > 0) {
      showActionError(actionErrorMessages(parsed))
    } else if (parsed.status === 403) {
      // A rights refusal (see runListAction) — a plain answer.
      showActionError(parsed.message || 'You do not have rights to perform this action.')
    } else {
      reportError('Action failed', err, { toast: false })
    }
    // notify identity follows runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportError, runtime, showActionError])

  // ── List-level custom action buttons ──────────────────────────────────
  // Run a custom action from the list (toolbar form-scoped button, or a row's
  // 3-dot menu). The engine runs the action's pre-action validations + logic;
  // we then dispatch by handler. ``item_change`` / ``select_grid`` results need
  // an open record to apply to, so we open the relevant editor first (Add for a
  // form-scoped action, Edit for the targeted row) and apply the result there.
  const runListAction = useCallback(async (
    name: string,
    target: { mode: 'add' } | { mode: 'edit'; txnId: string },
    rowValues?: Record<string, unknown>,
  ) => {
    const headerNo = forms[0]?.formNo || '1'
    // A button that asks for a value first (a scan / typed code): cancelling
    // the prompt runs nothing.
    const clickedDef = [...rowActions, ...formActions].find(a => a.name === name)
    const capture = actionCaptureOf(clickedDef)
    let posted = rowValues || {}
    if (capture) {
      const captured = await captureFor(clickedDef)
      if (!captured) return
      posted = withCapturedValue(posted, capture, captured)
    }
    setBusy(true)
    try {
      const res = await attemptActionWithConfirm(
        (confirm) => attemptWithWarningConfirm(
          (ignore) => runtime.execute(objName, 'run_action', {
            form_no: headerNo,
            action_name: name,
            header: posted,
            header_values: {},
            ...(target.mode === 'edit' ? { txn_id: target.txnId } : {}),
            ...(ignore.length ? { ignore_warnings: ignore } : {}),
            ...(confirm ? { confirm } : {}),
          }),
          confirmWarnings,
        ) as Promise<RunActionResponse>,
        confirmAction,
      )
      // Informational severity-'P' prompts — shown for every handler.
      res.prompts?.forEach(p => notifyInfo(p.message))
      switch (res.handler) {
        case 'system':
          if (res.system_op === 'add') await startAdd()
          // A 'view' system action opens the record read-only after ITS
          // conditions pass — the edit action's conditions never run here.
          else if (res.system_op === 'view' && target.mode === 'edit') await startEdit(target.txnId, { forceView: true })
          else if (res.system_op === 'edit' && target.mode === 'edit') await startEdit(target.txnId)
          else if (res.system_op === 'delete' && target.mode === 'edit') await deleteRow(target.txnId)
          else if (res.system_op === 'save') await saveForm()
          break
        case 'message':
          // A bare "Done" only when nothing else was said — a Vision bean
          // reports its outcome as a prompt ("Confirm successful."), already
          // shown above.
          if (res.message || !res.prompts?.length) notify.success(res.message || 'Done')
          // The action may have mutated data (e.g. Confirm → status/inventory);
          // reflect it in the list without a manual refresh.
          await refreshList()
          break
        case 'html_modal':
          setActionHtml({ title: name, html: res.html || '' })
          await refreshList()
          break
        case 'report': {
          // Open the bound Jasper report with params resolved from the row.
          // The engine echoes the binding; fall back to the model's action
          // def so an older engine response still works.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const rep = (res.report?.obj_name ? res.report : def?.report) || undefined
          const reportObj = (rep?.obj_name || '').trim()
          if (!reportObj) {
            notify.error(`Action "${name}" has no report bound to it.`)
            break
          }
          const make = runtime.txn.makeReportAdapter
          if (!make) {
            notify.error('Report viewing is not available on this surface.')
            break
          }
          setActionReport({
            title: name,
            objName: reportObj,
            adapter: make(reportObj),
            params: resolveReportActionParams(rep?.param_map, rowValues),
          })
          break
        }
        case 'assistant': {
          // Call the in-app Assistant with values from the clicked row (a list
          // row is the header record, so {form.X} and {header.X} both resolve
          // to it). Engine echoes the binding; fall back to the model's def.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const binding = (res.assistant?.mode || res.assistant?.intent_id || res.assistant?.prompt)
            ? res.assistant : def?.assistant
          const open = runtime.txn.openAssistantAction
          if (!open) {
            notify.error('The Assistant is not available on this surface.')
            break
          }
          const req = buildAssistantActionRequest(name, binding, rowValues, rowValues)
          if (typeof req === 'string') {
            notify.error(req)
            break
          }
          open(req)
          break
        }
        case 'payment': {
          // Take payment for the clicked row. `target.txnId` is the saved
          // record; an 'add' target has none, and buildPaymentActionRequest
          // refuses that rather than charging for nothing.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const binding = (res.payment?.amount_column
            ? res.payment : def?.payment) || undefined
          const open = runtime.txn.openPaymentAction
          if (!open) {
            notify.error('Payments are not available on this surface.')
            break
          }
          const req = buildPaymentActionRequest(
            name, binding, objName, target.mode === 'edit' ? target.txnId : undefined)
          if (typeof req === 'string') {
            notify.error(req)
            break
          }
          open(req)
          break
        }
        case 'import': {
          // Import a file into THIS object in the host's import window; the
          // list reloads once anything was saved there.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const open = runtime.txn.openImportAction
          if (!open) {
            notify.error('Importing files is not available on this surface.')
            break
          }
          open(buildImportActionRequest(name, res.import || def?.import, objName, (outcome) => {
            if (outcome && outcome.saved > 0) {
              refreshList().catch(err => console.warn('[TxnScreen] list refresh after import failed', err))
            }
          }))
          break
        }
        case 'open': {
          // Open ANOTHER object with values from the clicked row. A report
          // opens in this screen's report window; everything else goes to the
          // host, which owns navigation and the drawer. The engine echoes the
          // normalised binding; fall back to the model's def for an older one.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const binding = (res.open?.obj_name ? res.open : def?.open) || undefined
          const req = buildOpenActionRequest(name, binding, objName, rowValues, rowValues,
            () => { refreshList().catch(err => console.warn('[TxnScreen] list refresh after open failed', err)) })
          if (typeof req === 'string') {
            notify.error(req)
            break
          }
          if (req.objType === 'R') {
            const make = runtime.txn.makeReportAdapter
            if (!make) {
              notify.error('Report viewing is not available on this surface.')
              break
            }
            setActionReport({ title: req.title, objName: req.objName, adapter: make(req.objName), params: req.params })
            break
          }
          const open = runtime.txn.openObjectAction
          if (!open) {
            notify.error('Opening another screen is not available on this surface.')
            break
          }
          open(req)
          break
        }
        case 'wizard': {
          // Start a Transaction Wizard with inputs from the clicked row (a
          // list row is the header record, so {form.X} and {header.X} both
          // resolve to it). A drawer refreshes the list when it closes.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const binding = (res.wizard?.obj_name ? res.wizard : def?.wizard) || undefined
          const open = runtime.txn.openWizardAction
          if (!open) {
            notify.error('Wizards are not available on this surface.')
            break
          }
          const req = buildWizardActionRequest(name, binding, rowValues, rowValues,
            () => { refreshList().catch(err => console.warn('[TxnScreen] list refresh after wizard failed', err)) })
          if (typeof req === 'string') {
            notify.error(req)
            break
          }
          open(req)
          break
        }
        case 'send': {
          // Open the send window (preview with the default template) over the
          // clicked record. Nothing was sent by the action call — the window's
          // Send is its own request, re-authorized by the host.
          const def = [...rowActions, ...formActions].find(a => a.name === name)
          const open = runtime.txn.openSendAction
          if (!open) {
            notify.error('Sending messages is not available on this surface.')
            break
          }
          open(buildSendActionRequest(name, res.send || def?.send, objName,
            target.mode === 'edit' ? [target.txnId] : [],
            (runKey) => {
              if (runKey) refreshList().catch(err => console.warn('[TxnScreen] list refresh after send failed', err))
            }))
          break
        }
        case 'select_grid':
          setActionGrid({ title: name, res, target })
          break
        case 'item_change':
        default:
          if (target.mode === 'edit') await startEdit(target.txnId)
          else await startAdd()
          applyActionResult(res)
          if (res.message) notify.success(res.message)
          if (res.warnings?.length) res.warnings.forEach(w => notifyWarn(w.message))
          else if (res.warning) notify.error(res.warning)
          break
      }
    } catch (err) {
      if (isActionConfirmCancelled(err)) return  // user backed out of the gate
      // The action ran from the list (no editor open), so a banner/field error
      // would be invisible — surface the business-logic / validation failure as
      // a toast instead of silently swallowing it. Unexpected (non-validation)
      // errors additionally open the full details modal.
      const parsed = parseApiError(err)
      if (parsed.validationErrors.length > 0) {
        const msg = parsed.validationErrors[0].message || parsed.message || 'Action failed'
        showActionError(actionErrorMessages(parsed))
        // The EDIT gate refused this record. Its conditions say the record may
        // not be CHANGED — never that it may not be LOOKED AT — so leave the
        // user a way in: mark the row view-only with the refusal as its reason
        // and the pencil becomes the eye / "View" control, which opens the
        // record read-only through the VIEW action's own conditions.
        // The engine pre-evaluates expression gates straight onto the row, so
        // this is the fallback for the rules it cannot afford to run per row
        // (business logic, REST, master lookups) — and for a row the list
        // fetched before the record changed underneath it.
        if (target.mode === 'edit' && name && name === systemOverrides?.edit?.name) {
          setRows(prev => prev.map(r => (
            r._txn_id === target.txnId
              ? { ...r, _editable: false, _lock_reason: msg }
              : r
          )))
        }
      } else if (parsed.status === 403) {
        // A rights refusal (the button, or — for an 'open' button — the
        // object it opens) is a plain answer, not a failure to diagnose.
        showActionError(parsed.message || 'You do not have rights to perform this action.')
      } else {
        reportError('Action failed', err)
      }
    } finally {
      setBusy(false)
    }
    // notify helpers follow runtime (already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, objName, forms, startAdd, startEdit, deleteRow, saveForm, applyActionResult, reportError, refreshList, confirmWarnings, confirmAction, captureFor, rowActions, formActions, systemOverrides, showActionError])

  const runFormAction = useCallback(
    (name: string) => runListAction(name, { mode: 'add' }),
    [runListAction])
  const runRowAction = useCallback((name: string, txnId: string, header?: Record<string, unknown>) => {
    // An imported Vision button (OBJ_ACTIONS): Add / Edit / Audit Trail are the
    // screen's own operations; one bound to a Vision bean runs like any other
    // action; any other one has no Twasta implementation yet, so say so.
    const actionDef = rowActions.find(a => a.name === name)
    const vision = visionActionOf(actionDef)
    if (vision && !(visionRunnable(actionDef) && !vision.builtin)) {
      if (vision.builtin === 'add') void startAdd()
      else if (vision.builtin === 'edit') void startEdit(txnId)
      else if (vision.builtin === 'audit_trail') setHistoryTxn(txnId)
      else notifyInfo(visionNotWiredMessage(name, vision))
      return Promise.resolve()
    }
    // `header` is supplied by the multi-tabular grid (whose rows aren't in the
    // list's rowsRef); the list view omits it and we look the row up here.
    const row = header ?? rowsRef.current.find(r => r._txn_id === txnId)
    return runListAction(name, { mode: 'edit', txnId }, row as Record<string, unknown> | undefined)
    // notifyInfo follows runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runListAction, rowActions, startAdd, startEdit])
  // The list's built-in Delete. When the designer defined a system Delete
  // action it merges in here (see partitionActionsByScope): the delete runs
  // THROUGH that action so its pre-action validations / confirmation gate it,
  // and it is not offered a second time as a row action.
  const deleteOverrideName = systemOverrides.delete?.name
  const deleteFromList = useCallback((txnId: string) => (
    deleteOverrideName ? runRowAction(deleteOverrideName, txnId) : deleteRow(txnId)
  ), [deleteOverrideName, runRowAction, deleteRow])

  // ── Bulk actions (multi-row select on the list) ─────────────────────────
  // The bulk-eligible subset of the row actions is offered in ListView's
  // selection bar. The confirmation gate (if any) clears once for the whole
  // batch; results show in a BulkResultModal (managed by useBulkAction).
  const bulkActions = useMemo(
    () => bulkEligibleActions(bulkActionCandidates(rowActions, systemOverrides)),
    [rowActions, systemOverrides])
  const bulkNotify = useMemo<TxnNotifier>(() => ({
    success: (m: string) => notify.success(m),
    error: (m: string) => notify.error(m),
    info: (m: string) => notifyInfo(m),
    // notify helpers follow runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [runtime])
  const { runBulk, busy: bulkBusy, bulkModals } = useBulkAction({
    notify: bulkNotify, onDone: refreshList,
  })
  const runBulkAction = useCallback((name: string, txnIds: string[]) => {
    const headerNo = forms[0]?.formNo || '1'
    return runBulk(
      name,
      // A bulk Delete on an object that demands a reason for change asks for
      // ONE reason covering the batch (the engine refuses without it).
      ({ confirm }) => attemptWithReason(
        (reason, reasonCode) => runtime.execute(objName, 'run_action_bulk', {
          form_no: headerNo,
          action_name: name,
          txn_ids: txnIds,
          ...(confirm ? { confirm } : {}),
          ...(reason ? { reason, reason_code: reasonCode } : {}),
        }) as Promise<RunActionResponse>,
        promptReason,
      ),
      undefined,
      // A 'send' button answers ONE send window over the rows that passed its
      // checks; the rows it left out are reported here, not in a results modal.
      (res) => {
        if (res.handler !== 'send') return false
        const open = runtime.txn.openSendAction
        if (!open) {
          notify.error('Sending messages is not available on this surface.')
          return true
        }
        const left = (res.results || []).filter(r => r.status !== 'success')
        if (left.length) {
          notify.error(`${left.length} selected record${left.length === 1 ? '' : 's'} cannot be sent: `
            + (left[0].message || left[0].status || 'not eligible'))
        }
        const ids = res.txn_ids || []
        if (!ids.length) return true
        const def = rowActions.find(a => a.name === name)
        const req = buildSendActionRequest(name, res.send || def?.send, objName, ids,
          (runKey) => {
            if (runKey) refreshList().catch(err => console.warn('[TxnScreen] list refresh after send failed', err))
          })
        req.excluded = left.map(r => ({ txn_id: r.txn_id, status: r.status, message: r.message }))
        open(req)
        return true
      },
    )
    // notify follows runtime (host memoizes it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runBulk, forms, runtime, objName, promptReason, rowActions, refreshList])
  // Select-all-across-pages: resolve every matching record's txn_id under the
  // current enforced filters (the same context the list fetch uses).
  const selectAllMatching = useCallback(async (): Promise<string[]> => {
    const res = await runtime.execute(objName, 'list', {
      ids_only: true,
      ...(activeTabRef.current ? { tab: activeTabRef.current } : {}),
      enforced_overrides: overridesRef.current,
      ...(bypassRef.current
        ? { bypass_enforced: true }
        : (filterSetIdRef.current ? { enforced_filter_set_id: filterSetIdRef.current } : {})),
    }) as unknown as { ids?: string[]; total?: number; truncated?: boolean }
    const ids = res.ids || []
    if (res.truncated) {
      notifyWarn(`Selected the first ${ids.length} of ${res.total} matching records`)
    }
    return ids
    // notify helpers follow runtime (already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtime, objName])

  // Apply the rows the user ticked in a list-level select_grid action: open the
  // target editor (Add / the row's Edit) and merge the picked rows in.
  const applyActionGridSelection = useCallback(async (
    sel: { title: string; res: RunActionResponse; target: { mode: 'add' } | { mode: 'edit'; txnId: string } },
    selected: Array<Record<string, unknown>>,
  ) => {
    if (sel.target.mode === 'edit') await startEdit(sel.target.txnId)
    else await startAdd()
    applyActionResult({ ...sel.res, handler: 'select_grid', rows: selected })
  }, [startAdd, startEdit, applyActionResult])

  // When the user switches AWAY from the header tab, fire the header form's
  // form-level validations first and block the switch if any fail (standard
  // form-exit validation). Child tabs are grids with no single payload, so
  // only the header gates here. A record open in view mode cannot change, so
  // it is never validated (no form rules, no GENMST round-trip).
  const handleTopTabChange = useCallback(async (nextFormNo: string) => {
    const headerNo = forms[0]?.formNo
    if (headerNo && activeFormNo === headerNo && nextFormNo !== headerNo
        && !viewOnly && formNeedsValidation(sm)) {
      try {
        // editTxnId marks this as an edit of a saved record, so record-aware
        // rules (must_be_unique) exclude the row itself. Null only while adding.
        // A header rule that only WARNS (a 'W' business-logic / GENMST entry,
        // e.g. VTPRD2 "Period is not defined") must not block the move to the
        // detail tab: ask OK / Cancel, and OK retries with the warning
        // acknowledged. Only a real error blocks.
        await attemptWithWarningConfirm(
          (ignoreWarnings) => executeValidate(runtime, objName, headerNo, formValues,
                                              { txnId: editTxnId || undefined, ignoreWarnings }),
          confirmWarnings,
        )
        setFieldErrors({})
        setFormBanner(null)
      } catch (err) {
        // Cancel on the warnings prompt: stay on the header, no error popup,
        // and put the cursor on the field the warning is about.
        const cancelled = extractBlockingWarnings(err)
        if (cancelled) {
          const field = cancelled.find(w => w.field && w.field !== '_record')?.field
          if (field) {
            setFieldErrors({ [field]: cancelled.find(w => w.field === field)?.message || '' })
            window.setTimeout(() => focusFieldAnchor(field), 50)
          }
          return
        }
        const parsed = parseApiError(err)
        if (visionDialogs && parsed.validationErrors.length > 0) {
          showVisionValidationError(parsed.validationErrors)
        } else {
          applyValidationErrors(parsed.validationErrors, parsed.message, setFieldErrors, setFormBanner, notify)
        }
        if (parsed.validationErrors.length === 0) {
          reportError('Validation failed', err, { toast: false })
        }
        return
      }
    }
    setActiveFormNo(nextFormNo)
    // notify identity follows runtime (already a dep).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFormNo, forms, runtime, objName, formValues, sm, editTxnId, reportError, viewOnly,
      visionDialogs, showVisionValidationError, confirmWarnings])

  // Lazy-load an EDIT record's existing rows for one child form into the buffer
  // when its tab first opens. No-op while adding (nothing persisted yet) or once
  // that form is already in the buffer; concurrent opens are de-duped.
  const ensureChildRowsLoaded = useCallback(async (
    childFormNo: string, childParentPk: Record<string, unknown>,
  ) => {
    if (editTxnIdRef.current === null) return
    if (!childParentPk || Object.keys(childParentPk).length === 0) return
    if (Object.prototype.hasOwnProperty.call(detailsBufferRef.current, childFormNo)) return
    if (loadingFormsRef.current.has(childFormNo)) return
    loadingFormsRef.current.add(childFormNo)
    try {
      const rows = await listAllChildRows(
        (q) => runtime.list(objName, q), childFormNo, childParentPk,
      )
      setDetailsBuffer(b => (
        Object.prototype.hasOwnProperty.call(b, childFormNo)
          ? b
          : { ...b, [childFormNo]: rows }
      ))
    } catch (err) {
      if (typeof console !== 'undefined') {
        console.debug('lazy-load child rows failed (best-effort):', err)
      }
    } finally {
      loadingFormsRef.current.delete(childFormNo)
    }
  }, [runtime, objName])

  // `row_select` forms with `refill_on_open`: once a saved record's rows are
  // in (on open, and after each save), re-run that header column's
  // item_change and add the candidates not already on the form, UNTICKED, so
  // the saved rows show ticked among everything the user could have picked.
  // Only the form's rows are taken — the header is not touched.
  const rowSelectDoneRef = useRef('')
  const rowSelectBusyRef = useRef('')
  // A closed editor forgets: the next open of the same record refills again.
  useEffect(() => {
    if (!editorOpen) { rowSelectDoneRef.current = ''; rowSelectBusyRef.current = '' }
  }, [editorOpen])
  useEffect(() => {
    if (!editorOpen || !editTxnId) return
    const targets = topForms.slice(1)
      .map(f => ({ formNo: f.formNo, rs: rowSelectOf(f.sm) }))
      .filter(t => t.rs?.refillOnOpen)
    if (targets.length === 0) return
    const sig = `${editTxnId}|${rowSelectRefill}`
    if (rowSelectDoneRef.current === sig || rowSelectBusyRef.current === sig) return
    rowSelectBusyRef.current = sig
    void (async () => {
      let done = true
      for (const { formNo, rs } of targets) {
        if (!rs?.refillOnOpen) continue
        await ensureChildRowsLoaded(formNo, parentPk)
        await afterRender()
        // Saved rows not loaded yet (the key is still being filled in): try
        // again when the effect next runs, never merge ahead of them.
        if (!Object.prototype.hasOwnProperty.call(detailsBufferRef.current, formNo)) { done = false; continue }
        try {
          const res = await runtime.execute(objName, 'item_change', {
            field_name: rs.refillOnOpen, header: formValuesRef.current, header_values: {},
          }) as ItemChangeResult
          const change = res?.forms?.[formNo]
          if (rowSelectBusyRef.current !== sig || !change?.rows?.length) continue
          setDetailsBuffer(b => ({
            ...b, [formNo]: applySelectableRowsChange(b[formNo], { rows: change.rows }, rs, withLocalId),
          }))
        } catch {
          // Best-effort: the saved (ticked) rows still show.
        }
      }
      if (rowSelectBusyRef.current === sig) {
        rowSelectBusyRef.current = ''
        if (done) rowSelectDoneRef.current = sig
      }
    })()
    // parentPk is covered by parentPkKey (its stable serialised form).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorOpen, editTxnId, rowSelectRefill, parentPkKey, topForms, ensureChildRowsLoaded, runtime, objName])

  // A ChildStore over the top-level details buffer for one direct child form.
  const makeTopStore = (childFormNo: string): ChildStore => ({
    rows: detailsBuffer[childFormNo] || [],
    add: (row) => setDetailsBuffer(b => ({
      ...b, [childFormNo]: [...(b[childFormNo] || []), withLocalId(row)],
    })),
    edit: (index, patch) => setDetailsBuffer(b => {
      const rows = (b[childFormNo] || []).slice()
      const target = rows[index]
      if (!target) return b
      const { _error, _fieldErrors, ...rest } = target as Record<string, unknown>
      void _error; void _fieldErrors
      rows[index] = { ...rest, ...patch }
      return { ...b, [childFormNo]: rows }
    }),
    remove: (index) => setDetailsBuffer(b => {
      const rows = b[childFormNo] || []
      const target = rows[index]
      if (!target) return b
      const nextRows = target._txn_id
        ? rows.map((r, i) => (i === index ? { ...r, _deleted: true } : r))
        : rows.filter((_, i) => i !== index)
      return { ...b, [childFormNo]: nextRows }
    }),
    ensureLoaded: (childParentPk) => ensureChildRowsLoaded(childFormNo, childParentPk),
  })

  // Line Entry `on_header_change`: when the user picks a line form's source
  // document in the header, that form offers (or runs) its line commands
  // right here, without the user switching to the lines first.
  const lineEntryBanners = viewOnly ? null : topForms.slice(1)
    .filter(entry => lineEntryOf(entry.sm)?.onHeaderChange)
    .map(entry => {
      const store = makeTopStore(entry.formNo)
      return (
        <LineEntryBar
          key={`le-${entry.formNo}`}
          mode="banner"
          sm={entry.sm}
          formNo={entry.formNo}
          execute={(action, payload) => runtime.execute(objName, action, payload)}
          engineKey={project}
          headerValues={formValues}
          parentPk={parentPk}
          txnId={editTxnId || undefined}
          rows={store.rows}
          onAppend={(rows) => rows.forEach(r => store.add(r))}
          onRemove={(idx) => [...idx].sort((a, b) => b - a).forEach(i => store.remove(i))}
          notify={notify}
          headerEdit={headerEdit}
        />
      )
    })
  // The line command an 'open' button's follow-up step asked for, run with no
  // surface of its own (only the dialogs the command needs).
  const openLineBar = (() => {
    const req = openSequence.lineRequest
    if (!req || viewOnly || !editorOpen) return null
    const entry = topForms.find(f => f.formNo === req.formNo)
    if (!entry) return null
    const store = makeTopStore(entry.formNo)
    return (
      <LineEntryBar
        key={`le-open-${entry.formNo}`}
        mode="headless"
        sm={entry.sm}
        formNo={entry.formNo}
        execute={(action, payload) => runtime.execute(objName, action, payload)}
        engineKey={project}
        headerValues={formValues}
        parentPk={parentPk}
        txnId={editTxnId || undefined}
        rows={store.rows}
        onAppend={(rows) => rows.forEach(r => store.add(r))}
        onRemove={(idx) => [...idx].sort((a, b) => b - a).forEach(i => store.remove(i))}
        notify={notify}
        request={req}
        onRequestDone={openSequence.onLineRequestDone}
      />
    )
  })()

  // ── "Simple" layout wiring ────────────────────────────────────────────
  // Header FormRenderer + ONE editable ChildTabularGrid per direct child form,
  // all mounted at once (no tabs), inside the same EditorModal chrome. The
  // grids read and write the SAME detailsBuffer the tabbed editor uses, so the
  // save path (one atomic runtime.saveBatch) is untouched.

  // ChildTabularGrid speaks the embed executor shape (dataAccess + project
  // first); the screen runtime is already project-bound, so both are dropped.
  const gridExecute = useCallback<ItemChangeExecutor>(
    (_da, _project, oName, action, payload) => runtime.execute(oName, action, payload),
    [runtime],
  )

  // Functional mutate over ONE child form's rows — the grid's equivalent of
  // makeTopStore, but index-free so a synchronous Excel-paste loop (many
  // writes in one tick) merges instead of clobbering. `coalesceKey` is
  // ignored: it groups undo history, which this screen doesn't keep.
  const mutateChildRows = useCallback((childFormNo: string) => (
    updater: (prev: ChildRow[]) => ChildRow[],
  ) => {
    setDetailsBuffer(b => ({
      ...b,
      [childFormNo]: updater((b[childFormNo] || []) as ChildRow[]),
    }))
  }, [])

  // Fetch a grandchild form's saved rows — powers the grid's row expander.
  const loadGridChildRows = useCallback(async (
    childFormNo: string, childParentPk: Record<string, unknown>,
  ): Promise<Array<Record<string, unknown>>> => {
    return listAllChildRows((q) => runtime.list(objName, q), childFormNo, childParentPk)
  }, [runtime, objName])

  // Seed a grandchild form's rows under one SAVED child row without dirtying
  // it — the single-record twin of useEmbedRecords.seedRowChildren. The
  // recordId argument is ignored: this buffer holds exactly one record.
  const seedGridRowChildren = useCallback((
    _recordId: string, childFormNo: string, childTxnId: string,
    gcFormNo: string, gcRows: Array<Record<string, unknown>>,
  ) => {
    setDetailsBuffer(b => {
      const rows = b[childFormNo] || []
      let changed = false
      const next = rows.map(r => {
        if (String(r._txn_id ?? '') !== childTxnId) return r
        const existing = r.__children as EmbedDetailMap | undefined
        // Only seed when absent, so a slow load that resolves after the user
        // started editing never clobbers in-progress nested edits.
        if (existing && existing[gcFormNo] !== undefined) return r
        changed = true
        return { ...r, __children: { ...(existing || {}), [gcFormNo]: gcRows } }
      })
      return changed ? { ...b, [childFormNo]: next } : b
    })
  }, [])

  // Every child grid mounts at once in this layout, so nothing calls
  // ChildStore.ensureLoaded (in the tabbed editor that's ChildFormTab's mount
  // effect, which only fires for the ACTIVE tab). Drive the same lazy-load
  // from here.
  //
  // Deps, deliberately:
  // • editTxnId (state, not the ref) is the add-mode guard AND what re-fires
  //   the load when an in-place save promotes add → edit.
  // • parentPkKey is the stable serialisation of parentPk. buildParentPk reads
  //   only key columns, which render readOnlyKey in edit mode, so header
  //   keystrokes and item_change cascades don't move it.
  // • detailsBuffer is intentionally NOT a dep — that is what would loop
  //   (fetch → setDetailsBuffer → effect → fetch). ensureChildRowsLoaded is
  //   already idempotent: it bails on hasOwnProperty (so a form that
  //   legitimately loaded zero rows is never refetched) and de-dupes
  //   in-flight fetches via loadingFormsRef.
  // The tabbed editor's inline-in-group tables (`display_in_group`) are always
  // mounted too, so they load the same way.
  useEffect(() => {
    if (!editorOpen || !editTxnId) return
    if (effectiveEditor !== 'simple' && inlineFormNos.size === 0) return
    for (const entry of topForms.slice(1)) {
      if (effectiveEditor !== 'simple' && !inlineFormNos.has(entry.formNo)) continue
      void ensureChildRowsLoaded(entry.formNo, parentPk)
    }
    // parentPk is covered by parentPkKey (its stable serialised form).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveEditor, editorOpen, editTxnId, parentPkKey, topForms, inlineFormNos, ensureChildRowsLoaded])

  // The header FormRenderer's group slots: one editable grid per inline child
  // form, keyed by the header group it sits in. Wired exactly like the
  // "Simple" layout's grids (same buffer, same atomic save). A `lines_locked`
  // table that has no rows is left out, so its group disappears: nobody can
  // add to it, and an empty history is better not shown.
  const inlineGroupSlots: Record<string, ReactNode> | undefined = inlineFormNos.size === 0
    ? undefined
    : Object.fromEntries(topForms
      .filter(entry => inlineFormNos.has(entry.formNo))
      .filter(entry => !entry.sm.lines_locked
        || ((detailsBuffer[entry.formNo] as ChildRow[]) || []).some(r => !r._deleted))
      .map(entry => [
        (entry.sm.display_in_group || '').trim(),
        <div key={entry.formNo} style={{
          height: SIMPLE_GRID_HEIGHT, overflow: 'hidden',
          border: '1px solid var(--color-border)', borderRadius: 6,
          background: 'var(--color-bg)',
        }}>
          <ChildTabularGrid
            execute={gridExecute}
            project={project}
            objName={objName}
            formNo={entry.formNo}
            childSm={entry.sm}
            rows={(detailsBuffer[entry.formNo] as ChildRow[]) || []}
            onMutate={mutateChildRows(entry.formNo)}
            parentHeaderValues={formValues}
            uploadDocument={runtime.txn.uploadDocument}
            busy={busy || hostBusy}
            readOnly={viewOnly || formLinesViewOnly(entry.sm)}
            parentStatus={!editTxnId ? 'unsaved' : formDirty ? 'dirty' : 'retrieved'}
            gridLayout={gridLayout}
            model={workingModel}
            recordId={editTxnId || undefined}
            loadChildRows={loadGridChildRows}
            seedRowChildren={seedGridRowChildren}
            onAncestorPatch={applyHeaderPatch}
            depth={1}
            addLabel={entry.sm.add_label}
          />
        </div>,
      ]))

  // A line form whose Line Entry says `view: "grid"` is edited in the
  // spreadsheet grid in the tabbed editor too (ChildFormTab, which it
  // replaces, would otherwise have lazy-loaded its rows on mount).
  const activeGridForm = topForms.find(f => f.formNo === activeFormNo && lineEntryOf(f.sm)?.view === 'grid')
  useEffect(() => {
    if (!activeGridForm || !editorOpen || !editTxnId) return
    void ensureChildRowsLoaded(activeGridForm.formNo, parentPk)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGridForm?.formNo, editorOpen, editTxnId, parentPkKey, ensureChildRowsLoaded])

  // Rows as shown in the list: when the viewer lacks edit/save rights every
  // row is presented view-only (pencil → eye, opens read-only) by stamping the
  // engine's own _editable/_lock_reason convention — same UX the legacy
  // runtime embed produced with its rights-aware rowCanEdit.
  const displayRows = useMemo(() => {
    const locked = canEditRecords
      ? rows
      : rows.map(r => (r._editable === false ? r : {
          ...r,
          _editable: false,
          _lock_reason: (typeof r._lock_reason === 'string' && r._lock_reason)
            || 'You do not have permission to edit records',
        }))
    // Imported Vision buttons a row's state rules out (OBJ_ACTIONS.SHOW_CONFIRM
    // against its confirmed / cancelled / closed state, and its workflow) join the row's hidden
    // actions, so every list surface drops them from that row's ⋮ menu. The
    // menu hides by NAME, and so does Vision: SHOW_CONFIRM belongs to the
    // title (visionShowConfirmByTitle), so every button of a name agrees.
    const states = sm?.list_view?.vision_states
    if (!states && !sm?.list_view?.vision_workflow) return locked
    const byTitle = visionShowConfirmByTitle(rowActions)
    return locked.map(r => {
      const inState = visionRecordStates(sm, r)
      const shown = new Set(rowActions.filter(a => showConfirmAllows(a, inState, byTitle)).map(a => a.name))
      const out = [...new Set(rowActions.map(a => a.name))].filter(n => !shown.has(n))
      if (out.length === 0) return r
      const prev = Array.isArray(r._hidden_actions) ? r._hidden_actions as string[] : []
      return { ...r, _hidden_actions: [...prev, ...out] }
    })
  }, [rows, canEditRecords, sm, rowActions])
  // The rows currently on screen, for handlers defined above them (the assign
  // dialog names the record the user clicked). A ref, not a dependency: the
  // handler must not be rebuilt on every page of a list.
  const displayRowsRef = useRef<typeof displayRows>([])
  displayRowsRef.current = displayRows

  // ── Customize mode: enter / leave / pick what to customize ───────────
  const enterCustomize = (scope: 'org' | 'app' = 'org') => {
    if (scope === 'app' && !runtime.build) return
    if (editorOpen && !guardDirtyClose()) return
    setEditorOpen(false)
    setEditTxnId(null)
    setFormDirty(false)
    if (view !== 'list') setView('list')
    setCzField(null)
    setCzAddField(null)
    setCzButton(null)
    setCzScope(scope)
    setCzReadOnly(scope === 'app' ? (runtime.build?.readOnlyReason?.(objName) ?? null) : null)
    setCzFull(true)
    setCustomizing(true)
  }
  const enterCustomizeRef = useRef(enterCustomize)
  enterCustomizeRef.current = enterCustomize
  // The record being edited closes with the mode (asking first when it has
  // unsaved changes): it was opened in the mode's place, not as a window.
  const leaveCustomize = () => {
    if (editorOpen && !guardDirtyClose()) return
    setCustomizing(false)
    setCzFull(false)
    setCzField(null)
    setCzAddField(null)
    setCzButton(null)
    setCzScope('org')
    setCzReadOnly(null)
    setEditorOpen(false)
    setEditTxnId(null)
    setViewOnly(false)
    setFormValues({})
    setDetailsBuffer({})
    setFormDirty(false)
    void refreshList()
  }
  const resetToStandard = async () => {
    if (!runtime.customize) return
    try {
      await runtime.customize.transport.reset('T', objName)
      const r = await runtime.customize.transport.get('T', objName)
      const eff = (r.effective as { transaction?: TransactionModel } | undefined)?.transaction
      if (eff) setWorkingModel(eff)
      notify.success('This screen is back to the standard layout')
    } catch (err) {
      reportError('Could not reset the customization', err)
    }
  }
  const czBuilding = customizing && czScope === 'app' && !!runtime.build
  const czApi: TxnCustomizeApi | null = customizing ? {
    session: czSession,
    core: czSession.core,
    openField: (formNo, colKey, anchor) => { setCzButton(null); setCzField({ formNo, colKey, anchor }) },
    openFieldKey: czField ? `${czField.formNo}|${czField.colKey}` : null,
    scope: czBuilding ? 'app' : 'org',
    ...(czBuilding ? {
      addField: (formNo: string, group: string | null) => { setCzField(null); setCzAddField({ formNo, group }) },
      openButton: (scopeKey: string, name: string, anchor: HTMLElement) => {
        setCzField(null); setCzButton({ scopeKey, name, anchor })
      },
      addButton: (scopeKey: string) => { setCzField(null); setCzButton({ scopeKey, name: null, anchor: null }) },
      openButtonKey: czButton?.name ? `${czButton.scopeKey}|${czButton.name}` : null,
    } : {}),
  } : null

  // ✎ Make changes beside the list's header actions, for people who build
  // the app (runtime.build answers canBuild). Never inside the mode.
  const withMakeChanges = (actions: ReactNode): ReactNode => (canBuild && !customizing && !runtime.build?.hostButton ? (
    <>
      <MakeChangesButton onClick={() => enterCustomize('app')} />
      {actions}
    </>
  ) : actions)
  // Where "Ask AI to change" is pointed: the screen › the form tab › the field.
  const czAiContext = (): string => {
    const title = sm?.form_title || objName
    if (czField) {
      const f = forms.find((x) => x.formNo === czField.formNo)
      const c = (f?.sm.columns || []).map((w) => w.column).find((x) => x && czColumnKey(x) === czField.colKey)
      const tab = f && czField.formNo !== forms[0]?.formNo ? ` › ${f.sm.form_title || 'lines'} tab` : ''
      return `${title}${tab} › ${c?.heading || czField.colKey}`
    }
    if (editorOpen) {
      const f = tabForms.find((x) => x.formNo === activeFormNo)
      return f && f.formNo !== forms[0]?.formNo ? `${title} › ${f.sm.form_title || 'lines'} tab` : `${title} (a record)`
    }
    return `${title} (the list)`
  }

  // ── Host imperative handle ───────────────────────────────────────────
  useImperativeHandle(apiRef, () => ({
    refresh: refreshList,
    refreshApprovals: () => { void refreshApprovalsCount() },
    closeAndClearEditor: () => {
      setEditTxnId(null)
      setFormValues({})
      setDetailsBuffer({})
      setFormDirty(false)
      setEditorOpen(false)
    },
    reportError,
    setBusy,
    enterCustomize: (scope) => enterCustomizeRef.current(scope),
    runOpen: (payload) => runOpenRef.current(payload),
  }), [refreshList, refreshApprovalsCount, reportError])

  // ── Render ────────────────────────────────────────────────────────────

  if (!sm) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: '0.9125rem' }}>
        This transaction has no sql_models. Add a form in the designer first.
      </div>
    )
  }

  // The viewer can't read this object at all (standalone runtime rights):
  // show a friendly notice instead of the screen — the surrounding app shell
  // still renders. Same message the legacy runtime embed used.
  if (rights && !rights.can_read && !submit) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: '0.9125rem', textAlign: 'center' }}>
        You don't have access to this content.
      </div>
    )
  }

  // A submit form needs only `add` — the right to fill it in and run it.
  if (submit && ready && !rightsPending && !can('add')) {
    return (
      <div style={{ padding: 24, color: 'var(--color-text-secondary)', fontSize: '0.9125rem', textAlign: 'center' }}>
        You don't have rights to run this. Ask an administrator to grant your role this screen.
      </div>
    )
  }

  const containerStyle: React.CSSProperties = czFullOn ? {
    // Customize mode, full screen: the whole mode overlays the page.
    position: 'fixed', inset: 0, zIndex: Z_FULLSCREEN_VIEW,
    display: 'flex', flexDirection: 'column',
    background: 'var(--color-bg)', color: 'var(--color-text)',
  } : {
    display: 'flex', flexDirection: 'column', height: '100%',
  }
  // Customize mode shows a form IN PLACE of the list, under the mode's bar —
  // not as a window over the page — so full screen / restore move the bar, the
  // form and the list together.
  const czEditorInline = customizing && editorOpen && effectiveEditor !== 'document' && effectiveEditor !== 'compact'
  // A View-mode 'open' button that presents its target in a drawer (a
  // "details in a side panel" link): the record is drawn in the drawer, in
  // place of the list, rather than as a window over it — a window would sit
  // apart from the drawer it was opened for. Closing it closes the drawer.
  const drawerViewInline = editorOpen && effectiveEditor !== 'document' && effectiveEditor !== 'compact'
    && initialScreen?.open?.mode === 'view' && initialScreen?.open?.presentation === 'drawer'
  // A submit form (`submit`) is its one form, drawn in place of the list.
  const editorInline = czEditorInline || drawerViewInline || !!submit

  // The LIST layout switch (List · Spread sheet · Bulk entry · Compact, +
  // Process where the object sits on a flow). Threaded into whichever
  // interface is active so it stays visible and can switch back. Switching to
  // the list refreshes it so records entered via the batch interfaces show
  // up. Without host-supplied capture interfaces, list ⇆ compact is still
  // offered so a phone user can switch back to the desktop grid. How a record
  // is EDITED is a separate choice, made in the open record (the editor
  // layout switch below).
  const switchTo = (v: EmbedListView) => {
    // An old link / host may still name an editor layout as a view.
    const legacyEditor = LEGACY_EDITOR_VIEWS[v]
    if (legacyEditor && isEditorLayout(legacyEditor)) {
      applyEditorLayout(legacyEditor)
      v = 'list'
    }
    touchedViewRef.current = true
    setView(v)
    updateScreenPref(project, objName, { view: v })
    if (v === 'list') void refreshList()
  }
  // A phone draws Compact as its own full-width card list; a desktop keeps the
  // list's header, tabs and toolbar around a grid of the same cards.
  const phoneList = isMobileViewport()
  const viewOptions = useMemo(() => {
    const base = (interfaces ? TXN_SCREEN_VIEWS : TXN_SCREEN_BUILTIN_VIEWS)
      .filter(v => !(v in LEGACY_EDITOR_VIEWS))
    return onFlow ? [...base, 'process' as EmbedListView] : [...base]
  }, [interfaces, onFlow])
  // WHERE the switch sits is the person's choice per screen (screenPrefs
  // `switch`): on the screen (default), or in the ⋮ menu — which then offers
  // the layouts and a way to put the switch back. The List's ⋮ is the screen's
  // own (no host chrome) or the host's `renderMore`; a host that hands plain
  // `headerActions` has none to carry it.
  const switchPlace: LayoutSwitchPlace = submit
    ? 'bar' : (screenPref.pref.switch ?? DEFAULT_LAYOUT_SWITCH_PLACE)
  const switchInMenu = switchPlace === 'menu' && viewOptions.length >= 2
  const listHasMenu = !!hostRenderMore || !headerActions
  const layoutItems = layoutMenuItems({
    value: view, options: viewOptions, onChange: switchTo, place: switchPlace,
    onPlace: (p) => updateScreenPref(project, objName, { switch: p }),
  })
  // Kept in the ⋮ menu: where a layout has no ⋮ of its own to carry it
  // (Spread sheet / Bulk entry, Process, a phone's card list, a host without
  // one) a small ⋮ holding just the layouts stands in for the switch.
  const layoutOnlyMenu = <CustomizeMenu enabled={false} onCustomize={() => {}} items={layoutItems} />
  const switcher = switchInMenu ? layoutOnlyMenu : (
    <LayoutSwitch value={view} options={viewOptions} onChange={switchTo} iconsOnly={phoneList} />
  )
  // The List's own row: with the switch in its ⋮ menu the row carries nothing
  // for it (and is not drawn when it has no tabs either).
  const listSwitcher = switchInMenu && listHasMenu ? null : switcher
  const listLayoutMenuItems = listHasMenu ? layoutItems : undefined

  // ── Editor layout (E2) ────────────────────────────────────────────────
  // Standard ⇄ Simple ⇄ Compact share this screen's form state and line
  // buffer, so they switch freely with the record open. Document saves
  // through its own path, so moving to or from it with unsaved changes asks
  // Save / Discard first (`layoutAsk`).
  const editorOptions = useMemo<EditorLayout[]>(() => EDITOR_LAYOUTS
    .map(l => l.id as EditorLayout)
    .filter(id => id !== 'document' || !!documentView), [documentView])
  const applyEditorLayout = (next: EditorLayout, remember = true) => {
    touchedEditorRef.current = true
    setEditorLayout(next)
    if (remember) updateScreenPref(project, objName, { editor: next })
  }
  const [layoutAsk, setLayoutAsk] = useState<{ next: EditorLayout; leavingDoc: boolean } | null>(null)
  const unsavedRef = useRef(false)
  unsavedRef.current = formDirty || bufferHasUnsaved(detailsBuffer)
  const changeEditorLayout = (next: EditorLayout) => {
    if (next === effectiveEditor) return
    const leavingDoc = effectiveEditor === 'document'
    if (editorOpen && !viewOnly && (leavingDoc || next === 'document')) {
      const dirty = leavingDoc ? !!documentControl.current?.dirty : unsavedRef.current
      if (dirty) { setLayoutAsk({ next, leavingDoc }); return }
    }
    applyEditorLayout(next)
  }
  const answerLayoutAsk = async (choice: 'save' | 'discard' | 'cancel') => {
    const ask = layoutAsk
    if (!ask || choice === 'cancel') { setLayoutAsk(null); return }
    try {
      if (ask.leavingDoc) {
        const ctl = documentControl.current
        if (choice === 'save' && ctl && !(await ctl.save())) return  // its errors are on the sheet
        if (choice === 'discard') ctl?.discard()
      } else if (choice === 'save') {
        await saveFormRef.current()
        await afterRender()
        if (unsavedRef.current) return  // refused — the banner says why; stay
      } else if (editTxnId) {
        await startEdit(editTxnId)  // re-read the stored record: edits gone
      } else {
        setFormValues({})
        setDetailsBuffer({})
        setFormDirty(false)
      }
      setLayoutAsk(null)
      applyEditorLayout(ask.next)
    } catch (err) {
      reportError('Could not switch the editor layout', err)
    }
  }
  const layoutAskNotice = layoutAsk ? (
    <div role="alert" data-tw-dialog="editor-layout-ask" style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
      padding: '10px 20px', background: 'var(--color-warning-weak, #fff8e6)',
      borderBottom: '1px solid var(--color-warning, #f0d58a)',
      color: 'var(--color-text, #1c2430)', fontSize: '0.875rem',
    }}>
      <span style={{ flex: '1 1 260px', fontWeight: 600 }}>
        {`You have unsaved changes. Save them before switching to ${editorLayoutLabel(layoutAsk.next)}?`}
      </span>
      <button type="button" {...tw('action', 'cancel')} style={{ ...editorFootBtn, height: 34 }}
              onClick={() => { void answerLayoutAsk('cancel') }}>Cancel</button>
      <button type="button" {...tw('action', 'discard')}
              style={{ ...editorFootBtn, height: 34, color: 'var(--color-error, #b42318)' }}
              onClick={() => { void answerLayoutAsk('discard') }}>Discard</button>
      <button type="button" {...tw('action', 'save')} style={{ ...editorFootPrimary, height: 34 }}
              onClick={() => { void answerLayoutAsk('save') }}>Save</button>
    </div>
  ) : null
  const editorLayoutSwitch = (opts?: { exclude?: EditorLayout[] }) => (submit ? null : (
    <EditorLayoutSwitch
      value={effectiveEditor}
      options={editorOptions.filter(o => !opts?.exclude?.includes(o))}
      onChange={changeEditorLayout}
    />
  ))
  // The editor's title line: its layout switch and its Maximise / Restore each
  // sit on the line (default) or in the editor's ⋮ menu — the person's choice
  // per screen (screenPrefs `editorSwitch` / `sizeControl`). A submit form has
  // no layouts and saves no preferences; its resize control stays on the line.
  const editorSwitchPlace: LayoutSwitchPlace = submit
    ? 'bar' : (screenPref.pref.editorSwitch ?? DEFAULT_LAYOUT_SWITCH_PLACE)
  const editorMenuLayoutItems = submit ? [] : editorLayoutMenuItems({
    value: effectiveEditor, options: editorOptions, onChange: changeEditorLayout, place: editorSwitchPlace,
    onPlace: (p) => updateScreenPref(project, objName, { editorSwitch: p }),
  })
  const editorTitleSwitch = editorSwitchPlace === 'menu' && editorOptions.length >= 2
    ? null : editorLayoutSwitch()
  const editorSizeControl: EditorSizeControl | undefined = submit ? undefined : {
    place: screenPref.pref.sizeControl ?? DEFAULT_LAYOUT_SWITCH_PLACE,
    onPlace: (p) => updateScreenPref(project, objName, { sizeControl: p }),
  }

  // The server's answer arrives after the first paint: apply what it knows
  // that this visit has not already decided otherwise.
  useEffect(() => {
    if (submit || !screenPref.ready) return
    const p = screenPref.pref
    if (!touchedViewRef.current && p.view && p.view !== view
        && viewOptions.includes(p.view as EmbedListView)) {
      setView(p.view as EmbedListView)
    }
    if (!touchedEditorRef.current && p.editor && p.editor !== editorLayout) {
      setEditorLayout(p.editor)
    }
    if (p.tab && tabsCfg && p.tab !== activeTabRef.current
        && initialTab(tabsCfg, p.tab) === p.tab) {
      if (readyRef.current) void selectTab(p.tab, { remember: false })
      else { activeTabRef.current = p.tab; setActiveTab(p.tab) }  // the first fetch asks for it
    }
    // Only a new answer re-applies; the person's own switches set the refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenPref.ready, screenPref.pref.view, screenPref.pref.editor, screenPref.pref.tab, viewOptions])

  /*
   * The view a LINK asked for, applied once.
   *
   * Placed here rather than beside the other `initialScreen` effect because
   * the dependency array is evaluated during render and `viewOptions` is
   * defined just above — reading it earlier is a temporal-dead-zone crash, not
   * a stale value.
   *
   * Deliberately not remembered: the reader followed a link to a document,
   * they did not choose to work in the document view from now on. A link
   * naming `document` / `simple` opens the record in that EDITOR layout.
   */
  const appliedViewRef = useRef<string>('')
  useEffect(() => {
    const want = String(initialScreen?.view || '').toLowerCase()
    if (!want || want === appliedViewRef.current) return
    const editorWant = LEGACY_EDITOR_VIEWS[want]
    if (editorWant && isEditorLayout(editorWant)) {
      if (editorWant === 'document' && !documentView) return
      appliedViewRef.current = want
      touchedEditorRef.current = true
      setEditorLayout(editorWant)
      if (view !== 'list' && view !== 'compact') setView('list')
      return
    }
    if (!viewOptions.includes(want as EmbedListView)) return
    appliedViewRef.current = want
    touchedViewRef.current = true
    setView(want as EmbedListView)
  }, [initialScreen?.view, viewOptions, documentView, view])

  // Shared header controls so the list AND spreadsheet interfaces carry the
  // same Approvals inbox + host chrome. Only surfaced on screens whose object
  // is approval-controlled — there's no point offering the inbox on a screen
  // that can never stage records — AND when the host actually supplies the
  // inbox slot (a button that opens nothing is worse than no button; hosts
  // without renderApprovalsInbox — the standalone runtime for now — see no
  // affordance at all).
  const approvalsButton = approvalEnabled && renderApprovalsInbox ? (
    <ApprovalsButton
      count={approvalsCount}
      onOpen={() => setApprovalsOpen(true)}
    />
  ) : null

  // Rights-gated action sets (full when the host supplies no runtime.rights):
  // form-scoped custom actions require add; row-scoped + bulk require edit,
  // except a bulk system Delete, which requires delete — the same
  // authorization the server applies to run_action / run_action_bulk.
  const grantedFormActions = can('add') ? formActions.filter(a => canUseAction(a.name)) : []
  // The built-in Add: the viewer's add right AND the designer not having
  // switched Add off for this form (list_view.allow_add === false, e.g. a
  // review screen whose records are created elsewhere; the engine refuses
  // the insert too). Form actions keep following the right alone.
  const canAddRecords = can('add') && sm?.list_view?.allow_add !== false
  // An imported Vision "Add" stays out of the row menu: the list's own Add
  // button above it is the same operation. So do the Vision buttons E12 keeps
  // off a record's home menu (field links, side-panel-only links, "More"
  // buttons — visionInRowMenu). One entry per name: the menu runs and hides
  // by name, so a second "Edit" row would be the same item drawn twice.
  const seenRowAction = new Set<string>()
  const grantedRowActions = can('edit')
    ? rowActions.filter(a => canUseAction(a.name) && visionActionOf(a)?.builtin !== 'add'
      && visionInRowMenu(a) && !seenRowAction.has(a.name) && !!seenRowAction.add(a.name))
    : []
  // An imported Vision screen offers Delete only when its OBJ_ACTIONS has a
  // "Delete" row, as E12 does; any other screen follows the delete right.
  const canDeleteRecords = can('delete') && visionScreenOffersDelete(headerSm?.actions) !== false
  // Vision's own Audit Trail button (builtin audit_trail) is the row's audit
  // trail: the list's built-in "Audit trail" item would be the same thing twice.
  const listHistory = grantedRowActions.some(a => visionActionOf(a)?.builtin === 'audit_trail')
    ? undefined : setHistoryTxn
  // The object sends messages (a send button or a send follow-up) and the host
  // can show a record's log → a "Messages" item on the row ⋮ and the editor ⋮.
  const hasSends = useMemo(() => {
    // `model` IS the transaction (its `follow_up_actions` sit beside `actions`).
    const raw = (model as unknown as { follow_up_actions?: unknown[] })?.follow_up_actions
    const fus = Array.isArray(raw) ? raw : []
    return [...rowActions, ...formActions].some(a => a.handler === 'send')
      || fus.some(f => !!f && typeof f === 'object' && !!(f as { send?: unknown }).send)
  }, [model, rowActions, formActions])
  const openMessages = useMemo(() => {
    const open = runtime.txn.openMessageLog
    return hasSends && open ? (txnId: string) => open(objName, txnId) : undefined
  }, [hasSends, runtime, objName])
  // ── Export (the system action `system_op: "export"`) ──────────────────
  // Present only while the model carries the enabled action AND the host can
  // export. Rights are the server's (`export` on the object — it follows the
  // read right until a grant names it); a refused person is told in the window.
  const exportApi = runtime.txn.exportApi
  const exportCfg = exportApi && !submit ? exportActionOf(model) : null
  const exportListColumns = orderedDisplayCols(
    listOrderedColumns(udaListSm ?? sm),
    gridLayout.layouts[forms[0]?.formNo || '1'],
  ).filter(c => !isListHidden(c)).map(columnFormKey)
  const exportQuery = (): Record<string, unknown> => ({
    ...(activeTabRef.current ? { tab: activeTabRef.current } : {}),
    ...(openFilterRef.current ? { filters: openFilterRef.current.filters } : {}),
    ...(overridesRef.current && Object.keys(overridesRef.current).length
      ? { enforced_overrides: overridesRef.current } : {}),
    ...(bypassRef.current ? { bypass_enforced: true }
      : filterSetIdRef.current ? { enforced_filter_set_id: filterSetIdRef.current } : {}),
  })
  const openExportList = (selectedIds: string[] = []) => setExportScope({
    kind: 'list', query: exportQuery(), matching: total, selectedIds,
    loadedIds: displayRows.map(r => String(r._txn_id ?? '')).filter(Boolean),
  })
  const exportBulkName = exportCfg && exportCfg.places.includes('bulk') ? exportCfg.label : null
  const grantedBulkActions = [
    ...bulkActions.filter(a =>
      (isSystemDeleteAction(a) ? can('delete') : can('edit')) && canUseAction(a.name)),
    // The selection bar's Export: the ticked records, never run through the
    // engine's run_action_bulk (runBulkActionOrExport opens the window).
    ...(exportBulkName && !bulkActions.some(a => a.name === exportBulkName)
      ? [{ name: exportBulkName, handler: 'system', system_op: 'export' } as ActionDef] : []),
  ]
  const runBulkActionOrExport = (name: string, txnIds: string[]) => {
    if (exportBulkName && name === exportBulkName) { openExportList(txnIds); return Promise.resolve() }
    return runBulkAction(name, txnIds)
  }
  // The screen's actions, ONE list for every layout: Export and the
  // form-level custom buttons. Each sits on the bar or (the default) in the ⋮
  // menu, as the designer placed it (`placement`, see ScreenActions).
  const screenActionItems: ScreenActionItem[] = [
    ...(exportCfg && exportCfg.places.includes('list') ? [{
      key: '__export__', label: exportCfg.label, icon: <Download size={15} />,
      placement: exportCfg.placement, onClick: () => openExportList(), hook: 'export:open',
    }] : []),
    ...grantedFormActions.map(a => ({
      key: `fa-${a.name}`, label: a.name, icon: <Play size={15} />, placement: actionPlacement(a),
      onClick: () => { void runFormAction(a.name) }, hook: a.name, scope: 'form',
      shortcut: shortcutHint(actionShortcutCombo(a)) || undefined,
    })),
  ]
  const beforeAddActionsWithExport = approvalsButton || screenActionItems.some(i => i.placement === 'bar') ? (
    <>{approvalsButton}<ScreenActionButtons items={screenActionItems} busy={busy} /></>
  ) : null
  const screenActionMenuItems = asMoreMenuItems(screenActionItems)
  const exportRecordItem: MoreMenuItem[] = exportCfg && exportCfg.places.includes('record') && editTxnId ? [{
    label: `${exportCfg.label} this record`, icon: <Download size={14} />, hook: 'export:record', divider: true,
    onClick: () => setExportScope({ kind: 'record', txnId: editTxnId }),
  }] : []

  // Context handed to the host's alternate-interface slots so they run in the
  // same filter/action state as the list.
  const interfaceCtx: TxnScreenInterfaceContext = {
    view,
    switcher,
    approvalsButton,
    splitHandle: splitHandleRef,
    enforcedOverrides: overridesRef.current,
    activeFilterSetId,
    bypassed,
    applyFilters,
    selectFilterSet,
    rowActions: grantedRowActions,
    runRowAction,
    bulkActions: grantedBulkActions,
    runBulkAction: runBulkActionOrExport,
    bulkBusy,
    canAdd: canAddRecords,
    canEdit: canEditRecords,
    canDelete: canDeleteRecords,
    recordTxnId: editTxnId || null,
    closeEditor,
    editorLayout: effectiveEditor,
    editorLayoutSwitch,
    bulkEditorLayout: (touchedEditorRef.current || screenPref.pref.editor) && effectiveEditor !== 'document'
      ? effectiveEditor : 'simple',
    activeTab,
    documentControl,
    actionButtons: <ScreenActionButtons items={screenActionItems} busy={busy} />,
    moreMenu: withMakeChanges(hostRenderMore
      ? hostRenderMore(screenActionMenuItems)
      : headerActions ?? ((canCustomize && !customizing) || screenActionMenuItems.length ? (
        <CustomizeMenu enabled={canCustomize && !customizing} items={screenActionMenuItems}
          onCustomize={() => enterCustomize()} onReset={() => { void resetToStandard() }}
          subtitle={`Arrange this list and every form of ${sm.form_title || objName} for your organization`} />
      ) : null)),
  }

  // A record shown on a process screen — the Process view's rail, or a step's
  // box in the trail — offers exactly what it offers in the list: the screen's
  // OWN handlers, already rights-gated, rather than a second copy that could
  // drift from them. Built once and handed to both.
  const processNodeActions: NodeActions = {
    objName,
    rowActions: grantedRowActions,
    // The built-in ops the designer put conditions on. A process surface opens
    // records THROUGH these, so "only an Open deal may be edited" holds in the
    // trail and on a process card exactly as it holds on the list.
    systemOverrides,
    onRunRowAction: runRowAction,
    onAdd: canAddRecords ? () => { void startAdd() } : undefined,
    onEdit: (txnId) => { void startEdit(txnId) },
    onDelete: (txnId) => { void deleteFromList(txnId) },
    onHistory: listHistory,
    onAlias: openAliases,
    onAssign: openAssign,
    canAdd: canAddRecords,
    canEdit: true,
    canDelete: canDeleteRecords,
  }

  // The Compact editor's content: one narrow column, every line form as
  // cards. A phone shows it full screen (CompactFormShell); a desktop in a
  // narrow window (EditorModal `narrow`).
  const phoneEditor = isMobileViewport()
  const compactEditorBody = (
      <>
        <ValidationBanner
          fieldErrors={fieldErrors}
          banner={formBanner}
          columnLabel={(dbName) => columnLabelFromModel(sm, dbName)}
        />
        {udaHeaderNotice}
        <FormRenderer
          model={udaHeaderSmView!}
          groupCaptions={udaCaptions}
          groupsHeading={udaGroupsHeading}
          keyGeneration={workingModel.key_generation}
          canUseAction={canUseAction}
          values={formValues}
          errors={fieldErrors}
          onChange={onFieldChange}
          readOnlyKey={!!editTxnId}
          viewOnly={viewOnly}
          lockReason={lockReason}
          wfInProgress={wfInProgress}
          dynamicProtect={dynamicProtect}
          project={project}
          objName={objName}
          formNo={forms[0]?.formNo}
          txnId={editTxnId || undefined}
          onActionResult={applyActionResult}
          onSystemAction={handleSystemAction}
          onVisionBuiltin={performVisionBuiltin}
          onActionError={handleActionError}
          layout="compact"
        />
        {model.content && contentPanelVisible(model.content) && (
          <ContentPanel profile={model.content} sm={sm} values={formValues} />
        )}
        {lineEntryBanners}
        {/* All direct child forms stacked as compact sections (no tabs). */}
        {topForms.slice(1).map((entry) => (
          <ChildFormTab
            key={entry.formNo}
            project={project}
            objName={objName}
            formNo={entry.formNo}
            sm={entry.sm}
            parentSm={sm}
            parentPkKey={parentPkKey}
            parentPk={parentPk}
            parentHeaderValues={formValues}
            gridLayout={gridLayout}
            model={workingModel}
            depth={1}
            store={makeTopStore(entry.formNo)}
            readOnly={viewOnly || formLinesViewOnly(entry.sm)}
            lockedLineActions={!viewOnly && !!entry.sm.lines_locked}
            selectLocked={viewOnly}
            variant="compact"
            visionDialogs={visionDialogs}
            recordTxnId={editTxnId || undefined}
            onAncestorPatch={applyHeaderPatch}
          />
        ))}
      </>
  )

  const body = (
    <div style={containerStyle} {...tw('screen', objName, {
      kind: 'T', view, mode: editorOpen ? (viewOnly ? 'view' : editTxnId ? 'edit' : 'add') : 'list',
    })}>
      {customizing && (
        <CustomizeBar
          title={sm.form_title || objName}
          session={czSession}
          onDone={leaveCustomize}
          scope={czBuilding ? 'app' : 'org'}
          note={czBuilding && czReadOnly ? `Read-only for now: ${czReadOnly}` : undefined}
          toolsRow={czBuilding && runtime.build ? (
            <BuildBarTools
              session={czSession}
              build={runtime.build}
              objName={objName}
              title={sm.form_title || objName}
              model={workingModel}
              headerFormNo={forms[0]?.formNo || '1'}
              context={czAiContext()}
              onOpenAttributes={UdaSetup && canUdaSetup ? () => { setCzField(null); setCzUda({}) } : undefined}
            />
          ) : undefined}
          actions={!czBuilding && UdaSetup && canUdaSetup ? (
            <CustomizeBarAction label="Additional attributes" hook="uda-setup"
              onClick={() => { setCzField(null); setCzUda({}) }} />
          ) : undefined}
          resetOptions={czBuilding ? [] : [
            // What is on screen: the list (with the header form it lists), or
            // the form tab the open record is showing.
            ...(!editorOpen || activeFormNo === (forms[0]?.formNo || '1')
              ? [{ label: `Reset the list and ${sm.form_title || 'the header form'}`, count: czSession.countCustomized(czFormBase(forms[0]?.formNo || '1')), run: () => { void czSession.resetUnder([czFormBase(forms[0]?.formNo || '1')], 'Reset list') } }]
              : (() => {
                  const f = tabForms.find((x) => x.formNo === activeFormNo)
                  return [{ label: `Reset ${f?.sm.form_title || 'this form'}`, count: czSession.countCustomized(czFormBase(activeFormNo)), run: () => { void czSession.resetUnder([czFormBase(activeFormNo)], 'Reset form') } }]
                })()),
            { label: 'Reset everything on this screen', count: czSession.countCustomized(), run: () => { void czSession.resetAll() } },
          ]}
        />
      )}
      {/* Customize → Additional attributes: the setup screen in place of the
          list (Phase 5 registers it; see uda/setupSlot). */}
      {customizing && czUda && UdaSetup && runtime.uda && (
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <UdaSetup transport={runtime.uda} objName={objName} formNo={czUda.formNo}
            columnKey={czUda.columnKey} onClose={() => setCzUda(null)}
            model={workingModel} project={project} />
        </div>
      )}
      {/* List fills the surface. Host chrome (deploy summary / menus) lives
          behind headerActions in the list header; the grid toolbar owns the
          fullscreen toggle. */}
      <div style={{
        flex: 1, overflow: 'hidden', flexDirection: 'column',
        display: (customizing && czUda && UdaSetup) || editorInline ? 'none' : 'flex',
      }}>
        {openFilter && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', margin: '6px 12px 0',
            borderRadius: 6, fontSize: '0.8625rem', background: 'var(--color-bg-secondary, #f4f6f9)',
            border: '1px solid var(--color-border, #dde3ea)', color: 'var(--color-text, #1c2430)',
          }}>
            <span style={{ flex: 1 }}>{openFilter.label}</span>
            <button type="button" onClick={() => showOpenMatches(null)}
              title="Show every record again" aria-label="Clear this filter"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none',
                background: 'transparent', color: 'var(--color-accent, #0061D0)', cursor: 'pointer',
                fontSize: '0.8625rem', padding: 2 }}>
              <X size={13} /> Show all
            </button>
          </div>
        )}
        {view === 'process' ? (
        <ProcessOverview
          project={project}
          runtime={runtime}
          focusObj={objName}
          onOpenRecord={onOpenLinkedRecord}
          headerRight={switcher}
          reloadKey={dataVersion}
          nodeActions={processNodeActions}
        />
        ) : view === 'compact' && phoneList ? (
        <CompactListView
          model={udaListSm ?? sm}
          showTitle={!hostNamesThis}
          rows={displayRows}
          total={total}
          busy={busy || hostBusy}
          canAdd={canAddRecords}
          canDelete={canDeleteRecords}
          onAdd={openAdd}
          onOpen={(txnId, opts) => { void openEdit(txnId, { forceView: opts?.view }) }}
          cardOpensView={visionDialogs}
          onDelete={deleteFromList}
          onHistory={listHistory}
          onProcessTrail={onFlow ? setTrailTxn : undefined}
          onAlias={openAliases}
          onAssign={openAssign}
          onRefresh={refreshList}
          // Drawn as screen actions (bar buttons + a ⋮ of their own).
          formActions={[]}
          rowActions={grantedRowActions}
          onRunFormAction={runFormAction}
          onRunRowAction={runRowAction}
          systemOverrides={systemOverrides}
          enforcedFilters={enforcedEcho}
          onApplyFilters={applyFilters}
          filterSets={filterSetsEcho}
          activeFilterSetId={activeFilterSetId}
          bypassed={bypassed}
          onSelectFilterSet={selectFilterSet}
          filterNotices={filterNotices}
          project={project}
          objName={objName}
          pagination={listPagination}
          viewSwitcher={switcher}
          tabs={tabEcho}
          activeTab={activeTab}
          onSelectTab={(id) => { void selectTab(id) }}
          showTabCounts={tabsCfg?.counts !== false}
          beforeAddActions={<>{approvalsButton}<ScreenActions items={screenActionItems} busy={busy} /></>}
          headerActions={withMakeChanges(headerActions ?? (canCustomize && !customizing ? (
            <CustomizeMenu enabled onCustomize={() => enterCustomize()} onReset={() => { void resetToStandard() }}
              subtitle={`Arrange this list and every form of ${sm.form_title || objName} for your organization`} />
          ) : null))}
        />
        ) : view === 'list' || view === 'compact' || !interfaces ? (
        // How a record is EDITED (Standard / Simple / Document / Compact) is
        // the editor layout below, not a list layout: the list stays the list.
        <ListView
          framed
          body={view === 'compact' ? 'cards' : 'table'}
          model={udaListSm ?? sm}
          rowAdornment={udaRowFlags}
          rowAdornmentHeading="Flags"
          fullScreen={customizing ? czFullScreen : undefined}
          showTitle={!hostNamesThis}
          rows={displayRows}
          total={total}
          busy={busy || hostBusy}
          gridLayout={gridLayout}
          formNo={forms[0]?.formNo}
          selectedTxnId={editTxnId}
          shortcutsEnabled={!editorOpen}
          viewSwitcher={listSwitcher}
          layoutMenuItems={listLayoutMenuItems}
          splitHandle={splitHandleRef}
          canAdd={canAddRecords}
          canDelete={canDeleteRecords}
          onAdd={openAdd}
          onEdit={openEdit}
          // A Vision app opens a clicked row in view mode, as its browse
          // screen does; the pencil / F4 / ⋮ Edit still open it for editing.
          onView={visionDialogs ? (txnId) => { void startEdit(txnId, { forceView: true }) } : undefined}
          onDelete={deleteFromList}
          onHistory={listHistory}
          onMessages={openMessages}
          onProcessTrail={onFlow ? setTrailTxn : undefined}
          onAlias={openAliases}
          onAssign={openAssign}
          onRefresh={refreshList}
          // Drawn as screen actions (bar / ⋮) — see screenActionItems.
          formActions={[]}
          actionMenuItems={screenActionMenuItems}
          rowActions={grantedRowActions}
          onRunFormAction={runFormAction}
          onRunRowAction={runRowAction}
          systemOverrides={systemOverrides}
          bulkActions={grantedBulkActions}
          onRunBulkAction={runBulkActionOrExport}
          bulkBusy={bulkBusy}
          onSelectAllMatching={selectAllMatching}
          enforcedFilters={enforcedEcho}
          onApplyFilters={applyFilters}
          filterSets={filterSetsEcho}
          activeFilterSetId={activeFilterSetId}
          bypassed={bypassed}
          onSelectFilterSet={selectFilterSet}
          filterNotices={filterNotices}
          project={project}
          objName={objName}
          pagination={listPagination}
          tabs={tabEcho}
          activeTab={activeTab}
          onSelectTab={(id) => { void selectTab(id) }}
          showTabCounts={tabsCfg?.counts !== false}
          beforeAddActions={beforeAddActionsWithExport}
          headerActions={withMakeChanges(headerActions ?? null)}
          // Without host chrome the screen's ⋮ carries the list's own items
          // (row density) beside Customize.
          {...(hostRenderMore ? {
            renderMore: (items: MoreMenuItem[]) => withMakeChanges(hostRenderMore(items)),
          } : headerActions ? {} : {
            renderMore: (items: MoreMenuItem[]) => withMakeChanges(
              <CustomizeMenu enabled={canCustomize && !customizing} items={items}
                onCustomize={() => enterCustomize()} onReset={() => { void resetToStandard() }}
                subtitle={`Arrange this list and every form of ${sm.form_title || objName} for your organization`} />),
          })}
        />
        ) : (
          // The same page + card the List sits in, so switching layouts keeps
          // the screen's frame still.
          <div style={{
            flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
            background: 'var(--color-bg, #eef1f5)',
            // The same margin rule as the List (ListView `framed`): none inside
            // a host that names the screen, a small one elsewhere.
            padding: screenMargin({ hosted: hostNamesThis, narrow: phoneList }), gap: phoneList ? 6 : 10,
          }}>
            <div style={{ ...screenCardStyle, overflow: 'auto' }}>
              {/* The tabs stay above every list layout; with them the layout
                  switch moves into their row. */}
              {/* Every screen's top row: its tabs (when it has any) and the
                  layout switch — the same row the List draws. */}
              <ListTabs tabs={tabEcho || []} active={activeTab} onSelect={(id) => { void selectTab(id) }}
                        showCounts={tabsCfg?.counts !== false} busy={busy}
                        trailing={switcher} />
              {view === 'tabular'
                ? interfaces.tabular?.({ ...interfaceCtx, switcher: null })
                : interfaces.formInput?.({ ...interfaceCtx, switcher: null })}
            </div>
          </div>
        )}
      </div>

      {effectiveEditor === 'document' && documentView ? (
      /*
       * The document IS the record editor in this layout.
       *
       * It gets its own shell rather than `EditorModal` because it drives its
       * own save — through the document's `/er/save`, which is the same
       * `execute_transaction_batch` the grid form reaches, so there is still
       * one way a record is written. Putting it inside `EditorModal` would
       * give the sheet two Save buttons wired to two different code paths,
       * which is exactly the confusion a single editor is supposed to prevent.
       */
      editorOpen ? (
        <DocumentEditorShell title={editorTitle} onRequestClose={closeEditor}
          layoutSwitch={editorTitleSwitch} menuItems={[...editorMenuLayoutItems, ...exportRecordItem]}
          sizeControl={editorSizeControl} notice={layoutAskNotice}>
          {documentView(interfaceCtx)}
        </DocumentEditorShell>
      ) : null
      ) : effectiveEditor === 'compact' && phoneEditor ? (
      <CompactFormShell
        open={editorOpen}
        title={editorTitle}
        mode={viewOnly ? 'view' : editTxnId ? 'edit' : 'add'}
        lockReason={lockReason}
        txnId={editTxnId}
        dirty={formDirty || bufferHasUnsaved(detailsBuffer)}
        busy={busy}
        badges={udaHeaderBadges}
        onRequestClose={closeEditor}
        onSave={sessionBlock?.kind === 'changed' ? undefined : saveForm}
        onSaveAndClose={sessionBlock?.kind === 'changed' ? undefined : saveAndClose}
        hidden={!!sessionCtx && !sessionVisible}
        onMinimise={minimiseSession}
        layoutSwitch={editorLayoutSwitch()}
      >
        {layoutAskNotice}
        {sessionNotice}
        {compactEditorBody}
      </CompactFormShell>
      ) : (
      <EditorModal
        open={editorOpen}
        inline={editorInline}
        fullScreen={customizing ? czFullScreen : undefined}
        title={submit ? (submit.title || sm.form_title || objName) : editorTitle}
        submitLabel={submit?.label}
        txnId={editTxnId}
        dirty={formDirty || bufferHasUnsaved(detailsBuffer)}
        busy={busy}
        viewOnly={viewOnly}
        lockReason={lockReason}
        badges={udaHeaderBadges}
        onRequestClose={closeEditor}
        onSave={saveForm}
        onSaveAndClose={saveAndClose}
        onMessages={openMessages && editTxnId ? () => openMessages(editTxnId) : undefined}
        // The Additional attributes setup takes the form's place while it is open.
        hidden={(!!sessionCtx && !sessionVisible) || (czEditorInline && !!czUda && !!UdaSetup)}
        onMinimise={minimiseSession}
        notice={submit ? submit.notice : <>{layoutAskNotice}{sessionNotice}</>}
        saveDisabled={sessionBlock?.kind === 'changed' || (sessionBlock?.kind === 'checking' && checkingShown)}
        layoutSwitch={editorTitleSwitch}
        menuItems={[...editorMenuLayoutItems, ...exportRecordItem]}
        sizeControl={editorSizeControl}
        narrow={effectiveEditor === 'compact'}
      >
        {effectiveEditor === 'compact' ? (
          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            {/* The ask / session notices already sit in the modal's notice slot. */}
            {compactEditorBody}
          </div>
        ) : effectiveEditor === 'simple' ? (
        // ── "Simple" layout ──────────────────────────────────────────────
        // Header form stacked over one editable grid per direct child form,
        // in ONE scroll column. EditorModal's body slot is
        // `flex:1; overflow:hidden`, so this branch owns its own scroller;
        // minHeight:0 is required or the flex child refuses to shrink below
        // its content height.
        <div style={{
          flex: 1, minHeight: 0, overflow: 'auto',
          display: 'flex', flexDirection: 'column',
        }}>
          {/* Unconditional here. In the tabbed body the banner lives inside
              the header-tab branch; with no tabs, a routeBatchError banner
              naming a failing child row would otherwise be set and never
              shown. The failing ROW is separately annotated by
              annotateRowError and rendered by its grid. */}
          <ValidationBanner
            fieldErrors={fieldErrors}
            banner={formBanner}
            columnLabel={(dbName) => columnLabelFromModel(sm, dbName)}
            blockedLabel={submit ? 'Run blocked' : undefined}
          />
          {udaHeaderNotice}
          <div style={{ flexShrink: 0 }}>
            <FormRenderer
              model={udaHeaderSmView!}
              groupCaptions={udaCaptions}
              groupsHeading={udaGroupsHeading}
              keyGeneration={workingModel.key_generation}
              canUseAction={canUseAction}
              values={formValues}
              errors={fieldErrors}
              onChange={onFieldChange}
              readOnlyKey={!!editTxnId}
              viewOnly={viewOnly}
              lockReason={lockReason}
              wfInProgress={wfInProgress}
              dynamicProtect={dynamicProtect}
              project={project}
              objName={objName}
              formNo={forms[0]?.formNo}
              txnId={editTxnId || undefined}
              onActionResult={applyActionResult}
              onSystemAction={handleSystemAction}
              onVisionBuiltin={performVisionBuiltin}
              onActionError={handleActionError}
            />
          </div>
          {model.content && contentPanelVisible(model.content) && (
            <ContentPanel profile={model.content} sm={sm} values={formValues} />
          )}
          {lineEntryBanners}
          {topForms.slice(1).map((entry) => (
            <div key={entry.formNo} style={{
              // flexShrink:0 — a flex column shrinks its children by default,
              // which would squash the fixed-height grids.
              flexShrink: 0, margin: '0 14px 12px',
              border: '1px solid var(--color-border)', borderRadius: 6,
              background: 'var(--color-bg)', overflow: 'hidden',
            }}>
              <div style={{
                padding: '4px 10px', fontSize: '0.8125rem', fontWeight: 600,
                background: 'var(--color-surface-2, var(--color-bg-secondary))',
                borderBottom: '1px solid var(--color-border)',
                color: 'var(--color-text-secondary)',
              }}>{entry.sm.form_title || `Detail ${entry.formNo}`}</div>
              <div style={{ height: SIMPLE_GRID_HEIGHT, overflow: 'hidden' }}>
                <ChildTabularGrid
                  execute={gridExecute}
                  project={project}
                  objName={objName}
                  formNo={entry.formNo}
                  childSm={entry.sm}
                  rows={(detailsBuffer[entry.formNo] as ChildRow[]) || []}
                  onMutate={mutateChildRows(entry.formNo)}
                  parentHeaderValues={formValues}
                  uploadDocument={runtime.txn.uploadDocument}
                  busy={busy || hostBusy}
                  readOnly={viewOnly || formLinesViewOnly(entry.sm)}
                  parentStatus={
                    !editTxnId ? 'unsaved' : formDirty ? 'dirty' : 'retrieved'
                  }
                  gridLayout={gridLayout}
                  model={workingModel}
                  recordId={editTxnId || undefined}
                  recordTxnId={editTxnId || undefined}
                  parentPk={parentPk}
                  loadChildRows={loadGridChildRows}
                  seedRowChildren={seedGridRowChildren}
                  onAncestorPatch={applyHeaderPatch}
                  depth={1}
                />
              </div>
            </div>
          ))}
        </div>
        ) : (
        <>
        {tabForms.length > 1 && (
          <FormTabs
            forms={tabForms}
            active={activeFormNo}
            onChange={handleTopTabChange}
            childTabsEnabled={true}
          />
        )}
        {activeFormNo === forms[0]?.formNo ? (
          <>
            <ValidationBanner
              fieldErrors={fieldErrors}
              banner={formBanner}
              columnLabel={(dbName) => columnLabelFromModel(sm, dbName)}
            />
            {udaHeaderNotice}
            <div style={{ flex: 1, overflow: 'auto' }}>
              <FormRenderer
                model={udaHeaderSmView!}
                groupCaptions={udaCaptions}
                groupsHeading={udaGroupsHeading}
                keyGeneration={workingModel.key_generation}
                canUseAction={canUseAction}
                values={formValues}
                errors={fieldErrors}
                onChange={onFieldChange}
                readOnlyKey={!!editTxnId}
                viewOnly={viewOnly}
                lockReason={lockReason}
                wfInProgress={wfInProgress}
                dynamicProtect={dynamicProtect}
                project={project}
                objName={objName}
                formNo={forms[0]?.formNo}
                txnId={editTxnId || undefined}
                onActionResult={applyActionResult}
                onSystemAction={handleSystemAction}
                onVisionBuiltin={performVisionBuiltin}
                onActionError={handleActionError}
                groupSlots={inlineGroupSlots}
              />
              {model.content && contentPanelVisible(model.content) && (
                <ContentPanel profile={model.content} sm={sm} values={formValues} />
              )}
              {lineEntryBanners}
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {(() => {
              const entry = topForms.find(f => f.formNo === activeFormNo)
              if (!entry || !sm) return null
              if (lineEntryOf(entry.sm)?.view === 'grid') {
                return (
                  <ChildTabularGrid
                    key={entry.formNo}
                    execute={gridExecute}
                    project={project}
                    objName={objName}
                    formNo={entry.formNo}
                    childSm={entry.sm}
                    rows={(detailsBuffer[entry.formNo] as ChildRow[]) || []}
                    onMutate={mutateChildRows(entry.formNo)}
                    parentHeaderValues={formValues}
                    uploadDocument={runtime.txn.uploadDocument}
                    busy={busy || hostBusy}
                    readOnly={viewOnly}
                    parentStatus={!editTxnId ? 'unsaved' : formDirty ? 'dirty' : 'retrieved'}
                    gridLayout={gridLayout}
                    model={workingModel}
                    recordId={editTxnId || undefined}
                    recordTxnId={editTxnId || undefined}
                    parentPk={parentPk}
                    loadChildRows={loadGridChildRows}
                    seedRowChildren={seedGridRowChildren}
                    onAncestorPatch={applyHeaderPatch}
                    depth={1}
                  />
                )
              }
              return (
                <ChildFormTab
                  key={entry.formNo}
                  project={project}
                  objName={objName}
                  formNo={entry.formNo}
                  sm={entry.sm}
                  parentSm={sm}
                  parentPkKey={parentPkKey}
                  parentPk={parentPk}
                  parentHeaderValues={formValues}
                  gridLayout={gridLayout}
                  model={workingModel}
                  depth={1}
                  store={makeTopStore(entry.formNo)}
                  readOnly={viewOnly || formLinesViewOnly(entry.sm)}
                  lockedLineActions={!viewOnly && !!entry.sm.lines_locked}
                  selectLocked={viewOnly}
                  // A detail form saved on Feed view in the designer reads
                  // as cards, the feed's own shape; otherwise its grid.
                  variant={entry.sm.view_layout === 'feed' ? 'compact' : 'grid'}
                  visionDialogs={visionDialogs}
                  recordTxnId={editTxnId || undefined}
                  onAncestorPatch={applyHeaderPatch}
                />
              )
            })()}
          </div>
        )}
        </>
        )}
      </EditorModal>
      )}

      {renderErrorDetails
        ? renderErrorDetails({
            report: errorReport,
            onClose: () => setErrorReport(null),
          })
        : (
          <DefaultErrorDetailsModal
            report={errorReport}
            onClose={() => setErrorReport(null)}
          />
        )}

      {aliasTxn != null && runtime.recordAliases && runtime.saveRecordAliases && (
        <RecordAliasModal
          project={project}
          objName={objName}
          txnId={aliasTxn}
          recordLabel={recordDisplayLabel(displayRows, aliasTxn, model)}
          onClose={() => setAliasTxn(null)}
          fetchAliases={runtime.recordAliases}
          saveAliases={runtime.saveRecordAliases}
        />
      )}

      {trailTxn != null && (
        <ProcessTrailModal
          project={project}
          objName={objName}
          txnId={trailTxn}
          runtime={runtime}
          onClose={() => setTrailTxn(null)}
          onOpenFullScreen={onOpenLinkedRecord}
          // The same handlers the list rows carry, so a record in the trail
          // offers what it offers on its own screen — for THIS object; a record
          // of another object is offered that object's screen.
          nodeActions={processNodeActions}
          onChanged={refreshList}
        />
      )}

      {historyTxn != null && (
        <RecordHistoryModal
          project={project}
          objName={objName}
          txnId={historyTxn}
          onClose={() => setHistoryTxn(null)}
          fetchHistory={runtime.recordHistory}
        />
      )}

      {approvalsOpen && renderApprovalsInbox?.({
        // A decision taken in the inbox (approve → promoted, reject, edit)
        // changes what this grid shows — re-read it on close too.
        onClose: () => {
          setApprovalsOpen(false)
          void refreshApprovalsCount()
          void refreshList()
        },
        onCountChange: onInboxCountChange,
      })}

      {/* Host-only modals (designer fix-code / audit / log / customize …). */}
      {extraModals}

      {/* An 'open' action button's line command, run without a surface. */}
      {openLineBar}

      {/* An 'open' action button is filling this record in: hold input until
          every value and follow-up step has landed. */}
      {openSequence.running && (
        <div role="status" aria-live="polite"
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483000, background: 'rgba(0,0,0,0.12)',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 72,
          }}>
          <div style={{
            background: 'var(--color-bg, #fff)', color: 'var(--color-text, #1c2430)',
            border: '1px solid var(--color-border, #dde3ea)', borderRadius: 8, padding: '10px 16px',
            boxShadow: '0 8px 28px rgba(0,0,0,0.18)', fontSize: '0.9125rem',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
            Filling in {screenName} from “{openSequence.running}”…
          </div>
        </div>
      )}

      {/* List-level custom action results (run from the toolbar / row menu). */}
      {actionHtml && (
        <HtmlResultModal
          title={actionHtml.title}
          html={actionHtml.html}
          onClose={() => setActionHtml(null)}
        />
      )}
      {actionGrid && (
        <SelectGridModal
          title={actionGrid.title}
          rows={actionGrid.res.rows || []}
          columns={actionGrid.res.columns}
          labels={gridLabels(actionGrid.res.target_form_no)}
          onCancel={() => setActionGrid(null)}
          onApply={(selected) => {
            const sel = actionGrid
            setActionGrid(null)
            void applyActionGridSelection(sel, selected)
          }}
        />
      )}
      {actionReport && (
        <ReportActionModal
          title={actionReport.title}
          project={project}
          objName={actionReport.objName}
          adapter={actionReport.adapter}
          params={actionReport.params}
          onClose={() => setActionReport(null)}
        />
      )}
      {/* "Ignore warnings and proceed" confirm for save / run_action. */}
      {warningsModal}
      {actionErrorModal}
      {validationErrorModal}
      {review && (
        <SaveReview
          mustFix={review.mustFix}
          confirm={review.confirm}
          summary={reviewSummary()}
          recordNoun={(sm?.form_title || 'record').toLowerCase()}
          onGoTo={goToReviewItem}
          onBack={() => closeReview(false)}
          onSave={review.resolve ? () => closeReview(true) : undefined}
        />
      )}
      {reasonModal}
      {actionConfirmModal}
      {actionCaptureModal}
      {bulkModals}
      {/* Portalled to <body>: "Export this record" opens while the list (and
          everything inside it) is hidden behind the record editor. */}
      {exportScope && exportApi && createPortal(
        <ExportWindow transport={exportApi} objName={objName} scope={exportScope}
          listColumns={exportListColumns} reloadKey={exportReloadKey}
          onClose={() => setExportScope(null)}
          notify={{ success: (m) => notify.success(m), error: (m) => notify.error(m), info: (m) => notifyInfo(m) }}
          onManage={(templateId) => setExportManage({ templateId })} />,
        document.body,
      )}
      {exportManage && exportApi && createPortal(
        <TemplateManager transport={exportApi} objName={objName} startWith={exportManage.templateId}
          notify={{ success: (m) => notify.success(m), error: (m) => notify.error(m) }}
          onClose={(changed) => {
            setExportManage(null)
            // The open Export window re-reads its templates.
            if (changed) setExportReloadKey(k => k + 1)
          }} />,
        document.body,
      )}
    </div>
  )

  // A transaction model that has become corrupted (missing forms / columns /
  // edit-mask shapes the renderers assume) would otherwise throw mid-render
  // and blank the whole screen with a raw "Cannot read properties of undefined"
  // JS error. Wrap the surface so any such crash is caught and turned into a
  // readable message that points the author at regenerating the model.
  return (
    <TxnScreenRuntimeProvider value={runtime}>
      <TxnRuntimeProvider value={runtime.txn}>
        <PeopleProvider value={people}>
          <TransactionErrorBoundary objName={objName}>
            <TxnCustomizeContext.Provider value={czApi}>
              <UdaScreenProvider value={udaStore}>
              <WizardFillContext.Provider value={wizardFill}>
              {body}
              {czApi && czField && (() => {
                const f = forms.find((x) => x.formNo === czField.formNo)
                if (!f) return null
                const isHeader = czField.formNo === forms[0]?.formNo
                const gridLabel = isHeader ? 'the list' : `the ${f.sm.form_title || 'line'} grid`
                if (czBuilding && runtime.build) {
                  return (
                    <BuildFieldPanel
                      key={`${czField.formNo}|${czField.colKey}`}
                      session={czSession}
                      objName={objName}
                      form={f.sm}
                      formNo={czField.formNo}
                      isHeader={isHeader}
                      colKey={czField.colKey}
                      gridLabel={gridLabel}
                      anchor={czField.anchor}
                      initialTab={czField.tab}
                      transport={runtime.build.transport}
                      onClose={() => setCzField(null)}
                    />
                  )
                }
                return (
                  <TxnFieldPanel
                    session={czSession}
                    core={czSession.core}
                    // An additional attribute the screen shows is found too
                    // (the panel then points at Additional attributes).
                    form={withUdaColumns(f.sm, udaStore?.columnsOf(czField.formNo))}
                    formNo={czField.formNo}
                    colKey={czField.colKey}
                    gridLabel={gridLabel}
                    anchor={czField.anchor}
                    onClose={() => setCzField(null)}
                    onOpenUdaSetup={UdaSetup && canUdaSetup
                      ? (formNo, columnKey) => setCzUda({ formNo, columnKey }) : undefined}
                  />
                )
              })()}
              {czBuilding && runtime.build && czAddField && (() => {
                const f = forms.find((x) => x.formNo === czAddField.formNo)
                if (!f) return null
                return (
                  <AddFieldDialog
                    session={czSession}
                    screenTitle={sm.form_title || objName}
                    form={f.sm}
                    formNo={czAddField.formNo}
                    isHeader={czAddField.formNo === forms[0]?.formNo}
                    group={czAddField.group}
                    transport={runtime.build.transport}
                    onClose={() => setCzAddField(null)}
                    onAdded={(colKey) => {
                      setCzAddField(null)
                      setCzField({ formNo: czAddField.formNo, colKey, anchor: null, tab: 'rules' })
                    }}
                  />
                )
              })()}
              {czBuilding && runtime.build && czButton && (
                <ButtonPanel
                  key={`${czButton.scopeKey}|${czButton.name ?? '+'}`}
                  session={czSession}
                  model={workingModel}
                  objName={objName}
                  scopeKey={czButton.scopeKey}
                  name={czButton.name}
                  anchor={czButton.anchor}
                  transport={runtime.build.transport}
                  onClose={() => setCzButton(null)}
                />
              )}
              </WizardFillContext.Provider>
              </UdaScreenProvider>
            </TxnCustomizeContext.Provider>
          </TransactionErrorBoundary>
        </PeopleProvider>
      </TxnRuntimeProvider>
    </TxnScreenRuntimeProvider>
  )
}

// Catches render-time crashes from a corrupted/incomplete transaction model and
// shows a friendly "please regenerate" message instead of taking the whole
// screen down with an unhandled exception. Resets when the open object changes
// so navigating to a healthy transaction recovers automatically.
interface TransactionErrorBoundaryProps {
  objName: string
  children: ReactNode
}
interface TransactionErrorBoundaryState {
  error: Error | null
}
class TransactionErrorBoundary extends Component<
  TransactionErrorBoundaryProps,
  TransactionErrorBoundaryState
> {
  state: TransactionErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): TransactionErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface to the console for debugging, but don't rethrow — the fallback UI
    // below keeps the rest of the app usable.
    // eslint-disable-next-line no-console
    console.error(`[transaction "${this.props.objName}"] render failed:`, error, info)
  }

  componentDidUpdate(prevProps: TransactionErrorBoundaryProps) {
    if (prevProps.objName !== this.props.objName && this.state.error) {
      this.setState({ error: null })
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 14, height: '100%', padding: 32,
            textAlign: 'center', color: 'var(--color-text)',
          }}
        >
          <AlertTriangle size={36} color="#f59e0b" />
          <div style={{ fontWeight: 600, fontSize: '1.0625rem' }}>
            This transaction screen could not be displayed
          </div>
          <div style={{
            maxWidth: 520, fontSize: '0.9125rem', lineHeight: 1.55,
            color: 'var(--color-text-secondary)',
          }}>
            The design data for <strong>{this.props.objName}</strong> appears to be
            corrupted or incomplete, so the screen can't be built. This usually
            means the transaction model needs to be regenerated. Open this object
            in the transaction designer and regenerate it, then reload this screen.
          </div>
          <div style={{
            fontFamily: 'monospace', fontSize: '0.8125rem', maxWidth: 520,
            color: 'var(--color-text-secondary)', wordBreak: 'break-word',
            opacity: 0.8,
          }}>
            {this.state.error.message || 'Unexpected rendering error'}
          </div>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => this.setState({ error: null })}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <RefreshCw size={14} /> Try again
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

// The details tree as it goes to the batch save: a `row_select` form sends its
// ticked rows only (an unticked saved row as a delete). Every other form, and
// a tree with no such form, goes out unchanged.
function detailsForSave(
  buf: EmbedDetailMap, forms: Array<{ formNo: string; sm: SqlModel }>,
): EmbedDetailMap {
  let out: EmbedDetailMap | null = null
  for (const f of forms) {
    const rs = rowSelectOf(f.sm)
    if (!rs || !Object.prototype.hasOwnProperty.call(buf, f.formNo)) continue
    out = out || { ...buf }
    out[f.formNo] = selectableRowsForSave(buf[f.formNo], rs) as EmbedDetailMap[string]
  }
  return out || buf
}

// Whether a form has ANYTHING to validate on navigate-away — a mandatory
// column, an active field-level validation, or an active form-level validation
// (legacy string or non-disabled structured rule). Mirrors what the save
// actually checks, so navigate-away validation matches save; lets us skip the
// round-trip only for forms with truly nothing to validate.
function formNeedsValidation(
  sm: {
    validations?: unknown[]
    columns?: Array<{ column?: { mandatory?: unknown; validations?: unknown[] } }>
  } | null | undefined,
): boolean {
  const active = (list: unknown): boolean =>
    Array.isArray(list) && list.some((v) =>
      typeof v === 'string'
        ? v.trim().length > 0
        : !!v && typeof v === 'object' && !(v as { disabled?: boolean }).disabled,
    )
  if (active(sm?.validations)) return true
  for (const wrap of sm?.columns || []) {
    const col = wrap?.column
    if (!col) continue
    const mand = col.mandatory === true || String(col.mandatory ?? '').toLowerCase() === 'true'
    if (mand || active(col.validations)) return true
  }
  return false
}

// Detect an "approval-staged" save result. When the saved object is under an
// approval workflow the backend returns ``{ ok, status:'pending', approval,
// message }`` INSTEAD of a normal ``{ ok, txn_id, header }``; there is no row to
// promote. Returns the message-bearing envelope when staged, else null.
// (BatchSaveResult doesn't type these fields, so we read defensively.)
function approvalEnvelopeOf(
  res: unknown,
): { message: string } | null {
  if (!res || typeof res !== 'object') return null
  const r = res as { approval?: unknown; message?: unknown }
  // The staged envelope is identified by its ``approval`` object (present
  // alongside status:'pending'); a normal save carries txn_id/header instead.
  if (!r.approval || typeof r.approval !== 'object') return null
  return { message: typeof r.message === 'string' ? r.message : '' }
}

// Toolbar entry to the cross-object "My Approvals" inbox. A small count badge
// shows how many records are pending the current user's approval (hidden when
// the count is 0 or unknown). Matches the surrounding ghost toolbar buttons.
function ApprovalsButton({
  count, onOpen,
}: {
  count: number | null
  onOpen: () => void
}) {
  const showBadge = typeof count === 'number' && count > 0
  return (
    <button
      className="btn btn-ghost btn-sm"
      onClick={onOpen}
      {...tw('action', 'approvals')}
      title="My Approvals — records pending your approval"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, position: 'relative' }}
    >
      <Inbox size={14} /> Approvals
      {showBadge && (
        <span style={{
          minWidth: 16, height: 16, padding: '0 4px',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1,
          borderRadius: 8, background: 'var(--color-accent, #4c8dff)', color: '#fff',
        }}>
          {count! > 99 ? '99+' : count}
        </span>
      )}
    </button>
  )
}

// The record editor's chrome: quiet icon buttons in its header, bordered and
// solid buttons in its footer (the prototype's look, from the theme tokens).
const editorFootBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px',
  borderRadius: 8, border: '1px solid var(--color-border-strong, #c7cfd9)',
  background: 'var(--color-bg-secondary, #ffffff)', color: 'var(--color-text, #1c2430)',
  fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
}
const editorFootPrimary: React.CSSProperties = {
  ...editorFootBtn, border: '1px solid transparent',
  background: 'var(--color-accent, #0061D0)', color: '#ffffff', fontWeight: 600,
}

/** Where the editor's resize control (Maximise / Restore) sits — on the title
 *  line or in its ⋮ menu — and how the person moves it. */
interface EditorSizeControl {
  place: LayoutSwitchPlace
  onPlace: (p: LayoutSwitchPlace) => void
}

/** The edit window's Maximise / Restore, remembered in the browser. */
function useEditorMaximized(): [boolean, () => void] {
  const [maximized, setMaximized] = useState<boolean>(rememberedMaximized)
  const toggle = useCallback(() => {
    setMaximized((cur) => {
      rememberMaximized(!cur)
      return !cur
    })
  }, [])
  return [maximized, toggle]
}

/** Groups of ⋮ items in one menu: each later group opens with a divider
 *  unless its first item already starts one. */
function joinMenuGroups(...groups: MoreMenuItem[][]): MoreMenuItem[] {
  const out: MoreMenuItem[] = []
  for (const g of groups) {
    if (!g.length) continue
    const [first, ...rest] = g
    out.push(out.length && !first.heading && !first.divider ? { ...first, divider: true } : first, ...rest)
  }
  return out
}

/** The ⋮ menu's part of the resize control: on the line, one item hides it;
 *  in the menu, Maximise / Restore itself plus one item that puts it back. */
function sizeMenuItems(sc: EditorSizeControl | undefined, maximized: boolean, toggle: () => void): MoreMenuItem[] {
  if (!sc) return []
  if (sc.place !== 'menu') {
    return [{
      label: 'Hide resize control', icon: <EyeOff size={14} aria-hidden="true" />,
      onClick: () => sc.onPlace('menu'), hook: `size-control-place:menu`,
    }]
  }
  return [
    {
      label: maximized ? 'Restore' : 'Maximise',
      icon: maximized ? <Copy size={14} aria-hidden="true" /> : <Square size={14} aria-hidden="true" />,
      onClick: toggle, hook: maximized ? 'restore' : 'maximize', heading: 'Window',
    },
    {
      label: 'Show resize control on the screen', icon: <PanelTop size={14} aria-hidden="true" />,
      onClick: () => sc.onPlace('bar'), hook: `size-control-place:bar`,
    },
  ]
}

const windowBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 32,
  borderRadius: 6, border: 0, padding: 0, flex: 'none', cursor: 'pointer',
  background: 'transparent', color: 'var(--color-text-secondary, #465062)',
}

/** One title-bar window button. Close turns red on hover, the others take the
 *  band tone — the desktop convention. */
function WindowButton({ label, title, onClick, disabled, danger = false, hook, children, ...rest }: {
  label: string
  title?: string
  onClick: () => void
  disabled?: boolean
  danger?: boolean
  hook: Record<string, string>
  children: React.ReactNode
  'aria-haspopup'?: 'menu'
  'aria-expanded'?: boolean
}) {
  const [hover, setHover] = useState(false)
  const lit = hover && !disabled
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={title ?? label}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            onFocus={() => setHover(true)} onBlur={() => setHover(false)}
            {...hook} {...rest}
            style={{
              ...windowBtn,
              background: lit ? (danger ? 'var(--color-error, #c42b1c)' : 'var(--color-surface-2, #eef1f5)') : 'transparent',
              color: lit && danger ? '#ffffff' : 'var(--color-text-secondary, #465062)',
              opacity: disabled ? 0.5 : 1, cursor: disabled ? 'default' : 'pointer',
            }}>
      {children}
    </button>
  )
}

/** The editor's ⋮ menu (Messages, the layouts when the switch is off the
 *  line, the resize control's place). Nothing when it has no items. */
function EditorMenu({ items, open, setOpen }: {
  items: MoreMenuItem[]
  open: boolean
  setOpen: (v: boolean | ((cur: boolean) => boolean)) => void
}) {
  // The menu closes on any outside click.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!(e.target as Element | null)?.closest('[data-tw-editor-menu]')) setOpen(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [open, setOpen])
  if (!items.length) return null
  return (
    <div data-tw-editor-menu style={{ position: 'relative', display: 'flex' }}>
      <WindowButton label="More" onClick={() => setOpen((v) => !v)} hook={tw('action', 'editor_menu')}
                    aria-haspopup="menu" aria-expanded={open}>
        <MoreVertical size={16} />
      </WindowButton>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0, minWidth: 230,
          background: 'var(--color-bg-secondary, #ffffff)', border: '1px solid var(--color-border, #dde3ea)',
          borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 4, zIndex: 1,
        }}>
          {items.map((it, i) => (
            <div key={`${it.hook || it.label}-${i}`}>
              {(it.heading || it.divider) && <MoreMenuHeading text={it.heading} first={i === 0} />}
              <button className="btn btn-ghost btn-sm" {...tw('action', it.hook || it.label)}
                      role={it.checked === undefined ? 'menuitem' : 'menuitemradio'}
                      aria-checked={it.checked === undefined ? undefined : it.checked}
                      onClick={() => { setOpen(false); it.onClick() }}
                      style={{ width: '100%', justifyContent: 'flex-start', fontWeight: it.checked ? 600 : undefined }}>
                {it.icon ?? <span aria-hidden style={{ width: 14, flex: 'none' }} />}
                <span style={{ flex: 1, textAlign: 'left' }}>{it.label}</span>
                {it.checked && <Check size={14} aria-hidden style={{ color: 'var(--color-accent, #0061D0)' }} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The record editor's title line.
 *
 * Left: the title and what it says about the record, then the layout switch
 * (when it sits on the line); they wrap onto a second row in a narrow window.
 * Right, always on the FIRST row: ⋮ · Minimise · Maximise / Restore · Close —
 * the order every desktop window uses. Double-clicking the bar maximises /
 * restores, as it does on a desktop.
 */
function EditorTitleBar({
  title, info, layoutSwitch, menuItems, onMinimise, busy = false,
  maximized, onToggleMaximized, sizeOnBar, onClose, closeLabel = 'Close', closeTitle = 'Close (Esc)',
  closeHook = 'close_x', menuOpen, setMenuOpen,
}: {
  title: React.ReactNode
  info?: React.ReactNode
  layoutSwitch?: React.ReactNode
  menuItems: MoreMenuItem[]
  onMinimise?: () => void
  busy?: boolean
  maximized: boolean
  /** Absent ⇒ this window cannot be resized (drawn in the page's flow). */
  onToggleMaximized?: () => void
  /** Maximise / Restore is drawn on the line (else it is in the ⋮ menu). */
  sizeOnBar: boolean
  onClose: () => void
  closeLabel?: string
  closeTitle?: string
  /** The Close button's test hook (the Document editor has no footer Close,
   *  so its title-bar Close keeps the `close` hook cases click). */
  closeHook?: string
  menuOpen: boolean
  setMenuOpen: (v: boolean | ((cur: boolean) => boolean)) => void
}) {
  return (
    <div
      onDoubleClick={onToggleMaximized ? (e) => {
        if ((e.target as Element | null)?.closest('button, a, input, select, textarea, [role="group"], [role="menu"]')) return
        onToggleMaximized()
      } : undefined}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '8px 8px 8px 20px',
        borderBottom: '1px solid var(--color-border, #dde3ea)',
        background: 'var(--color-bg-secondary, #ffffff)',
        userSelect: 'none',
      }}>
      <div style={{
        flex: '1 1 auto', minWidth: 0, minHeight: 36,
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 10, rowGap: 8,
      }}>
        <strong style={{
          fontSize: '1.125rem', fontWeight: 600, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{title}</strong>
        {info}
        <span style={{ flex: '1 1 10px' }} />
        {layoutSwitch}
      </div>
      <div role="toolbar" aria-label="Window" style={{
        flex: 'none', height: 36, display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <EditorMenu items={menuItems} open={menuOpen} setOpen={setMenuOpen} />
        {menuItems.length > 0 && (onMinimise || (onToggleMaximized && sizeOnBar)) && (
          <span aria-hidden style={{ width: 1, height: 20, margin: '0 4px', background: 'var(--color-border, #dde3ea)' }} />
        )}
        {onMinimise && (
          <WindowButton label="Minimise" onClick={onMinimise} disabled={busy} hook={tw('action', 'shell-minimise')}
                        title="Minimise — keep this record open with everything typed so far, and pick it up again from Open editors">
            <Minus size={16} />
          </WindowButton>
        )}
        {onToggleMaximized && sizeOnBar && (
          <WindowButton label={maximized ? 'Restore' : 'Maximise'} title={maximized ? 'Restore down' : 'Maximise'}
                        onClick={onToggleMaximized} hook={tw('action', maximized ? 'restore' : 'maximize')}>
            {maximized ? <Copy size={14} style={{ transform: 'scaleX(-1)' }} /> : <Square size={13} />}
          </WindowButton>
        )}
        <WindowButton label={closeLabel} title={closeTitle} onClick={onClose} disabled={busy} danger
                      hook={tw('action', closeHook)}>
          <X size={17} />
        </WindowButton>
      </div>
    </div>
  )
}

/**
 * The shell the Document view fills instead of `EditorModal`.
 *
 * Chrome only — no Save, no dirty badge, no Ctrl+S. The document carries its
 * own toolbar with its own Save, because it writes through its own endpoint;
 * a second Save here would be a second code path wearing the same word.
 *
 * ESC closes, and the backdrop does not: a half-typed document is easy to lose
 * to a stray click, and unlike the grid form there is no "are you sure" between
 * the click and the loss.
 */
function DocumentEditorShell({ title, onRequestClose, children, layoutSwitch, menuItems = [], sizeControl, notice }: {
  title: string
  onRequestClose: () => void
  children: React.ReactNode
  /** The editor layout switch — leaving the document asks Save / Discard.
   *  Null when the person keeps it in the ⋮ menu (`menuItems`). */
  layoutSwitch?: React.ReactNode
  /** The layouts' ⋮ items (see `editorLayoutMenuItems`). */
  menuItems?: MoreMenuItem[]
  sizeControl?: EditorSizeControl
  notice?: React.ReactNode
}) {
  const [maximized, toggleMaximized] = useEditorMaximized()
  const [menuOpen, setMenuOpen] = useState(false)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onRequestClose() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onRequestClose])

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: Z_RECORD_EDITOR,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: maximized ? 0 : 24,
    }}>
      <div style={{
        background: 'var(--color-bg)', borderRadius: maximized ? 0 : 10,
        width: maximized ? '100%' : 'min(1000px, 100%)', height: '100%',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
      }}>
        <EditorTitleBar
          title={title}
          layoutSwitch={layoutSwitch}
          menuItems={joinMenuGroups(menuItems, sizeMenuItems(sizeControl, maximized, toggleMaximized))}
          maximized={maximized}
          onToggleMaximized={toggleMaximized}
          sizeOnBar={sizeControl?.place !== 'menu'}
          onClose={onRequestClose}
          closeHook="close"
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
        {notice}
        <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      </div>
    </div>,
    document.body,
  )
}

interface ChangedField { key: string; label: string; from: string; to: string }

/** How long a record check may take before the editor says it is checking. */
const CHECKING_NOTICE_DELAY_MS = 400

type SessionBlock =
  | { kind: 'checking' }
  | { kind: 'locked'; message: string }
  | { kind: 'changed'; fields: ChangedField[]; fromSnapshot: boolean }

/** What an open editor (Run shell) tells the user above its form: checking
 *  the record, held by someone else (Retry), changed since it was opened
 *  (Save withheld; Reload record), the changes they had made, a stale draft. */
function SessionNotice({ block, prior, note, onRetry, onReload, onDismissPrior, onDismissNote }: {
  block: SessionBlock | null
  prior: ChangedField[] | null
  note: string | null
  onRetry: () => void
  onReload: () => void
  onDismissPrior: () => void
  onDismissNote: () => void
}) {
  if (!block && !prior && !note) return null
  const box: React.CSSProperties = {
    margin: '10px 18px 0', padding: '10px 14px', borderRadius: 8, fontSize: '0.875rem', lineHeight: 1.5,
    display: 'flex', flexDirection: 'column', gap: 8,
  }
  const warn: React.CSSProperties = {
    ...box, background: 'var(--color-warning-weak, rgba(154,103,0,0.10))',
    border: '1px solid var(--color-warning, #9a6700)', color: 'var(--color-text)',
  }
  const fieldsTable = (fields: ChangedField[]) => (
    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8425rem' }}>
      <thead>
        <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
          <th style={{ padding: '2px 8px 2px 0', fontWeight: 600 }}>Field</th>
          <th style={{ padding: '2px 8px', fontWeight: 600 }}>Was</th>
          <th style={{ padding: '2px 0 2px 8px', fontWeight: 600 }}>You entered</th>
        </tr>
      </thead>
      <tbody>
        {fields.map((f) => (
          <tr key={f.key} style={{ borderTop: '1px solid var(--color-border)' }}>
            <td style={{ padding: '3px 8px 3px 0', fontWeight: 500 }}>{f.label}</td>
            <td style={{ padding: '3px 8px', color: 'var(--color-text-secondary)', wordBreak: 'break-word' }}>{f.from || '—'}</td>
            <td style={{ padding: '3px 0 3px 8px', wordBreak: 'break-word' }}>{f.to || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
  return (
    <>
      {block?.kind === 'checking' && (
        <div role="status" {...tw('msg', 'info', { kind: 'editor-checking' })}
          style={{ ...box, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
          Checking the record before you continue…
        </div>
      )}
      {block?.kind === 'locked' && (
        <div role="alert" {...tw('msg', 'warning', { kind: 'editor-locked' })} style={warn}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <LockIcon size={15} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{block.message} You can look, but not change it until they finish. Nothing you typed is lost.</span>
            <button className="btn btn-secondary btn-sm" onClick={onRetry} {...tw('action', 'editor-lock-retry')}>
              <RefreshCw size={13} /> Retry
            </button>
          </div>
        </div>
      )}
      {block?.kind === 'changed' && (
        <div role="alert" {...tw('msg', 'warning', { kind: 'editor-record-changed' })} style={warn}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={15} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>
              <strong>This record was changed since you opened it</strong> — saved, approved or processed elsewhere.
              Your changes cannot be saved over it. Reload the record and re-enter what still applies.
            </span>
            <button className="btn btn-primary btn-sm" onClick={onReload} {...tw('action', 'editor-reload-record')}>
              <RotateCcw size={13} /> Reload record
            </button>
          </div>
          {block.fields.length > 0 ? fieldsTable(block.fields)
            : <span style={{ color: 'var(--color-text-secondary)' }}>You had not changed any field.</span>}
        </div>
      )}
      {!block && prior && (
        <div role="note" {...tw('msg', 'info', { kind: 'editor-prior-changes' })}
          style={{ ...box, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ flex: 1 }}>The record has been reloaded. What you had entered before, to re-enter where it still applies:</span>
            <button className="btn btn-ghost btn-sm" onClick={onDismissPrior} aria-label="Dismiss"><X size={13} /></button>
          </div>
          {fieldsTable(prior)}
        </div>
      )}
      {note && (
        <div role="note" {...tw('msg', 'warning', { kind: 'editor-draft-note' })} style={warn}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <AlertTriangle size={15} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{note}</span>
            <button className="btn btn-ghost btn-sm" onClick={onDismissNote} aria-label="Dismiss"><X size={13} /></button>
          </div>
        </div>
      )}
    </>
  )
}

function EditorModal({
  open, title, txnId, dirty, busy, viewOnly, lockReason, inline = false, fullScreen,
  onRequestClose, onSave, onSaveAndClose, onMessages, children, badges,
  hidden = false, onMinimise, notice, saveDisabled = false, submitLabel, layoutSwitch,
  menuItems = [], sizeControl, narrow = false,
}: {
  open: boolean
  /** The editor layout switch (Standard / Simple / Document / Compact), on the
   *  title line. Null when the person keeps it in the ⋮ menu (`menuItems`). */
  layoutSwitch?: React.ReactNode
  /** The layouts' ⋮ items (see `editorLayoutMenuItems`). */
  menuItems?: MoreMenuItem[]
  /** Where Maximise / Restore sits. Absent ⇒ on the title line, not movable. */
  sizeControl?: EditorSizeControl
  /** The Compact layout on a desktop: one narrow column. */
  narrow?: boolean
  /** A submit form (`TxnScreen` `submit`): one primary button with this
   *  caption runs `onSave`; Close reads Clear; there is no saved state. */
  submitLabel?: string
  /** An open editor the Run shell keeps alive while minimised: it stays
   *  MOUNTED (every value, child buffer and lookup survives) but is not shown
   *  and takes no keys. */
  hidden?: boolean
  /** The shell's Minimise — present only for such a kept-alive editor. */
  onMinimise?: () => void
  /** A notice above the form (edit lock held elsewhere, record changed). */
  notice?: React.ReactNode
  /** Save is withheld (the record changed since it was opened). */
  saveDisabled?: boolean
  /** Flags raised on the record (additional attributes), by the mode badge. */
  badges?: React.ReactNode
  /** The record's message log (the object has a send). Absent ⇒ no item. */
  onMessages?: () => void
  /** Drawn in the page's flow (filling its slot) instead of as a window over
   *  it — Customize mode, where the form sits under the mode's bar. */
  inline?: boolean
  /** The surrounding surface's Full screen / Restore (Customize mode). It is
   *  what Maximise / Restore toggles there, in place of the card's own size. */
  fullScreen?: { on: boolean; set: (on: boolean) => void }
  title: string
  txnId: string | null
  dirty: boolean
  busy: boolean
  // Record is locked / read-only: hide the Save controls and disable Ctrl+S.
  viewOnly?: boolean
  // Why the record is read-only — shown as the mode badge's hover tooltip.
  lockReason?: string
  onRequestClose: () => void
  onSave: () => void
  onSaveAndClose: () => void
  children: React.ReactNode
}) {
  // Close on ESC (only when modal is open). Doesn't fire when nested
  // dialogs (e.g. lookup) are in front because they stopPropagate /
  // handle ESC themselves first.
  const active = open && !hidden
  useEffect(() => {
    if (!active) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onRequestClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [active, onRequestClose])

  // Ctrl+S saves (continue editing). Scoped to the modal card, and gated on
  // being the topmost layer so a lookup / child-row editor stacked on top
  // (whose DOM is nested in this card) saves itself instead of the header.
  const cardRef = useRef<HTMLDivElement>(null)
  const [maximized, toggleMaximized] = useEditorMaximized()
  const [menuOpen, setMenuOpen] = useState(false)
  // A reopened editor starts with its menu closed.
  useEffect(() => { if (!open) setMenuOpen(false) }, [open])
  // Maximise / Restore: the window's own size, or — Customize mode — the
  // surrounding surface's full screen. A window drawn in the page's flow with
  // no such surface has no size to change.
  const isMax = fullScreen ? fullScreen.on : maximized
  const toggleSize = fullScreen
    ? () => fullScreen.set(!fullScreen.on)
    : (inline ? undefined : toggleMaximized)
  const { isTopLayer } = useShortcutLayer(active)
  // F10 saves and closes (the Run form's own Save and close; a submit-mode
  // form has only its one button, so F10 is that).
  const canSave = () => !busy && !viewOnly && !saveDisabled && isTopLayer()
  useKeyboardShortcuts(
    [
      { combo: systemCombo('save'), run: () => { if (canSave()) onSave() } },
      { combo: systemCombo('save_close'), run: () => { if (canSave()) (submitLabel ? onSave : onSaveAndClose)() } },
    ],
    { enabled: active, scopeRef: cardRef },
  )

  if (!open) return null

  const card = (
      <div
        ref={cardRef}
        onClick={(e) => e.stopPropagation()}
        data-tw-dialog={hidden ? undefined : 'editor'}
        data-tw-mode={viewOnly ? 'view' : txnId ? 'edit' : 'add'}
        style={inline ? {
          // In the page's flow: fill the slot the list would occupy.
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          flex: 1, minHeight: 0, width: '100%',
          display: hidden ? 'none' : 'flex', flexDirection: 'column',
          overflow: 'hidden',
        } : {
          background: 'var(--color-bg)',
          color: 'var(--color-text)',
          border: '1px solid var(--color-border)',
          borderRadius: maximized ? 0 : 12,
          width: maximized ? '100%' : (narrow ? 'min(600px, 96vw)' : 'min(1080px, 96vw)'),
          height: maximized ? '100%' : 'min(800px, 92vh)',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        <EditorTitleBar
          title={title}
          info={<>
            {/* The title already says New / Edit; a view-only record says why. */}
            {!submitLabel && viewOnly && (
              <FormModeBadge mode="view" lockReason={lockReason} />
            )}
            {badges}
            {txnId && (
              <code title={txnId} style={{
                fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: '0.8125rem',
                color: 'var(--color-text-secondary, #465062)', background: 'transparent', padding: 0,
              }}>
                {txnId.slice(0, 18)}{txnId.length > 18 ? '…' : ''}
              </code>
            )}
            {dirty && !submitLabel && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem',
                fontWeight: 600, color: 'var(--color-warning, #b26a00)', whiteSpace: 'nowrap' }}>
                <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--color-warning, #d98a12)' }} />
                Unsaved changes
              </span>
            )}
          </>}
          layoutSwitch={layoutSwitch}
          menuItems={joinMenuGroups(
            onMessages && txnId
              ? [{ label: 'Messages', icon: <Mail size={14} />, onClick: onMessages, hook: 'messages' }]
              : [],
            menuItems,
            toggleSize ? sizeMenuItems(sizeControl, isMax, toggleSize) : [],
          )}
          onMinimise={onMinimise}
          busy={busy}
          maximized={isMax}
          onToggleMaximized={toggleSize}
          sizeOnBar={sizeControl?.place !== 'menu'}
          onClose={onRequestClose}
          closeLabel={submitLabel ? 'Clear' : 'Close'}
          closeTitle={submitLabel ? 'Clear the form and start again' : 'Close (Esc)'}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
        />
        {notice}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
        {/* Footer — what to do with the record. */}
        <div style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          padding: '12px 20px', borderTop: '1px solid var(--color-border, #dde3ea)',
          background: 'var(--color-surface-2, #f0f3f7)',
        }}>
          <span style={{ flex: '1 1 200px', fontSize: '0.8125rem', color: 'var(--color-text-secondary, #465062)' }}>
            {viewOnly && lockReason ? lockReason : ''}
          </span>
          {submitLabel ? (
            <>
              <button type="button" onClick={onRequestClose} disabled={busy} style={editorFootBtn}
                      {...tw('action', 'clear')} title="Clear the form and start again">
                <RotateCcw size={15} /> Clear
              </button>
              {!viewOnly && (
                <button type="button" onClick={onSave} disabled={busy || saveDisabled} style={editorFootPrimary}
                        {...tw('action', 'submit')} title={withShortcutHint(submitLabel, systemCombo('save'))}>
                  <Play size={15} /> {busy ? 'Working…' : submitLabel}
                </button>
              )}
            </>
          ) : (
            <>
              <button type="button" onClick={onRequestClose} disabled={busy} style={editorFootBtn}
                      {...tw('action', 'close')}>
                Close
              </button>
              {!viewOnly && (
                <>
                  <button type="button" onClick={onSave} disabled={busy || saveDisabled} style={editorFootBtn}
                          {...tw('action', 'save')} title={withShortcutHint('Save and continue editing', systemCombo('save'))}>
                    Save
                  </button>
                  <button type="button" onClick={onSaveAndClose} disabled={busy || saveDisabled}
                          style={editorFootPrimary} {...tw('action', 'save_close')}
                          title={withShortcutHint('Save and close the editor', systemCombo('save_close'))}>
                    Save and close
                  </button>
                </>
              )}
            </>
          )}
        </div>
      </div>
  )
  if (inline) return card

  const node = (
    <div
      onClick={onRequestClose}
      aria-hidden={hidden || undefined}
      style={{
        // Above the Run view's sticky top bar and the process-trail window
        // it can be opened from, so the editor header (Close / Save / Save &
        // Close) isn't truncated behind either. Lookup and child-form modals
        // stack above this — see `layers`.
        position: 'fixed', inset: 0, zIndex: Z_RECORD_EDITOR,
        background: 'rgba(0,0,0,0.55)',
        // Minimised: still mounted (every value survives), never shown.
        display: hidden ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center',
        padding: maximized ? 0 : 24,
      }}
    >
      {card}
    </div>
  )

  return createPortal(node, document.body)
}

function applyValidationErrors(
  items: ValidationErrorItem[],
  fallbackMessage: string,
  setFieldErrors: (v: Record<string, string>) => void,
  setBanner: (v: string | null) => void,
  notify: TxnNotifier,
): void {
  if (items.length === 0) {
    notify.error(fallbackMessage)
    setBanner(fallbackMessage)
    return
  }
  const fieldMap: Record<string, string> = {}
  const general: string[] = []
  for (const it of items) {
    if (it.field && it.field !== '_record') {
      fieldMap[it.field] = it.message
    } else {
      general.push(it.message)
    }
  }
  setFieldErrors(fieldMap)
  setBanner(general.length > 0 ? general.join(' · ') : null)
  // Always surface a toast so the user can't miss the failure when the
  // banner scrolls off-screen. Prefer a general/banner message; fall
  // back to the first field-level message; cap suffix when many fields.
  const firstMessage = general[0]
    || (Object.values(fieldMap)[0] ?? null)
  if (firstMessage) {
    const extra = items.length - 1
    notify.error(extra > 0
      ? `${firstMessage} (+${extra} more)`
      : firstMessage)
  } else {
    notify.error(fallbackMessage)
  }
  const firstField = Object.keys(fieldMap)[0]
  if (firstField) {
    window.setTimeout(() => {
      const el = document.getElementById(fieldAnchorId(firstField))
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        const input = el.querySelector('input, select, textarea') as HTMLElement | null
        if (input) input.focus()
      }
    }, 50)
  }
}

function columnLabelFromModel(
  sm: { columns?: Array<{ column?: { name?: string; db_name?: string; heading?: string } }> } | null,
  key: string,
): string {
  if (!sm?.columns) return key
  // ``key`` may be a form_key (modern validation) or a db_name (PG-error
  // fallback). Match against both shapes so labels resolve either way.
  for (const wrap of sm.columns) {
    const col = wrap.column
    if (!col) continue
    const fkey = (col.name || col.db_name || '').replace(/\s+/g, '_').toLowerCase()
    const dbn = (col.db_name || col.name || '').replace(/\s+/g, '_').toLowerCase()
    if (fkey === key || dbn === key) {
      return col.heading || col.name || key
    }
  }
  return key
}

function ValidationBanner({
  fieldErrors, banner, columnLabel, blockedLabel = 'Save blocked',
}: {
  fieldErrors: Record<string, string>
  banner: string | null
  columnLabel: (dbName: string) => string
  /** What was stopped — 'Run blocked' on a submit form (nothing is saved). */
  blockedLabel?: string
}) {
  const fieldKeys = Object.keys(fieldErrors)
  if (fieldKeys.length === 0 && !banner) return null

  const focus = (dbName: string) => {
    const el = document.getElementById(fieldAnchorId(dbName))
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      const input = el.querySelector('input, select, textarea') as HTMLElement | null
      if (input) input.focus()
    }
  }

  return (
    <div {...tw('msg', 'error', { fields: fieldKeys.join(',') })} style={{
      padding: '10px 16px',
      background: 'rgba(239,68,68,0.08)',
      borderBottom: '1px solid rgba(239,68,68,0.35)',
      color: '#ef4444',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: '0.8625rem', fontWeight: 600, marginBottom: fieldKeys.length ? 6 : 0,
      }}>
        <AlertTriangle size={14} />
        {fieldKeys.length > 0
          ? `${blockedLabel} — ${fieldKeys.length} validation error${fieldKeys.length === 1 ? '' : 's'}`
          : banner}
      </div>
      {fieldKeys.length > 0 && (
        <ul style={{
          listStyle: 'none', padding: 0, margin: 0,
          display: 'flex', flexDirection: 'column', gap: 3,
          fontSize: '0.8425rem',
        }}>
          {fieldKeys.map(k => (
            <li key={k}>
              <button
                type="button"
                onClick={() => focus(k)}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: '#ef4444', cursor: 'pointer', textAlign: 'left',
                  fontSize: '0.8425rem',
                }}
              >
                <strong>{columnLabel(k)}</strong>: {fieldErrors[k]}
              </button>
            </li>
          ))}
        </ul>
      )}
      {fieldKeys.length > 0 && banner && (
        <div style={{ fontSize: '0.8225rem', marginTop: 6, opacity: 0.9 }}>
          {banner}
        </div>
      )}
    </div>
  )
}
